import { normalizeLocale, type SupportedLocale } from '@evi/shared';
import type { InviteLinkCopy } from './inviteLink';

/**
 * Copy for the Admin sales screens: the Sales Team view (map, team, reports, scoreboard), the partner book pages,
 * partner notes, My commission, Sales statements, Sales Staff, the plan revision form and the payout run.
 *
 * Every locale spells out every key: the Record type rejects a missing or extra key at compile time, and
 * salesTeamCopy.test.ts checks that no value is empty, dashed, or carries different placeholders from English.
 * Screens read the locale with payoutLocale(), the browser language when the Admin supports it and English otherwise.
 * Placeholders are {name}: fillSalesCopy fills them with text, and SalesFill (SalesPrimitives) places elements in them.
 * Recorded server values (stages, statuses, categories, report columns) stay as recorded; the status labels below
 * only cover values the screens pass themselves, and the badge tone always follows the recorded value.
 */
const en = {
  // Shared labels and the status values the screens write themselves
  notRecorded: 'Not recorded',
  notSet: 'Not set',
  noResults: 'No results',
  person: 'Person',
  status: 'Status',
  reason: 'Reason',
  partner: 'Partner',
  partners: 'Partners',
  chooseAPerson: 'Choose a person',
  chooseAPartner: 'Choose a partner',
  nextStep: 'Next step',
  ledgerMonth: 'Ledger month',
  allMonths: 'All months',
  manager: 'Manager',
  noManager: 'No manager',
  rep: 'Rep',
  roleManager: 'Manager, with own rep book',
  territory: 'Territory',
  startDate: 'Start date',
  effectiveDate: 'Effective date',
  action: 'Action',
  period: 'Period',
  currency: 'Currency',
  location: 'Location',
  name: 'Name',
  email: 'Email',
  paymentMethod: 'Payment method',
  stripeConnect: 'Stripe Connect',
  payroll: 'Payroll',
  payrollEligibility: 'Payroll eligibility',
  activePartners: 'Active partners',
  marginShare: 'Margin share',
  heldUnderCap: 'Held under cap',
  earned: 'Earned',
  paymentsAndPayroll: 'Payments and payroll',
  salesStaff: 'Sales staff',
  salesPartner: 'Sales Partner',
  recording: 'Recording...',
  recordAssignment: 'Record assignment',
  assignmentFailed: 'Assignment failed.',
  pointOne: '{n} point',
  pointOther: '{n} points',
  statusActive: 'active',
  statusInactive: 'inactive',
  statusQualified: 'qualified',
  statusNotQualified: 'not qualified',
  statusAccepted: 'accepted',
  statusAwaitingAcceptance: 'awaiting acceptance',
  statusAssigned: 'assigned',
  statusOffline: 'offline',
  statusNotStarted: 'not started',
  statusPending: 'pending',
  statusNoSubmission60: 'No submission in 60 days',
  statusBelowPace: 'Below 6-month pace',
  statusOrderFollowUp: 'Order follow-up needed',
  // Sales Team view: map, team, reports and scoreboard tabs
  teamTitle: 'Sales Team',
  teamSubtitle: 'Your network, team activity and recorded earnings.',
  teamOpenPerson: "Open a person's team",
  teamLoading: 'Loading your team',
  teamChoose: 'Choose a team',
  teamEmptyBody: 'Your permitted team and book data appear here.',
  tabMap: 'Map',
  tabTeam: 'Team',
  tabReports: 'Reports',
  tabScoreboard: 'Scoreboard',
  teamMyBook: 'My book',
  teamWholeTeam: 'Whole team',
  teamNetworkMap: 'Network map',
  teamEveryone: 'Everyone in scope',
  teamStage: 'Stage',
  teamAllStages: 'All stages',
  teamChargerType: 'Charger type',
  teamAllTypes: 'All types',
  teamState: 'State',
  teamAllStates: 'All states',
  teamLaunchedFrom: 'Launched from',
  teamLaunchedThrough: 'Launched through',
  teamPinLegend: 'Pin color identifies the person. A filled circle is online, an empty circle is offline, and an exclamation mark is faulted. {n} sites have no map coordinates.',
  teamCloseDetails: 'Close site details',
  teamCity: 'City',
  teamChargers: 'Chargers',
  teamDirectDeal: 'Direct deal',
  teamLaunchDate: 'Launch date',
  teamNotLaunched: 'Not launched',
  teamSessions30: 'Sessions, last 30 days',
  teamKwh30: 'kWh, last 30 days',
  teamNoSites: 'No sites match',
  teamNoSitesBody: 'Change the filters to see other sites in your book.',
  teamSite: 'Site',
  teamDirectReports: 'Direct reports',
  teamNoDirectReports: 'No direct reports',
  teamNoDirectReportsBody: 'Dated assignments appear here once recorded.',
  teamActivity: 'Activity',
  teamEarnedMonth: 'Earned this month',
  teamActivatedMonth: 'Activated this month',
  teamLaunchedMonth: 'Launched this month',
  teamPace: 'Pace',
  teamOnPace: 'On pace',
  teamEligibility: '{active}/{target} active for {months}/{required} months.',
  teamUsdAverage: 'Six-month USD average: {amount}',
  teamNotAvailable: 'Not available',
  teamScoreboard: 'Team scoreboard',
  teamThisWeek: 'This week',
  teamThisMonth: 'This month',
  teamScoreNote: 'Counts only. Ranked by qualifying launches, then active partners and activations. Leads, demos and applications use your audited activity records. Calls and conversations use partner notes. No estimates are added.',
  teamOutreach: 'Outreach',
  teamLaunches: 'Launches',
  scoreLeadsStarted: 'leads started',
  scoreCalls: 'calls',
  scoreConversations: 'conversations',
  scoreDemos: 'demos',
  scoreApplications: 'applications',
  scoreApproved: 'approved',
  scoreFirstPaidOrders: 'first paid orders',
  scoreActivated: 'activated',
  scoreActivePartners: 'active partners',
  scoreQualifyingLaunches: 'qualifying launches',
  reportMonthly: 'Commission by month and category',
  reportSessions: 'Session points by charger',
  reportYearEnd: 'Year-end totals',
  reportsTitle: 'My reports',
  reportYear: 'Year',
  reportKind: 'Report',
  reportDownloadFailed: 'Report download failed.',
  reportExport: 'Export CSV',
  reportEmpty: 'No recorded activity',
  reportEmptyBody: 'Recorded earnings and payment evidence will appear here.',
  reportNote: 'Ledger currencies stay separate. Year-end totals support filing preparation and are not a tax form.',
  activityRecorded: 'Activity recorded.',
  activityFailed: 'Activity could not be recorded.',
  activityLabel: 'Record my activity',
  activityLead: 'New lead started',
  activityDemo: 'Demo',
  activityApplication: 'Application',
  activityEvidence: 'Evidence reference',
  activityPlaceholder: 'Your activity reference',
  activityRecord: 'Record activity',
  // Sales payout run
  payTitle: 'Payout run',
  paySubtitle: 'Review closed statements, then confirm payments. Automatic payouts stay off until approved.',
  payCycle: 'Monthly cycle',
  payCycleBody: 'Statements close on the 1st. Review on days 1 to 4. Pay on the 5th or the next weekday. Each currency is paid separately. Unready accounts and failed transfers remain payable.',
  payStatementMonth: 'Statement month',
  payClose: 'Close statements',
  payPay: 'Pay and export payroll',
  paySync: 'Synchronize earnings',
  paySynced: 'Earnings and reversals synchronized.',
  payPreview: 'Preview only',
  payRequestFailed: 'Request failed.',
  payNewLines: '{n} new ledger lines. Statement review required.',
  payDate: 'Payment date: {date}.',
  payMayProceed: 'Payments may proceed.',
  payWait: 'Payments wait until that date.',
  payNothing: 'Nothing to process',
  payNothingBody: 'No statement or payment balance is available for this month.',
  payLedgerHeld: 'Ledger and held',
  payPayment: 'Payment',
  payReview: 'Review',
  payHeld: 'Held: {amount}',
  payConversion: 'Conversion cost: {amount}',
  payReady: 'Ready',
  payPrepareQuote: 'Prepare conversion quote',
  payQuotePrepared: 'Quote prepared. Run a fresh preview.',
  payConfirmCheck: 'I reviewed these amounts, currencies and blocked recipients.',
  payConfirmClose: 'Confirm statement close',
  payConfirmPay: 'Confirm payout run',
  payRunRecorded: 'Run recorded: {result}',
  payPayroll: 'Payroll export and reconciliation',
  payDownload: 'Download recorded payroll CSV',
  payPaymentId: 'Payment or export ID',
  payProviderRef: 'Provider reference',
  payRecordPayroll: 'Record completed payroll',
  payPayrollRecorded: 'Payroll payment recorded.',
  payActualCost: 'Actual conversion cost',
  payActualBody: "Record the provider's final conversion cost in the ledger currency. A difference from the quote becomes an adjustment on the current open statement.",
  payLedgerCurrency: 'Ledger currency',
  payActualCents: 'Actual cost in cents',
  payRecordActual: 'Record actual cost',
  payActualReconciled: 'Actual conversion cost reconciled.',
  // Partner book: partners, orders and onboarding help
  bookPartnersSubtitle: 'Activity, locations and next steps for the partners in your book.',
  bookLoadingPartners: 'Loading your partners...',
  bookQualifyingChargers: 'Qualifying chargers',
  bookLaunches12: 'Launches in 12 months',
  bookActivityTarget: 'Activity target: 8',
  bookLaunches6: 'Launches in 6 months',
  bookPaceTarget: 'Pace target: 4',
  bookLocationsSubmissions: 'Locations / submissions',
  bookLastContact: 'Last contact',
  bookLocationsHeading: 'Locations and submissions',
  bookNoLocations: 'No locations yet',
  bookNoLocationsBody: "This partner's submitted locations will appear here.",
  bookNotes: 'Notes and next steps',
  bookNoPartners: 'No partners in your book',
  bookNoPartnersBody: 'Assigned partners and their locations will appear here.',
  bookLoadingSubmission: 'Loading submission...',
  bookLocationType: 'Location type',
  bookAddress: 'Address',
  bookContact: 'Contact',
  bookConnectivity: 'Connectivity',
  bookCommissioning: 'Commissioning authorized',
  bookAwaitingReview: 'Awaiting EVI review',
  bookInstallerExpires: 'Installer access expires',
  bookNotIssued: 'Not issued',
  bookLaunch: 'Launch',
  bookAwaitingApproval: 'Awaiting EVI approval',
  bookOrdersTitle: 'Orders',
  bookOrdersSubtitle: 'Customer orders attributed to you and the partners in your book.',
  bookLoadingOrders: 'Loading orders...',
  bookOrderDate: 'Order date',
  bookDirectStaffOrder: 'Direct staff order',
  bookOrderTotal: 'Order total',
  bookQty: 'Qty {n}',
  bookRegisterDeal: 'Register this direct deal',
  bookClaimRecorded: 'Site claim recorded. Existing non-sales sites require Super Admin review.',
  bookClaimFailed: 'Site claim could not be recorded.',
  bookDealEvidence: 'Direct deal evidence',
  bookRegisterButton: 'Register direct deal',
  bookNoOrders: 'No orders yet',
  bookNoOrdersBody: 'Orders from your direct deals and partner book will appear here.',
  bookNewestOrders: 'Newest orders',
  bookOlderOrders: 'Older orders',
  bookOnboardingTitle: 'Onboarding help',
  bookOnboardingSubtitle: 'Help people at locations in your book get the access they need.',
  bookInvite: 'Invite a person',
  bookInviteBody: 'Invite or resend an invitation. Delivery follows the current mail policy.',
  bookNoInviteLocations: 'No locations to invite yet',
  bookNoInviteLocationsBody: 'A location must be in your book before you can invite its users.',
  bookInviteRecorded: 'Invitation recorded. Delivery follows the current mail policy.',
  bookInviteFailed: 'Invitation could not be sent.',
  bookChooseLocation: 'Choose a location',
  bookAccess: 'Access',
  roleBuildingAdmin: 'Building Admin',
  rolePropertyManager: 'Property Manager',
  roleDriver: 'Driver',
  bookInviting: 'Recording invitation...',
  bookSendInvite: 'Send or resend invitation',
  // Partner and direct site notes
  notesHeading: 'Calls, visits and emails',
  notesIntro: 'Record each touch the same day. Notes belong to EVI. A correction adds a version and preserves the original.',
  notesEmpty: 'No notes yet',
  notesEmptyBody: 'Record the first contact and a clear next step below.',
  notesVersion: 'Version {n}',
  notesDue: 'Due',
  notesAddCorrection: 'Add corrected version',
  notesLinksFailed: 'Linked locations and submissions could not be loaded.',
  notesRecorded: 'Note recorded.',
  notesFailed: 'Note could not be recorded.',
  notesChannel: 'Channel',
  notesCall: 'Call',
  notesVisit: 'Visit',
  notesEmailChannel: 'Email',
  notesProjectStage: 'Project stage',
  notesNote: 'Note',
  notesNextDue: 'Next step due',
  notesLocationOptional: 'Location (optional)',
  notesNoLocation: 'No linked location',
  notesSubmissionOptional: 'Submission (optional)',
  notesNoSubmission: 'No linked submission',
  notesRecordVersion: 'Record new version',
  notesRecordNote: 'Record note',
  notesCancel: 'Cancel correction',
  notesSiteTitle: 'Direct site notes',
  notesNoClaims: 'No direct site claims',
  notesNoClaimsBody: 'Your direct site claims and their contact history will appear here.',
  notesStaffTitle: 'Partner notes and next steps',
  notesStaffIntro: "Record notes for any partner, including EVI's house partners. Notes stay with the partner when assigned to a rep.",
  notesFindPartner: 'Find a partner',
  notesHouseBook: ' (EVI house book)',
  notesFirstPartners: 'First partners',
  notesMorePartners: 'More partners',
  notesChooseBody: 'Select a partner to read or record contact history.',
  // My commission and Sales statements
  commPlanRevision: 'Commission plan, revision {n}',
  commOfferRevision: 'Your offer, revision {n}',
  commEffective: 'Effective {date}',
  commThrough: 'through {date}',
  commAccepted: 'Accepted {date}. Your accepted plan is shown read only.',
  commDirectMargin: 'Direct hardware margin',
  commAboveThreshold: 'Above annual threshold',
  commPartnerPoints: 'Partner session points',
  commFleetShare: 'Fleet subscription share',
  commFleetMeta: '{n} months; attributed fleet orders only',
  commFleetEvery: 'Every renewal while on the team; fleet orders only',
  commAnnualThreshold: 'Annual hardware margin threshold ({currency})',
  commFirstOrder: 'First qualifying paid order',
  commFourthCharger: 'Fourth qualifying charger',
  commBookDuration: 'Partner book margin duration',
  commNoMonthLimit: 'No month limit while assigned',
  commMonthsPerPartner: '{n} months per partner',
  commContinuation: 'Good standing departure continuation',
  commMonths: '{n} months',
  commOwnLadder: 'Own direct charger ladder',
  commFromCharger: 'From qualifying stamped charger',
  commSessionPoints: 'Session points',
  commLadderNote: 'Accrual begins at the fourth qualifying direct charger. The first 12 months are a build-up year. After that, a new launch needs 8 qualifying launches in the last 12 months including itself. Unstamped launches count for activity but never receive a stamp later. Existing stamps keep paying while you are on the team.',
  commBookTiers: 'Partner book margin tiers',
  commActiveAtLeast: 'Active partners, at least',
  commPointsNote: "Partner session points apply after the partner's commission. Staff session amounts are capped at EVI's available remainder. Fleet subscription share begins once subscription billing is active; other subscriptions do not earn a share.",
  commGolden: 'Paid Golden bonus: {amount}.',
  commOverrideTiers: 'Manager override tiers',
  commTeamActiveAtLeast: 'Team active partners, at least',
  commGrowth: "Manager growth bonuses: {six} at six active partners and {payroll} at the report's payroll milestone. Team session points: {points}.",
  commAccepting: 'Accepting...',
  commAccept: 'Accept this commission plan',
  commOfferAccepted: 'Offer accepted.',
  commOfferFailed: 'Offer acceptance failed.',
  commStage2: 'Stage 2 information',
  commStage2Body: 'Six completed months of recorded commission, after reversals. This does not move you to Stage 2. Super Admin must record an approved plan revision.',
  commConsecutive: '{n} of {required} consecutive months',
  commCompletedMonth: 'Completed month',
  commRecordedCommission: 'Recorded commission ({currency})',
  commNoMonths: 'No completed plan months yet',
  commNoMonthsBody: "Only completed months from your plan's effective date appear here.",
  commMonthsAvailable: '{n} of 6 completed months available.',
  commAverage: 'Average: {amount}',
  commAgainst: 'against {amount}',
  commNoCrossCurrency: '; no cross-currency comparison',
  commStatements: 'Monthly statements',
  commNoStatements: 'No statements yet',
  commNoStatementsBody: 'Your first statement appears after a commission month is closed.',
  commOpening: 'Opening',
  commReversals: 'Reversals',
  commPaid: 'Paid',
  commClosing: 'Closing',
  commNoPayments: 'No payments yet',
  commNoPaymentsBody: 'Connect payments or payroll records will appear after earnings are paid.',
  commLedgerAmount: 'Ledger amount',
  commMonth: 'Month',
  commStripeTransfer: 'Stripe transfer',
  commNotSent: 'Not sent',
  commConversionCost: 'Conversion cost',
  commCashAmount: 'Cash amount',
  commSentAs: 'Sent as {amount}',
  commReservedAs: 'Reserved as {amount}',
  commLines: 'Commission lines',
  commNoLines: 'No commission lines yet',
  commNoLinesBody: 'Qualifying activity creates commission lines here, with any later corrections shown separately.',
  commCategory: 'Category',
  commBasis: 'Basis',
  commPlainTerms: "How to read this: Basis is the amount your rate applies to (for a hardware order, EVI's margin on it). Held is earned but not paid yet: EVI pays your earnings on a partner from that partner's collected hardware margin, up to half of it, and the rest pays as the partner's later orders bring more in. Stage 2 is EVI's payroll plan with a base salary: six completed months of commission is the bar to be considered, and the move is a new plan EVI agrees with you.",
  commRate: 'Rate',
  commCommission: 'Commission',
  commPlanRevisionHeader: 'Plan revision',
  commTitle: 'My commission',
  commSubtitle: 'Your offer, commission activity and payment records. CAD and USD remain separate.',
  commNoOffer: 'No offer recorded yet',
  commNoOfferBody: 'EVI will record a commission plan for you to review and accept.',
  commClosedNote: 'Closed statements do not change. Corrections appear as new lines.',
  commLoading: 'Loading commission...',
  commNewestLines: 'Newest lines',
  commOlderLines: 'Older lines',
  stmtTitle: 'Sales statements',
  stmtSubtitle: 'Review recorded commissions, statements, payments and payroll for your sales team.',
  stmtNote: 'Use the reviewed scripts to accrue and close a month, pay Connect contractors or export payroll. This page never starts a collection or a transfer.',
  stmtChooseBody: 'Select a staff member to review their commission ledger and statements.',
  stmtReviewQueue: 'Review queue',
  stmtNothingReview: 'Nothing awaiting review',
  stmtNothingReviewBody: 'Settlement review items will appear here when evidence needs attention.',
  stmtSource: 'Source',
  stmtCostMissing: 'Cost not recorded',
  stmtCostIntro: 'Admin-only manual evidence is stored separately. The original order snapshot is not changed.',
  stmtNoMissing: 'No missing cost evidence',
  stmtNoMissingBody: 'Attributed paid orders missing a cost snapshot will appear here.',
  stmtCostRecorded: 'Cost evidence recorded.',
  stmtCostFailed: 'Cost evidence could not be saved.',
  stmtChooseCurrency: 'Choose currency',
  stmtUnits: '{name}: {n} units',
  stmtUnitCost: 'Unit cost',
  stmtAutelFee: 'Absorbed Autel fee per unit',
  stmtEvidenceReason: 'Evidence and reason',
  stmtRecordEvidence: 'Record manual evidence',
  stmtCorrection: 'Append a correction',
  stmtCorrectionIntro: 'Reversals are negative. Restorations cannot exceed the original entitlement. Use a stable reference for a retry.',
  stmtNoLinesToCorrect: 'No lines to correct',
  stmtNoLinesToCorrectBody: 'A positive commission line is required before a correction can be appended.',
  stmtCorrectionRecorded: 'Correction recorded.',
  stmtCorrectionFailed: 'Correction failed.',
  stmtOriginalLine: 'Original line',
  stmtSignedAmount: 'Signed amount',
  stmtReference: 'Reference',
  stmtAppend: 'Append correction',
  // Sales Staff
  staffTitle: 'Sales Staff',
  staffSubtitle: 'Manage people, dated commission plans and partner books. Changes are audited.',
  staffNote: 'Only Super Admin can change staff, plans or partner assignments.',
  staffPeople: 'People',
  staffActive: 'Active',
  staffContractors: 'Contractors',
  staffNoStaff: 'No sales staff yet',
  staffNoStaffBody: 'Add a person with an existing active account below.',
  staffKind: 'Kind',
  staffRole: 'Role',
  staffConnect: 'Connect',
  staffEligible: 'Eligible',
  staffNotEligible: 'Not yet eligible',
  staffEligibility: '{status}: {active}/{target} active, {months}/{required} months',
  staffAdd: 'Add a person',
  staffCreated: 'Staff record created.',
  staffAddFailed: 'Person could not be added.',
  staffInviteSent: 'Invite sent. The staff record is created with this role, manager and start date when they accept.',
  staffInviteNeedsName: 'This email has no account yet, so they get an invite. Add their name first.',
  staffInvitesTitle: 'Invited, not yet accepted',
  staffInvitedOn: 'Invited {date}',
  staffInviteFailed: 'Not created when they accepted: {reason}',
  staffResendInvite: 'Resend invite',
  staffInviteResent: 'Invite sent again.',
  staffCancelInvite: 'Cancel invite',
  staffInviteCancelled: 'Invite cancelled.',
  staffInviteActionFailed: 'The invite could not be changed.',
  // Copy invite link (Warren, 29 October 2026): the dialog beside Resend invite and its email note.
  inviteLinkAction: "Copy invite link",
  inviteLinkTitle: "Copy invite link",
  inviteLinkField: "Invite link",
  inviteLinkLoading: "Making a fresh link...",
  inviteLinkCopy: "Copy",
  inviteLinkCopied: "Copied",
  inviteLinkSelected: "The link is selected. Press Ctrl+C, or Cmd+C on a Mac, to copy it.",
  inviteLinkEmail: "Email from my account",
  inviteLinkReplaces: "This link replaces the one in earlier invite emails. It works once and expires {date}.",
  inviteLinkDiscard: "Closing this window discards the link. It is never shown again.",
  inviteLinkExpired: "This invite has expired. Re-send it to start a new one, then copy that link.",
  inviteLinkFailed: "The invite link could not be made.",
  inviteLinkClose: "Close",
  inviteLinkMailSubject: "Your EV Initiative invite",
  inviteLinkMailGreeting: "Hi {name},",
  inviteLinkMailGreetingNoName: "Hello,",
  inviteLinkMailBody: "I am sending your EV Initiative invite from my own email in case the earlier one did not reach you. The link below works once and expires {date}.",
  inviteLinkMailSignoff: "Thanks,",
  inviteLinkSignInPage: "They already have an EV Initiative account with this email, so this link opens the sign-in page. Signing in with that account accepts the invite.",
  inviteLinkMailSignInPage: "I am sending your EV Initiative invite from my own email in case the earlier one did not reach you. You already have an EV Initiative account with this email, so sign in with it at the link below to accept.",
  staffAccountEmail: 'Account email',
  staffStaffRole: 'Staff role',
  staffContractor: 'Contractor',
  staffEmployee: 'Employee',
  staffPayThrough: 'Pay through',
  staffStatesComma: 'States (comma separated)',
  staffCreate: 'Create staff record',
  staffPersonDetails: 'Person details',
  staffChooseBody: 'Select a row or use Person details to review their record, plans and book.',
  staffRecord: 'Staff record',
  staffAccount: 'Account',
  staffPartnerBook: 'Partner book',
  staffNoPartners: 'No partners assigned',
  staffNoPartnersBody: "Assign a partner below to add them to this person's book.",
  staffMoveAll: "Move all partners",
  staffMoveAllIntro: "Moves every partner {name} holds today to another SP Manager or the EVI house book, from now. Chargers already launched keep their launch stamp.",
  staffMoveAllTo: "Move to",
  staffHouseBook: "EVI house book",
  staffMoveAllButton: "Move all partners",
  staffMoveAllDone: "{count} partners moved.",
  staffDepartureMoves: "When the departure date arrives, this person's partners move to their Sales Manager, or to the EVI house book when there is none.",
  staffDeparted: 'Departed {date}: {reason}',
  staffUpdated: 'Staff record updated.',
  staffUpdateFailed: 'Update failed.',
  staffStates: 'States',
  staffEmploymentNote: 'Employment changes use the dated move in New plan revision. Charger stamps, book assignments and the activity window stay with this staff record.',
  staffEndDate: 'End date (optional)',
  staffDepartureReason: 'Departure reason',
  staffGoodStanding: 'Good standing',
  staffCause: 'Cause',
  staffConductIds: 'Conduct-related ledger line IDs (cause only)',
  staffSave: 'Save staff changes',
  staffManagerAssignment: 'Dated manager assignment',
  staffManagerRecorded: 'Dated manager assignment recorded.',
  staffPlanRevisions: 'Plan revisions',
  staffNoPlans: 'No plan revisions',
  staffNoPlansBody: 'Record a dated offer for this person to accept.',
  staffRevision: 'Revision',
  staffEffectiveFrom: 'Effective from',
  staffEffectiveTo: 'Effective to',
  staffOngoing: 'Ongoing',
  staffAssignPartner: 'Assign a partner',
  staffAssignIntro: 'Record a new assignment with its reason. Earlier assignment history remains intact.',
  staffAssignmentRecorded: 'Assignment recorded.',
  staffUnassigned: 'Unassigned',
  staffStandardPlan: 'Standard plan for future reps',
  staffSiteClaims: 'Site claims',
  staffClaimsIntro: 'Existing non-sales sites need approval. No book access or commission is granted while a claim awaits review.',
  staffNoClaims: 'No site claims',
  staffNoClaimsBody: 'Registered sales sites will appear here for review.',
  staffRegistrant: 'Registrant',
  staffExisting: 'Existing {kind}: {id}',
  staffReviewRecorded: 'Review recorded.',
  staffReviewFailed: 'Review failed.',
  staffDecision: 'Decision',
  staffReject: 'Reject',
  staffApprove: 'Approve',
  staffRecordDecision: 'Record decision',
  staffReassignRecorded: 'Reassignment recorded.',
  staffReassign: 'Reassign claim',
  staffReassignFailed: 'Reassignment failed.',
  staffRegistrantKind: 'New registrant kind',
  // Plan revision form
  planNoMonthLimit: 'No month limit',
  planStoredStandard: 'Stored standard revision',
  planNew: 'New plan revision',
  planPrefilled: 'Prefilled from stored standard revision {revision}. Accepted plans and existing charger stamps stay unchanged.',
  planPrefilledCode: 'Prefilled from the current standard, revision {revision}, not yet recorded. Accepted plans and existing charger stamps stay unchanged.',
  planLoading: 'loading',
  planLoadFailed: 'The stored standard could not be loaded. A revision cannot be recorded.',
  planStage: 'Plan stage',
  planStage1: 'Stage 1 commission',
  planStage2: 'Stage 2 payroll rates',
  planNoRates: 'No stored rates are available for this stage.',
  planCentsReadback: 'Reads as {amount}',
  planRecorded: 'Revision recorded. A personal revision requires acceptance.',
  planFailed: 'Revision could not be recorded.',
  planDatedRevision: 'Dated plan or role revision',
  planEmployeeMove: 'Dated move to employee and payroll',
  planRoleOnRevision: 'Role on this revision',
  planNotice: 'Written notice reference',
  planRecord: 'Record new revision',
};

export type SalesTeamCopyKey = keyof typeof en;

const copy: Record<SupportedLocale, Record<SalesTeamCopyKey, string>> = {
  en,
  es: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'No registrado',
    notSet: 'Sin definir',
    noResults: 'Sin resultados',
    person: 'Persona',
    status: 'Estado',
    reason: 'Motivo',
    partner: 'Socio',
    partners: 'Socios',
    chooseAPerson: 'Elige una persona',
    chooseAPartner: 'Elige un socio',
    nextStep: 'Próximo paso',
    ledgerMonth: 'Mes contable',
    allMonths: 'Todos los meses',
    manager: 'Gerente',
    noManager: 'Sin gerente',
    rep: 'Representante',
    roleManager: 'Gerente, con cartera propia de representante',
    territory: 'Territorio',
    startDate: 'Fecha de inicio',
    effectiveDate: 'Fecha de vigencia',
    action: 'Acción',
    period: 'Período',
    currency: 'Moneda',
    location: 'Ubicación',
    name: 'Nombre',
    email: 'Correo electrónico',
    paymentMethod: 'Método de pago',
    stripeConnect: 'Stripe Connect',
    payroll: 'Nómina',
    payrollEligibility: 'Elegibilidad para nómina',
    activePartners: 'Socios activos',
    marginShare: 'Participación en el margen',
    heldUnderCap: 'Retenido por el tope',
    earned: 'Ganado',
    paymentsAndPayroll: 'Pagos y nómina',
    salesStaff: 'Personal de ventas',
    salesPartner: 'Socio de Ventas',
    recording: 'Registrando...',
    recordAssignment: 'Registrar asignación',
    assignmentFailed: 'La asignación falló.',
    pointOne: '{n} punto',
    pointOther: '{n} puntos',
    statusActive: 'activo',
    statusInactive: 'inactivo',
    statusQualified: 'calificado',
    statusNotQualified: 'no calificado',
    statusAccepted: 'aceptado',
    statusAwaitingAcceptance: 'pendiente de aceptación',
    statusAssigned: 'asignado',
    statusOffline: 'sin conexión',
    statusNotStarted: 'no iniciado',
    statusPending: 'pendiente',
    statusNoSubmission60: 'Sin envíos en 60 días',
    statusBelowPace: 'Por debajo del ritmo de 6 meses',
    statusOrderFollowUp: 'Se necesita seguimiento del pedido',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Equipo de ventas',
    teamSubtitle: 'Tu red, la actividad del equipo y las ganancias registradas.',
    teamOpenPerson: 'Abrir el equipo de una persona',
    teamLoading: 'Cargando tu equipo',
    teamChoose: 'Elige un equipo',
    teamEmptyBody: 'Aquí aparecen los datos de equipo y cartera a los que tienes acceso.',
    tabMap: 'Mapa',
    tabTeam: 'Equipo',
    tabReports: 'Informes',
    tabScoreboard: 'Marcador',
    teamMyBook: 'Mi cartera',
    teamWholeTeam: 'Todo el equipo',
    teamNetworkMap: 'Mapa de la red',
    teamEveryone: 'Todas las personas a tu alcance',
    teamStage: 'Etapa',
    teamAllStages: 'Todas las etapas',
    teamChargerType: 'Tipo de cargador',
    teamAllTypes: 'Todos los tipos',
    teamState: 'Estado',
    teamAllStates: 'Todos los estados',
    teamLaunchedFrom: 'Lanzado desde',
    teamLaunchedThrough: 'Lanzado hasta',
    teamPinLegend: 'El color del marcador identifica a la persona. Un círculo relleno indica en línea, un círculo vacío indica sin conexión y un signo de exclamación indica una falla. Sitios sin coordenadas en el mapa: {n}.',
    teamCloseDetails: 'Cerrar detalles del sitio',
    teamCity: 'Ciudad',
    teamChargers: 'Cargadores',
    teamDirectDeal: 'Venta directa',
    teamLaunchDate: 'Fecha de lanzamiento',
    teamNotLaunched: 'Sin lanzar',
    teamSessions30: 'Sesiones, últimos 30 días',
    teamKwh30: 'kWh, últimos 30 días',
    teamNoSites: 'Ningún sitio coincide',
    teamNoSitesBody: 'Cambia los filtros para ver otros sitios de tu cartera.',
    teamSite: 'Sitio',
    teamDirectReports: 'Subordinados directos',
    teamNoDirectReports: 'Sin subordinados directos',
    teamNoDirectReportsBody: 'Las asignaciones con fecha aparecen aquí una vez registradas.',
    teamActivity: 'Actividad',
    teamEarnedMonth: 'Ganado este mes',
    teamActivatedMonth: 'Activados este mes',
    teamLaunchedMonth: 'Lanzados este mes',
    teamPace: 'Ritmo',
    teamOnPace: 'Al ritmo previsto',
    teamEligibility: '{active}/{target} activos durante {months}/{required} meses.',
    teamUsdAverage: 'Promedio de seis meses en USD: {amount}',
    teamNotAvailable: 'No disponible',
    teamScoreboard: 'Marcador del equipo',
    teamThisWeek: 'Esta semana',
    teamThisMonth: 'Este mes',
    teamScoreNote: 'Solo conteos. Clasificación por lanzamientos que califican y luego por socios activos y activaciones. Los prospectos, las demostraciones y las solicitudes usan tus registros de actividad auditados. Las llamadas y las conversaciones usan las notas de socios. No se agregan estimaciones.',
    teamOutreach: 'Prospección',
    teamLaunches: 'Lanzamientos',
    scoreLeadsStarted: 'prospectos iniciados',
    scoreCalls: 'llamadas',
    scoreConversations: 'conversaciones',
    scoreDemos: 'demostraciones',
    scoreApplications: 'solicitudes',
    scoreApproved: 'aprobados',
    scoreFirstPaidOrders: 'primeros pedidos pagados',
    scoreActivated: 'activados',
    scoreActivePartners: 'socios activos',
    scoreQualifyingLaunches: 'lanzamientos que califican',
    reportMonthly: 'Comisión por mes y categoría',
    reportSessions: 'Puntos de sesión por cargador',
    reportYearEnd: 'Totales de fin de año',
    reportsTitle: 'Mis informes',
    reportYear: 'Año',
    reportKind: 'Informe',
    reportDownloadFailed: 'No se pudo descargar el informe.',
    reportExport: 'Exportar CSV',
    reportEmpty: 'Sin actividad registrada',
    reportEmptyBody: 'Aquí aparecerán las ganancias registradas y los comprobantes de pago.',
    reportNote: 'Las monedas contables se mantienen separadas. Los totales de fin de año ayudan a preparar declaraciones y no son un formulario fiscal.',
    activityRecorded: 'Actividad registrada.',
    activityFailed: 'No se pudo registrar la actividad.',
    activityLabel: 'Registrar mi actividad',
    activityLead: 'Nuevo prospecto iniciado',
    activityDemo: 'Demostración',
    activityApplication: 'Solicitud',
    activityEvidence: 'Referencia del comprobante',
    activityPlaceholder: 'Referencia de tu actividad',
    activityRecord: 'Registrar actividad',
    // Sales payout run
    payTitle: 'Ejecución de pagos',
    paySubtitle: 'Revisa los estados de cuenta cerrados y luego confirma los pagos. Los pagos automáticos siguen desactivados hasta que se aprueben.',
    payCycle: 'Ciclo mensual',
    payCycleBody: 'Los estados de cuenta cierran el día 1. La revisión es del día 1 al 4. El pago es el día 5 o el siguiente día hábil. Cada moneda se paga por separado. Las cuentas que no están listas y las transferencias fallidas siguen pendientes de pago.',
    payStatementMonth: 'Mes del estado de cuenta',
    payClose: 'Cerrar estados de cuenta',
    payPay: 'Pagar y exportar la nómina',
    paySync: 'Sincronizar ganancias',
    paySynced: 'Ganancias y reversiones sincronizadas.',
    payPreview: 'Solo vista previa',
    payRequestFailed: 'La solicitud falló.',
    payNewLines: 'Líneas contables nuevas: {n}. Se requiere revisar el estado de cuenta.',
    payDate: 'Fecha de pago: {date}.',
    payMayProceed: 'Los pagos pueden continuar.',
    payWait: 'Los pagos esperan hasta esa fecha.',
    payNothing: 'Nada que procesar',
    payNothingBody: 'No hay saldo de estado de cuenta ni de pago disponible para este mes.',
    payLedgerHeld: 'Importe contable y retenido',
    payPayment: 'Pago',
    payReview: 'Revisión',
    payHeld: 'Retenido: {amount}',
    payConversion: 'Costo de conversión: {amount}',
    payReady: 'Listo',
    payPrepareQuote: 'Preparar cotización de conversión',
    payQuotePrepared: 'Cotización preparada. Genera una nueva vista previa.',
    payConfirmCheck: 'Revisé estos importes, monedas y destinatarios bloqueados.',
    payConfirmClose: 'Confirmar el cierre de estados de cuenta',
    payConfirmPay: 'Confirmar la ejecución de pagos',
    payRunRecorded: 'Ejecución registrada: {result}',
    payPayroll: 'Exportación y conciliación de nómina',
    payDownload: 'Descargar CSV de la nómina registrada',
    payPaymentId: 'ID de pago o de exportación',
    payProviderRef: 'Referencia del proveedor',
    payRecordPayroll: 'Registrar nómina completada',
    payPayrollRecorded: 'Pago de nómina registrado.',
    payActualCost: 'Costo real de conversión',
    payActualBody: 'Registra el costo final de conversión del proveedor en la moneda contable. Una diferencia con la cotización se convierte en un ajuste en el estado de cuenta abierto actual.',
    payLedgerCurrency: 'Moneda contable',
    payActualCents: 'Costo real en centavos',
    payRecordActual: 'Registrar costo real',
    payActualReconciled: 'Costo real de conversión conciliado.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Actividad, ubicaciones y próximos pasos de los socios de tu cartera.',
    bookLoadingPartners: 'Cargando tus socios...',
    bookQualifyingChargers: 'Cargadores que califican',
    bookLaunches12: 'Lanzamientos en 12 meses',
    bookActivityTarget: 'Meta de actividad: 8',
    bookLaunches6: 'Lanzamientos en 6 meses',
    bookPaceTarget: 'Meta de ritmo: 4',
    bookLocationsSubmissions: 'Ubicaciones / envíos',
    bookLastContact: 'Último contacto',
    bookLocationsHeading: 'Ubicaciones y envíos',
    bookNoLocations: 'Aún no hay ubicaciones',
    bookNoLocationsBody: 'Aquí aparecerán las ubicaciones enviadas por este socio.',
    bookNotes: 'Notas y próximos pasos',
    bookNoPartners: 'No hay socios en tu cartera',
    bookNoPartnersBody: 'Aquí aparecerán los socios asignados y sus ubicaciones.',
    bookLoadingSubmission: 'Cargando envío...',
    bookLocationType: 'Tipo de ubicación',
    bookAddress: 'Dirección',
    bookContact: 'Contacto',
    bookConnectivity: 'Conectividad',
    bookCommissioning: 'Puesta en marcha autorizada',
    bookAwaitingReview: 'En espera de la revisión de EVI',
    bookInstallerExpires: 'Vencimiento del acceso del instalador',
    bookNotIssued: 'No emitido',
    bookLaunch: 'Lanzamiento',
    bookAwaitingApproval: 'En espera de la aprobación de EVI',
    bookOrdersTitle: 'Pedidos',
    bookOrdersSubtitle: 'Pedidos de clientes atribuidos a ti y a los socios de tu cartera.',
    bookLoadingOrders: 'Cargando pedidos...',
    bookOrderDate: 'Fecha del pedido',
    bookDirectStaffOrder: 'Pedido directo del personal',
    bookOrderTotal: 'Total del pedido',
    bookQty: 'Cant. {n}',
    bookRegisterDeal: 'Registrar esta venta directa',
    bookClaimRecorded: 'Atribución del sitio registrada. Los sitios existentes que no son de ventas requieren la revisión de un superadministrador.',
    bookClaimFailed: 'No se pudo registrar la atribución del sitio.',
    bookDealEvidence: 'Comprobante de la venta directa',
    bookRegisterButton: 'Registrar venta directa',
    bookNoOrders: 'Aún no hay pedidos',
    bookNoOrdersBody: 'Aquí aparecerán los pedidos de tus ventas directas y de tu cartera de socios.',
    bookNewestOrders: 'Pedidos más recientes',
    bookOlderOrders: 'Pedidos anteriores',
    bookOnboardingTitle: 'Ayuda de incorporación',
    bookOnboardingSubtitle: 'Ayuda a las personas de las ubicaciones de tu cartera a obtener el acceso que necesitan.',
    bookInvite: 'Invitar a una persona',
    bookInviteBody: 'Invita o reenvía una invitación. La entrega sigue la política de correo vigente.',
    bookNoInviteLocations: 'Aún no hay ubicaciones para invitar',
    bookNoInviteLocationsBody: 'Una ubicación debe estar en tu cartera antes de que puedas invitar a sus usuarios.',
    bookInviteRecorded: 'Invitación registrada. La entrega sigue la política de correo vigente.',
    bookInviteFailed: 'No se pudo enviar la invitación.',
    bookChooseLocation: 'Elige una ubicación',
    bookAccess: 'Acceso',
    roleBuildingAdmin: 'Administrador del edificio',
    rolePropertyManager: 'Administrador de la propiedad',
    roleDriver: 'Conductor',
    bookInviting: 'Registrando la invitación...',
    bookSendInvite: 'Enviar o reenviar la invitación',
    // Partner and direct site notes
    notesHeading: 'Llamadas, visitas y correos',
    notesIntro: 'Registra cada contacto el mismo día. Las notas pertenecen a EVI. Una corrección agrega una versión y conserva la original.',
    notesEmpty: 'Aún no hay notas',
    notesEmptyBody: 'Registra abajo el primer contacto y un próximo paso claro.',
    notesVersion: 'Versión {n}',
    notesDue: 'Vencimiento',
    notesAddCorrection: 'Agregar versión corregida',
    notesLinksFailed: 'No se pudieron cargar las ubicaciones y los envíos vinculados.',
    notesRecorded: 'Nota registrada.',
    notesFailed: 'No se pudo registrar la nota.',
    notesChannel: 'Canal',
    notesCall: 'Llamada',
    notesVisit: 'Visita',
    notesEmailChannel: 'Correo',
    notesProjectStage: 'Etapa del proyecto',
    notesNote: 'Nota',
    notesNextDue: 'Vencimiento del próximo paso',
    notesLocationOptional: 'Ubicación (opcional)',
    notesNoLocation: 'Sin ubicación vinculada',
    notesSubmissionOptional: 'Envío (opcional)',
    notesNoSubmission: 'Sin envío vinculado',
    notesRecordVersion: 'Registrar nueva versión',
    notesRecordNote: 'Registrar nota',
    notesCancel: 'Cancelar corrección',
    notesSiteTitle: 'Notas de sitios directos',
    notesNoClaims: 'No hay atribuciones de sitios directos',
    notesNoClaimsBody: 'Aquí aparecerán tus atribuciones de sitios directos y su historial de contacto.',
    notesStaffTitle: 'Notas de socios y próximos pasos',
    notesStaffIntro: 'Registra notas de cualquier socio, incluidos los socios propios de EVI. Las notas se quedan con el socio cuando se asigna a un representante.',
    notesFindPartner: 'Buscar un socio',
    notesHouseBook: ' (cartera propia de EVI)',
    notesFirstPartners: 'Primeros socios',
    notesMorePartners: 'Más socios',
    notesChooseBody: 'Selecciona un socio para leer o registrar su historial de contacto.',
    // My commission and Sales statements
    commPlanRevision: 'Plan de comisiones, revisión {n}',
    commOfferRevision: 'Tu oferta, revisión {n}',
    commEffective: 'Vigente desde el {date}',
    commThrough: 'hasta el {date}',
    commAccepted: 'Aceptado el {date}. Tu plan aceptado se muestra en modo de solo lectura.',
    commDirectMargin: 'Margen directo de hardware',
    commAboveThreshold: 'Por encima del umbral anual',
    commPartnerPoints: 'Puntos de sesión de socios',
    commFleetShare: 'Participación en suscripciones de flotas',
    commFleetMeta: '{n} meses; solo pedidos de flotas atribuidos',
    commFleetEvery: 'Cada renovación mientras formes parte del equipo; solo pedidos de flotas',
    commAnnualThreshold: 'Umbral anual de margen de hardware ({currency})',
    commFirstOrder: 'Primer pedido pagado que califica',
    commFourthCharger: 'Cuarto cargador que califica',
    commBookDuration: 'Duración del margen de la cartera de socios',
    commNoMonthLimit: 'Sin límite de meses mientras esté asignado',
    commMonthsPerPartner: '{n} meses por socio',
    commContinuation: 'Continuación tras una salida en buenos términos',
    commMonths: '{n} meses',
    commOwnLadder: 'Escala de cargadores directos propios',
    commFromCharger: 'Desde el cargador sellado que califica',
    commSessionPoints: 'Puntos de sesión',
    commLadderNote: 'La acumulación comienza en el cuarto cargador directo que califica. Los primeros 12 meses son un año de formación. Después, un nuevo lanzamiento necesita 8 lanzamientos que califican en los últimos 12 meses, incluido él mismo. Los lanzamientos sin sello cuentan para la actividad, pero nunca reciben un sello después. Los sellos existentes siguen pagando mientras estés en el equipo.',
    commBookTiers: 'Niveles de margen de la cartera de socios',
    commActiveAtLeast: 'Socios activos, como mínimo',
    commPointsNote: 'Los puntos de sesión de socios se aplican después de la comisión del socio. Los importes de sesión del personal tienen como tope el remanente disponible de EVI. La participación en suscripciones de flotas comienza cuando la facturación de suscripciones está activa; las demás suscripciones no generan participación.',
    commGolden: 'Bono Golden pagado: {amount}.',
    commOverrideTiers: 'Niveles de comisión del gerente sobre el equipo',
    commTeamActiveAtLeast: 'Socios activos del equipo, como mínimo',
    commGrowth: 'Bonos de crecimiento del gerente: {six} con seis socios activos y {payroll} al alcanzar el hito de nómina del subordinado. Puntos de sesión del equipo: {points}.',
    commAccepting: 'Aceptando...',
    commAccept: 'Aceptar este plan de comisiones',
    commOfferAccepted: 'Oferta aceptada.',
    commOfferFailed: 'No se pudo aceptar la oferta.',
    commStage2: 'Información de la Etapa 2',
    commStage2Body: 'Seis meses completos de comisión registrada, después de reversiones. Esto no te pasa a la Etapa 2. Un superadministrador debe registrar una revisión del plan aprobada.',
    commConsecutive: '{n} de {required} meses consecutivos',
    commCompletedMonth: 'Mes completado',
    commRecordedCommission: 'Comisión registrada ({currency})',
    commNoMonths: 'Aún no hay meses completados del plan',
    commNoMonthsBody: 'Aquí solo aparecen los meses completados desde la fecha de vigencia de tu plan.',
    commMonthsAvailable: '{n} de 6 meses completados disponibles.',
    commAverage: 'Promedio: {amount}',
    commAgainst: 'frente a {amount}',
    commNoCrossCurrency: '; sin comparación entre monedas',
    commStatements: 'Estados de cuenta mensuales',
    commNoStatements: 'Aún no hay estados de cuenta',
    commNoStatementsBody: 'Tu primer estado de cuenta aparece después de cerrar un mes de comisiones.',
    commOpening: 'Apertura',
    commReversals: 'Reversiones',
    commPaid: 'Pagado',
    commClosing: 'Cierre',
    commNoPayments: 'Aún no hay pagos',
    commNoPaymentsBody: 'Los pagos de Connect o los registros de nómina aparecerán después de que se paguen las ganancias.',
    commLedgerAmount: 'Importe registrado',
    commMonth: 'Mes',
    commStripeTransfer: 'Transferencia de Stripe',
    commNotSent: 'No enviado',
    commConversionCost: 'Costo de conversión',
    commCashAmount: 'Importe del pago',
    commSentAs: 'Enviado como {amount}',
    commReservedAs: 'Reservado como {amount}',
    commLines: 'Líneas de comisión',
    commNoLines: 'Aún no hay líneas de comisión',
    commNoLinesBody: 'La actividad que califica crea aquí líneas de comisión, y cualquier corrección posterior se muestra por separado.',
    commCategory: 'Categoría',
    commBasis: 'Base',
    commPlainTerms: "Cómo leer esto: la base es el importe al que se aplica tu tasa (en un pedido de hardware, el margen de EVI). Retenido es lo ganado que aún no se ha pagado: EVI paga tus ganancias de un socio con el margen de hardware cobrado a ese socio, hasta la mitad, y el resto se paga a medida que sus pedidos siguientes aportan más. La etapa 2 es el plan de nómina de EVI con salario base: seis meses completos de comisión es el requisito para ser considerado, y el cambio es un plan nuevo que EVI acuerda contigo.",
    commRate: 'Tasa',
    commCommission: 'Comisión',
    commPlanRevisionHeader: 'Revisión del plan',
    commTitle: 'Mi comisión',
    commSubtitle: 'Tu oferta, tu actividad de comisiones y tus registros de pago. CAD y USD se mantienen separados.',
    commNoOffer: 'Aún no hay ninguna oferta registrada',
    commNoOfferBody: 'EVI registrará un plan de comisiones para que lo revises y aceptes.',
    commClosedNote: 'Los estados de cuenta cerrados no cambian. Las correcciones aparecen como líneas nuevas.',
    commLoading: 'Cargando comisiones...',
    commNewestLines: 'Líneas más recientes',
    commOlderLines: 'Líneas anteriores',
    stmtTitle: 'Estados de cuenta de ventas',
    stmtSubtitle: 'Revisa las comisiones, los estados de cuenta, los pagos y la nómina registrados de tu equipo de ventas.',
    stmtNote: 'Usa los scripts revisados para acumular y cerrar un mes, pagar a contratistas de Connect o exportar la nómina. Esta página nunca inicia un cobro ni una transferencia.',
    stmtChooseBody: 'Selecciona a un miembro del personal para revisar su registro de comisiones y sus estados de cuenta.',
    stmtReviewQueue: 'Cola de revisión',
    stmtNothingReview: 'Nada pendiente de revisión',
    stmtNothingReviewBody: 'Aquí aparecerán los elementos de revisión de liquidación cuando un comprobante requiera atención.',
    stmtSource: 'Origen',
    stmtCostMissing: 'Costo no registrado',
    stmtCostIntro: 'Los comprobantes manuales exclusivos de administración se guardan por separado. La instantánea original del pedido no se modifica.',
    stmtNoMissing: 'No faltan comprobantes de costo',
    stmtNoMissingBody: 'Aquí aparecerán los pedidos pagados atribuidos a los que les falte una instantánea de costo.',
    stmtCostRecorded: 'Comprobante de costo registrado.',
    stmtCostFailed: 'No se pudo guardar el comprobante de costo.',
    stmtChooseCurrency: 'Elige la moneda',
    stmtUnits: '{name}: {n} unidades',
    stmtUnitCost: 'Costo unitario',
    stmtAutelFee: 'Tarifa de Autel absorbida por unidad',
    stmtEvidenceReason: 'Comprobante y motivo',
    stmtRecordEvidence: 'Registrar comprobante manual',
    stmtCorrection: 'Agregar una corrección',
    stmtCorrectionIntro: 'Las reversiones son negativas. Las restituciones no pueden superar el derecho original. Usa una referencia estable para un reintento.',
    stmtNoLinesToCorrect: 'No hay líneas para corregir',
    stmtNoLinesToCorrectBody: 'Se requiere una línea de comisión positiva antes de poder agregar una corrección.',
    stmtCorrectionRecorded: 'Corrección registrada.',
    stmtCorrectionFailed: 'La corrección falló.',
    stmtOriginalLine: 'Línea original',
    stmtSignedAmount: 'Importe con signo',
    stmtReference: 'Referencia',
    stmtAppend: 'Agregar corrección',
    // Sales Staff
    staffTitle: 'Personal de ventas',
    staffSubtitle: 'Gestiona personas, planes de comisiones con fecha y carteras de socios. Los cambios se auditan.',
    staffNote: 'Solo un superadministrador puede cambiar el personal, los planes o las asignaciones de socios.',
    staffPeople: 'Personas',
    staffActive: 'Activos',
    staffContractors: 'Contratistas',
    staffNoStaff: 'Aún no hay personal de ventas',
    staffNoStaffBody: 'Agrega abajo a una persona con una cuenta activa existente.',
    staffKind: 'Tipo',
    staffRole: 'Rol',
    staffConnect: 'Connect',
    staffEligible: 'Elegible',
    staffNotEligible: 'Aún no elegible',
    staffEligibility: '{status}: {active}/{target} activos, {months}/{required} meses',
    staffAdd: 'Agregar una persona',
    staffCreated: 'Registro de personal creado.',
    staffAddFailed: 'No se pudo agregar a la persona.',
    staffInviteSent: 'Invitación enviada. El registro de personal se crea con este rol, gerente y fecha de inicio cuando la acepten.',
    staffInviteNeedsName: 'Este correo aún no tiene cuenta, así que recibirá una invitación. Agrega primero su nombre.',
    staffInvitesTitle: 'Invitados, aún sin aceptar',
    staffInvitedOn: 'Invitado el {date}',
    staffInviteFailed: 'No se creó al aceptar: {reason}',
    staffResendInvite: 'Reenviar invitación',
    staffInviteResent: 'Invitación enviada de nuevo.',
    staffCancelInvite: 'Cancelar invitación',
    staffInviteCancelled: 'Invitación cancelada.',
    staffInviteActionFailed: 'No se pudo cambiar la invitación.',
    inviteLinkAction: "Copiar enlace de invitación",
    inviteLinkTitle: "Copiar enlace de invitación",
    inviteLinkField: "Enlace de invitación",
    inviteLinkLoading: "Creando un enlace nuevo...",
    inviteLinkCopy: "Copiar",
    inviteLinkCopied: "Copiado",
    inviteLinkSelected: "El enlace está seleccionado. Pulsa Ctrl+C, o Cmd+C en un Mac, para copiarlo.",
    inviteLinkEmail: "Enviar desde mi correo",
    inviteLinkReplaces: "Este enlace sustituye al de los correos de invitación anteriores. Funciona una sola vez y caduca el {date}.",
    inviteLinkDiscard: "Al cerrar esta ventana se descarta el enlace. No se vuelve a mostrar.",
    inviteLinkExpired: "Esta invitación ha caducado. Reenvíala para crear una nueva y luego copia ese enlace.",
    inviteLinkFailed: "No se pudo crear el enlace de invitación.",
    inviteLinkClose: "Cerrar",
    inviteLinkMailSubject: "Tu invitación a EV Initiative",
    inviteLinkMailGreeting: "Hola, {name}:",
    inviteLinkMailGreetingNoName: "Hola:",
    inviteLinkMailBody: "Te envío tu invitación a EV Initiative desde mi propio correo por si la anterior no te llegó. El enlace de abajo funciona una sola vez y caduca el {date}.",
    inviteLinkMailSignoff: "Gracias,",
    inviteLinkSignInPage: "Ya tiene una cuenta de EV Initiative con este correo, así que este enlace abre la página de inicio de sesión. Al iniciar sesión con esa cuenta se acepta la invitación.",
    inviteLinkMailSignInPage: "Te envío tu invitación a EV Initiative desde mi propio correo por si la anterior no te llegó. Ya tienes una cuenta de EV Initiative con este correo, así que inicia sesión con ella en el enlace de abajo para aceptarla.",
    staffAccountEmail: 'Correo electrónico de la cuenta',
    staffStaffRole: 'Rol en ventas',
    staffContractor: 'Contratista',
    staffEmployee: 'Empleado',
    staffPayThrough: 'Pagar mediante',
    staffStatesComma: 'Estados (separados por comas)',
    staffCreate: 'Crear registro de personal',
    staffPersonDetails: 'Detalles de la persona',
    staffChooseBody: 'Selecciona una fila o usa Detalles de la persona para revisar su registro, sus planes y su cartera.',
    staffRecord: 'Registro de personal',
    staffAccount: 'Cuenta',
    staffPartnerBook: 'Cartera de socios',
    staffNoPartners: 'No hay socios asignados',
    staffNoPartnersBody: 'Asigna un socio abajo para agregarlo a la cartera de esta persona.',
    staffMoveAll: "Mover todos los socios",
    staffMoveAllIntro: "Mueve todos los socios que {name} tiene hoy a otro SP Manager o a la cartera de EVI, desde ahora. Los cargadores ya lanzados conservan su sello de lanzamiento.",
    staffMoveAllTo: "Mover a",
    staffHouseBook: "Cartera de EVI",
    staffMoveAllButton: "Mover todos los socios",
    staffMoveAllDone: "{count} socios movidos.",
    staffDepartureMoves: "Cuando llegue la fecha de salida, los socios de esta persona pasan a su Sales Manager, o a la cartera de EVI si no hay ninguno.",
    staffDeparted: 'Salida el {date}: {reason}',
    staffUpdated: 'Registro de personal actualizado.',
    staffUpdateFailed: 'La actualización falló.',
    staffStates: 'Estados',
    staffEmploymentNote: 'Los cambios de empleo usan el cambio con fecha en Nueva revisión del plan. Los sellos de cargadores, las asignaciones de cartera y la ventana de actividad permanecen con este registro de personal.',
    staffEndDate: 'Fecha de finalización (opcional)',
    staffDepartureReason: 'Motivo de la salida',
    staffGoodStanding: 'En buenos términos',
    staffCause: 'Causa justificada',
    staffConductIds: 'ID de líneas contables relacionadas con la conducta (solo con causa justificada)',
    staffSave: 'Guardar cambios del personal',
    staffManagerAssignment: 'Asignación de gerente con fecha',
    staffManagerRecorded: 'Asignación de gerente con fecha registrada.',
    staffPlanRevisions: 'Revisiones del plan',
    staffNoPlans: 'No hay revisiones del plan',
    staffNoPlansBody: 'Registra una oferta con fecha para que esta persona la acepte.',
    staffRevision: 'Revisión',
    staffEffectiveFrom: 'Vigente desde',
    staffEffectiveTo: 'Vigente hasta',
    staffOngoing: 'En curso',
    staffAssignPartner: 'Asignar un socio',
    staffAssignIntro: 'Registra una nueva asignación con su motivo. El historial de asignaciones anterior se conserva intacto.',
    staffAssignmentRecorded: 'Asignación registrada.',
    staffUnassigned: 'Sin asignar',
    staffStandardPlan: 'Plan estándar para futuros representantes',
    staffSiteClaims: 'Atribuciones de sitios',
    staffClaimsIntro: 'Los sitios existentes que no son de ventas necesitan aprobación. No se otorga acceso a la cartera ni comisión mientras una atribución espera revisión.',
    staffNoClaims: 'No hay atribuciones de sitios',
    staffNoClaimsBody: 'Aquí aparecerán los sitios de ventas registrados para su revisión.',
    staffRegistrant: 'Solicitante',
    staffExisting: 'Existente ({kind}): {id}',
    staffReviewRecorded: 'Revisión registrada.',
    staffReviewFailed: 'La revisión falló.',
    staffDecision: 'Decisión',
    staffReject: 'Rechazar',
    staffApprove: 'Aprobar',
    staffRecordDecision: 'Registrar decisión',
    staffReassignRecorded: 'Reasignación registrada.',
    staffReassign: 'Reasignar atribución',
    staffReassignFailed: 'La reasignación falló.',
    staffRegistrantKind: 'Tipo del nuevo solicitante',
    // Plan revision form
    planNoMonthLimit: 'Sin límite de meses',
    planStoredStandard: 'Revisión estándar guardada',
    planNew: 'Nueva revisión del plan',
    planPrefilled: 'Rellenado a partir de la revisión estándar guardada {revision}. Los planes aceptados y los sellos de cargadores existentes no cambian.',
    planPrefilledCode: 'Rellenado a partir del estándar vigente, revisión {revision}, aún no registrada. Los planes aceptados y los sellos de cargadores existentes no cambian.',
    planLoading: 'cargando',
    planLoadFailed: 'No se pudo cargar el estándar guardado. No se puede registrar una revisión.',
    planStage: 'Etapa del plan',
    planStage1: 'Comisión de la Etapa 1',
    planStage2: 'Tarifas de nómina de la Etapa 2',
    planNoRates: 'No hay tarifas guardadas para esta etapa.',
    planCentsReadback: 'Equivale a {amount}',
    planRecorded: 'Revisión registrada. Una revisión personal requiere aceptación.',
    planFailed: 'No se pudo registrar la revisión.',
    planDatedRevision: 'Revisión del plan o del rol con fecha',
    planEmployeeMove: 'Cambio con fecha a empleado y nómina',
    planRoleOnRevision: 'Rol en esta revisión',
    planNotice: 'Referencia del aviso por escrito',
    planRecord: 'Registrar nueva revisión',
  },
  fr: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Non enregistré',
    notSet: 'Non défini',
    noResults: 'Aucun résultat',
    person: 'Personne',
    status: 'Statut',
    reason: 'Motif',
    partner: 'Partenaire',
    partners: 'Partenaires',
    chooseAPerson: 'Choisissez une personne',
    chooseAPartner: 'Choisissez un partenaire',
    nextStep: 'Prochaine étape',
    ledgerMonth: 'Mois comptable',
    allMonths: 'Tous les mois',
    manager: 'Responsable',
    noManager: 'Aucun responsable',
    rep: 'Représentant',
    roleManager: 'Responsable, avec son propre portefeuille de représentant',
    territory: 'Territoire',
    startDate: 'Date de début',
    effectiveDate: 'Date d’effet',
    action: 'Action',
    period: 'Période',
    currency: 'Devise',
    location: 'Emplacement',
    name: 'Nom',
    email: 'E-mail',
    paymentMethod: 'Mode de paiement',
    stripeConnect: 'Stripe Connect',
    payroll: 'Paie',
    payrollEligibility: 'Admissibilité à la paie',
    activePartners: 'Partenaires actifs',
    marginShare: 'Part de marge',
    heldUnderCap: 'Retenu au titre du plafond',
    earned: 'Gagné',
    paymentsAndPayroll: 'Paiements et paie',
    salesStaff: 'Personnel commercial',
    salesPartner: 'Partenaire de Vente',
    recording: 'Enregistrement...',
    recordAssignment: 'Enregistrer l’affectation',
    assignmentFailed: 'Échec de l’affectation.',
    pointOne: '{n} point',
    pointOther: '{n} points',
    statusActive: 'actif',
    statusInactive: 'inactif',
    statusQualified: 'qualifié',
    statusNotQualified: 'non qualifié',
    statusAccepted: 'accepté',
    statusAwaitingAcceptance: 'en attente d’acceptation',
    statusAssigned: 'affecté',
    statusOffline: 'hors ligne',
    statusNotStarted: 'non commencé',
    statusPending: 'en attente',
    statusNoSubmission60: 'Aucune soumission depuis 60 jours',
    statusBelowPace: 'Sous le rythme sur 6 mois',
    statusOrderFollowUp: 'Suivi de commande nécessaire',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Équipe commerciale',
    teamSubtitle: 'Votre réseau, l’activité de l’équipe et les gains enregistrés.',
    teamOpenPerson: 'Ouvrir l’équipe d’une personne',
    teamLoading: 'Chargement de votre équipe',
    teamChoose: 'Choisissez une équipe',
    teamEmptyBody: 'Les données d’équipe et de portefeuille auxquelles vous avez accès apparaissent ici.',
    tabMap: 'Carte',
    tabTeam: 'Équipe',
    tabReports: 'Rapports',
    tabScoreboard: 'Classement',
    teamMyBook: 'Mon portefeuille',
    teamWholeTeam: 'Toute l’équipe',
    teamNetworkMap: 'Carte du réseau',
    teamEveryone: 'Toutes les personnes de votre périmètre',
    teamStage: 'Étape',
    teamAllStages: 'Toutes les étapes',
    teamChargerType: 'Type de borne',
    teamAllTypes: 'Tous les types',
    teamState: 'État',
    teamAllStates: 'Tous les États',
    teamLaunchedFrom: 'Lancé à partir du',
    teamLaunchedThrough: 'Lancé jusqu’au',
    teamPinLegend: 'La couleur du repère identifie la personne. Un cercle plein indique en ligne, un cercle vide hors ligne et un point d’exclamation une panne. Sites sans coordonnées sur la carte : {n}.',
    teamCloseDetails: 'Fermer les détails du site',
    teamCity: 'Ville',
    teamChargers: 'Bornes',
    teamDirectDeal: 'Vente directe',
    teamLaunchDate: 'Date de lancement',
    teamNotLaunched: 'Non lancé',
    teamSessions30: 'Sessions, 30 derniers jours',
    teamKwh30: 'kWh, 30 derniers jours',
    teamNoSites: 'Aucun site ne correspond',
    teamNoSitesBody: 'Modifiez les filtres pour voir d’autres sites de votre portefeuille.',
    teamSite: 'Site',
    teamDirectReports: 'Subordonnés directs',
    teamNoDirectReports: 'Aucun subordonné direct',
    teamNoDirectReportsBody: 'Les affectations datées apparaissent ici une fois enregistrées.',
    teamActivity: 'Activité',
    teamEarnedMonth: 'Gagné ce mois-ci',
    teamActivatedMonth: 'Activés ce mois-ci',
    teamLaunchedMonth: 'Lancés ce mois-ci',
    teamPace: 'Rythme',
    teamOnPace: 'Dans le rythme',
    teamEligibility: '{active}/{target} actifs pendant {months}/{required} mois.',
    teamUsdAverage: 'Moyenne sur six mois en USD : {amount}',
    teamNotAvailable: 'Non disponible',
    teamScoreboard: 'Classement de l’équipe',
    teamThisWeek: 'Cette semaine',
    teamThisMonth: 'Ce mois-ci',
    teamScoreNote: 'Décomptes uniquement. Classement par lancements admissibles, puis par partenaires actifs et activations. Les prospects, démonstrations et candidatures s’appuient sur vos enregistrements d’activité audités. Les appels et conversations s’appuient sur les notes de partenaires. Aucune estimation n’est ajoutée.',
    teamOutreach: 'Prospection',
    teamLaunches: 'Lancements',
    scoreLeadsStarted: 'prospects ouverts',
    scoreCalls: 'appels',
    scoreConversations: 'conversations',
    scoreDemos: 'démonstrations',
    scoreApplications: 'candidatures',
    scoreApproved: 'approuvés',
    scoreFirstPaidOrders: 'premières commandes payées',
    scoreActivated: 'activés',
    scoreActivePartners: 'partenaires actifs',
    scoreQualifyingLaunches: 'lancements admissibles',
    reportMonthly: 'Commission par mois et par catégorie',
    reportSessions: 'Points de session par borne',
    reportYearEnd: 'Totaux de fin d’année',
    reportsTitle: 'Mes rapports',
    reportYear: 'Année',
    reportKind: 'Rapport',
    reportDownloadFailed: 'Échec du téléchargement du rapport.',
    reportExport: 'Exporter en CSV',
    reportEmpty: 'Aucune activité enregistrée',
    reportEmptyBody: 'Les gains enregistrés et les justificatifs de paiement apparaîtront ici.',
    reportNote: 'Les devises comptables restent séparées. Les totaux de fin d’année aident à préparer les déclarations et ne constituent pas un formulaire fiscal.',
    activityRecorded: 'Activité enregistrée.',
    activityFailed: 'Impossible d’enregistrer l’activité.',
    activityLabel: 'Enregistrer mon activité',
    activityLead: 'Nouveau prospect ouvert',
    activityDemo: 'Démonstration',
    activityApplication: 'Candidature',
    activityEvidence: 'Référence du justificatif',
    activityPlaceholder: 'Référence de votre activité',
    activityRecord: 'Enregistrer l’activité',
    // Sales payout run
    payTitle: 'Exécution des versements',
    paySubtitle: 'Vérifiez les relevés clôturés, puis confirmez les paiements. Les versements automatiques restent désactivés jusqu’à leur approbation.',
    payCycle: 'Cycle mensuel',
    payCycleBody: 'Les relevés sont clôturés le 1er. La vérification a lieu du 1er au 4. Le paiement a lieu le 5 ou le jour ouvré suivant. Chaque devise est payée séparément. Les comptes non prêts et les virements échoués restent à payer.',
    payStatementMonth: 'Mois du relevé',
    payClose: 'Clôturer les relevés',
    payPay: 'Payer et exporter la paie',
    paySync: 'Synchroniser les gains',
    paySynced: 'Gains et annulations synchronisés.',
    payPreview: 'Aperçu uniquement',
    payRequestFailed: 'Échec de la requête.',
    payNewLines: 'Nouvelles lignes comptables : {n}. Vérification du relevé requise.',
    payDate: 'Date de paiement : {date}.',
    payMayProceed: 'Les paiements peuvent être effectués.',
    payWait: 'Les paiements attendent cette date.',
    payNothing: 'Rien à traiter',
    payNothingBody: 'Aucun solde de relevé ou de paiement n’est disponible pour ce mois.',
    payLedgerHeld: 'Comptabilisé et retenu',
    payPayment: 'Paiement',
    payReview: 'Vérification',
    payHeld: 'Retenu : {amount}',
    payConversion: 'Frais de conversion : {amount}',
    payReady: 'Prêt',
    payPrepareQuote: 'Préparer un devis de conversion',
    payQuotePrepared: 'Devis préparé. Lancez un nouvel aperçu.',
    payConfirmCheck: 'J’ai vérifié ces montants, ces devises et les bénéficiaires bloqués.',
    payConfirmClose: 'Confirmer la clôture des relevés',
    payConfirmPay: 'Confirmer l’exécution des versements',
    payRunRecorded: 'Exécution enregistrée : {result}',
    payPayroll: 'Export et rapprochement de la paie',
    payDownload: 'Télécharger le CSV de la paie enregistrée',
    payPaymentId: 'ID de paiement ou d’export',
    payProviderRef: 'Référence du prestataire',
    payRecordPayroll: 'Enregistrer la paie effectuée',
    payPayrollRecorded: 'Paiement de la paie enregistré.',
    payActualCost: 'Frais de conversion réels',
    payActualBody: 'Enregistrez les frais de conversion définitifs du prestataire dans la devise comptable. Un écart par rapport au devis devient un ajustement sur le relevé ouvert en cours.',
    payLedgerCurrency: 'Devise comptable',
    payActualCents: 'Coût réel en centimes',
    payRecordActual: 'Enregistrer le coût réel',
    payActualReconciled: 'Frais de conversion réels rapprochés.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Activité, emplacements et prochaines étapes des partenaires de votre portefeuille.',
    bookLoadingPartners: 'Chargement de vos partenaires...',
    bookQualifyingChargers: 'Bornes admissibles',
    bookLaunches12: 'Lancements sur 12 mois',
    bookActivityTarget: 'Objectif d’activité : 8',
    bookLaunches6: 'Lancements sur 6 mois',
    bookPaceTarget: 'Objectif de rythme : 4',
    bookLocationsSubmissions: 'Emplacements / soumissions',
    bookLastContact: 'Dernier contact',
    bookLocationsHeading: 'Emplacements et soumissions',
    bookNoLocations: 'Aucun emplacement pour l’instant',
    bookNoLocationsBody: 'Les emplacements soumis par ce partenaire apparaîtront ici.',
    bookNotes: 'Notes et prochaines étapes',
    bookNoPartners: 'Aucun partenaire dans votre portefeuille',
    bookNoPartnersBody: 'Les partenaires affectés et leurs emplacements apparaîtront ici.',
    bookLoadingSubmission: 'Chargement de la soumission...',
    bookLocationType: 'Type d’emplacement',
    bookAddress: 'Adresse',
    bookContact: 'Contact',
    bookConnectivity: 'Connectivité',
    bookCommissioning: 'Mise en service autorisée',
    bookAwaitingReview: 'En attente de vérification par EVI',
    bookInstallerExpires: 'Expiration de l’accès installateur',
    bookNotIssued: 'Non délivré',
    bookLaunch: 'Lancement',
    bookAwaitingApproval: 'En attente d’approbation par EVI',
    bookOrdersTitle: 'Commandes',
    bookOrdersSubtitle: 'Commandes clients attribuées à vous et aux partenaires de votre portefeuille.',
    bookLoadingOrders: 'Chargement des commandes...',
    bookOrderDate: 'Date de commande',
    bookDirectStaffOrder: 'Commande directe du personnel',
    bookOrderTotal: 'Total de la commande',
    bookQty: 'Qté {n}',
    bookRegisterDeal: 'Enregistrer cette vente directe',
    bookClaimRecorded: 'Revendication du site enregistrée. Les sites existants hors ventes nécessitent une vérification par un super administrateur.',
    bookClaimFailed: 'Impossible d’enregistrer la revendication du site.',
    bookDealEvidence: 'Justificatif de la vente directe',
    bookRegisterButton: 'Enregistrer la vente directe',
    bookNoOrders: 'Aucune commande pour l’instant',
    bookNoOrdersBody: 'Les commandes de vos ventes directes et de votre portefeuille de partenaires apparaîtront ici.',
    bookNewestOrders: 'Commandes les plus récentes',
    bookOlderOrders: 'Commandes plus anciennes',
    bookOnboardingTitle: 'Aide à l’intégration',
    bookOnboardingSubtitle: 'Aidez les personnes des emplacements de votre portefeuille à obtenir l’accès dont elles ont besoin.',
    bookInvite: 'Inviter une personne',
    bookInviteBody: 'Invitez ou renvoyez une invitation. L’envoi suit la politique de messagerie en vigueur.',
    bookNoInviteLocations: 'Aucun emplacement où inviter pour l’instant',
    bookNoInviteLocationsBody: 'Un emplacement doit figurer dans votre portefeuille avant que vous puissiez inviter ses utilisateurs.',
    bookInviteRecorded: 'Invitation enregistrée. L’envoi suit la politique de messagerie en vigueur.',
    bookInviteFailed: 'Impossible d’envoyer l’invitation.',
    bookChooseLocation: 'Choisissez un emplacement',
    bookAccess: 'Accès',
    roleBuildingAdmin: 'Administrateur du bâtiment',
    rolePropertyManager: 'Gestionnaire d’immeuble',
    roleDriver: 'Conducteur',
    bookInviting: 'Enregistrement de l’invitation...',
    bookSendInvite: 'Envoyer ou renvoyer l’invitation',
    // Partner and direct site notes
    notesHeading: 'Appels, visites et e-mails',
    notesIntro: 'Enregistrez chaque échange le jour même. Les notes appartiennent à EVI. Une correction ajoute une version et conserve l’original.',
    notesEmpty: 'Aucune note pour l’instant',
    notesEmptyBody: 'Enregistrez ci-dessous le premier contact et une prochaine étape claire.',
    notesVersion: 'Version {n}',
    notesDue: 'Échéance',
    notesAddCorrection: 'Ajouter une version corrigée',
    notesLinksFailed: 'Impossible de charger les emplacements et soumissions liés.',
    notesRecorded: 'Note enregistrée.',
    notesFailed: 'Impossible d’enregistrer la note.',
    notesChannel: 'Canal',
    notesCall: 'Appel',
    notesVisit: 'Visite',
    notesEmailChannel: 'E-mail',
    notesProjectStage: 'Étape du projet',
    notesNote: 'Note',
    notesNextDue: 'Échéance de la prochaine étape',
    notesLocationOptional: 'Emplacement (facultatif)',
    notesNoLocation: 'Aucun emplacement lié',
    notesSubmissionOptional: 'Soumission (facultatif)',
    notesNoSubmission: 'Aucune soumission liée',
    notesRecordVersion: 'Enregistrer la nouvelle version',
    notesRecordNote: 'Enregistrer la note',
    notesCancel: 'Annuler la correction',
    notesSiteTitle: 'Notes des sites directs',
    notesNoClaims: 'Aucune revendication de site direct',
    notesNoClaimsBody: 'Vos revendications de sites directs et leur historique de contact apparaîtront ici.',
    notesStaffTitle: 'Notes de partenaires et prochaines étapes',
    notesStaffIntro: 'Enregistrez des notes pour n’importe quel partenaire, y compris les partenaires maison d’EVI. Les notes restent attachées au partenaire lorsqu’il est affecté à un représentant.',
    notesFindPartner: 'Rechercher un partenaire',
    notesHouseBook: ' (portefeuille maison d’EVI)',
    notesFirstPartners: 'Premiers partenaires',
    notesMorePartners: 'Plus de partenaires',
    notesChooseBody: 'Sélectionnez un partenaire pour consulter ou enregistrer l’historique de contact.',
    // My commission and Sales statements
    commPlanRevision: 'Plan de commission, révision {n}',
    commOfferRevision: 'Votre offre, révision {n}',
    commEffective: 'En vigueur à partir du {date}',
    commThrough: 'jusqu’au {date}',
    commAccepted: 'Accepté le {date}. Votre plan accepté est affiché en lecture seule.',
    commDirectMargin: 'Marge directe sur le matériel',
    commAboveThreshold: 'Au-delà du seuil annuel',
    commPartnerPoints: 'Points de session des partenaires',
    commFleetShare: 'Part des abonnements de flotte',
    commFleetMeta: '{n} mois ; commandes de flotte attribuées uniquement',
    commFleetEvery: 'Chaque renouvellement tant que vous faites partie de l’équipe ; commandes de flotte uniquement',
    commAnnualThreshold: 'Seuil annuel de marge sur le matériel ({currency})',
    commFirstOrder: 'Première commande payée admissible',
    commFourthCharger: 'Quatrième borne admissible',
    commBookDuration: 'Durée de la marge du portefeuille de partenaires',
    commNoMonthLimit: 'Sans limite de mois pendant l’affectation',
    commMonthsPerPartner: '{n} mois par partenaire',
    commContinuation: 'Prolongation après un départ en bons termes',
    commMonths: '{n} mois',
    commOwnLadder: 'Barème des bornes directes propres',
    commFromCharger: 'À partir de la borne estampillée admissible',
    commSessionPoints: 'Points de session',
    commLadderNote: 'L’accumulation commence à la quatrième borne directe admissible. Les 12 premiers mois constituent une année de montée en charge. Ensuite, un nouveau lancement nécessite 8 lancements admissibles au cours des 12 derniers mois, lui compris. Les lancements non estampillés comptent pour l’activité mais ne reçoivent jamais d’estampille par la suite. Les estampilles existantes continuent de rapporter tant que vous faites partie de l’équipe.',
    commBookTiers: 'Paliers de marge du portefeuille de partenaires',
    commActiveAtLeast: 'Partenaires actifs, au moins',
    commPointsNote: 'Les points de session des partenaires s’appliquent après la commission du partenaire. Les montants de session du personnel sont plafonnés au solde disponible d’EVI. La part des abonnements de flotte commence dès que la facturation des abonnements est active ; les autres abonnements ne donnent pas droit à une part.',
    commGolden: 'Prime Golden versée : {amount}.',
    commOverrideTiers: 'Paliers de commission du responsable sur l’équipe',
    commTeamActiveAtLeast: 'Partenaires actifs de l’équipe, au moins',
    commGrowth: 'Primes de croissance du responsable : {six} à six partenaires actifs et {payroll} au palier de paie du subordonné. Points de session de l’équipe : {points}.',
    commAccepting: 'Acceptation...',
    commAccept: 'Accepter ce plan de commission',
    commOfferAccepted: 'Offre acceptée.',
    commOfferFailed: 'Échec de l’acceptation de l’offre.',
    commStage2: 'Informations sur l’étape 2',
    commStage2Body: 'Six mois complets de commission enregistrée, après annulations. Cela ne vous fait pas passer à l’étape 2. Un super administrateur doit enregistrer une révision de plan approuvée.',
    commConsecutive: '{n} sur {required} mois consécutifs',
    commCompletedMonth: 'Mois terminé',
    commRecordedCommission: 'Commission enregistrée ({currency})',
    commNoMonths: 'Aucun mois de plan terminé pour l’instant',
    commNoMonthsBody: 'Seuls les mois terminés depuis la date d’effet de votre plan apparaissent ici.',
    commMonthsAvailable: '{n} sur 6 mois terminés disponibles.',
    commAverage: 'Moyenne : {amount}',
    commAgainst: 'par rapport à {amount}',
    commNoCrossCurrency: ', sans comparaison entre devises',
    commStatements: 'Relevés mensuels',
    commNoStatements: 'Aucun relevé pour l’instant',
    commNoStatementsBody: 'Votre premier relevé apparaît après la clôture d’un mois de commission.',
    commOpening: 'Ouverture',
    commReversals: 'Annulations',
    commPaid: 'Payé',
    commClosing: 'Clôture',
    commNoPayments: 'Aucun paiement pour l’instant',
    commNoPaymentsBody: 'Les paiements Connect ou les enregistrements de paie apparaîtront une fois les gains payés.',
    commLedgerAmount: 'Montant comptabilisé',
    commMonth: 'Mois',
    commStripeTransfer: 'Virement Stripe',
    commNotSent: 'Non envoyé',
    commConversionCost: 'Frais de conversion',
    commCashAmount: 'Montant versé',
    commSentAs: 'Envoyé sous forme de {amount}',
    commReservedAs: 'Réservé sous forme de {amount}',
    commLines: 'Lignes de commission',
    commNoLines: 'Aucune ligne de commission pour l’instant',
    commNoLinesBody: 'Les activités admissibles créent ici des lignes de commission, et les corrections ultérieures sont affichées séparément.',
    commCategory: 'Catégorie',
    commBasis: 'Base',
    commPlainTerms: "Comment lire ceci : la base est le montant auquel s’applique ton taux (pour une commande de matériel, la marge d’EVI). Retenu signifie gagné mais pas encore payé : EVI paie tes gains sur un partenaire à partir de la marge matériel encaissée auprès de ce partenaire, jusqu’à la moitié, et le reste est payé à mesure que ses commandes suivantes en apportent davantage. L’étape 2 est le plan salarié d’EVI avec un salaire de base : six mois complets de commission sont le seuil pour être considéré, et le passage est un nouveau plan convenu avec toi.",
    commRate: 'Taux',
    commCommission: 'Commission',
    commPlanRevisionHeader: 'Révision du plan',
    commTitle: 'Ma commission',
    commSubtitle: 'Votre offre, votre activité de commission et vos enregistrements de paiement. Les CAD et les USD restent séparés.',
    commNoOffer: 'Aucune offre enregistrée pour l’instant',
    commNoOfferBody: 'EVI enregistrera un plan de commission que vous pourrez consulter et accepter.',
    commClosedNote: 'Les relevés clôturés ne changent pas. Les corrections apparaissent sous forme de nouvelles lignes.',
    commLoading: 'Chargement de la commission...',
    commNewestLines: 'Lignes les plus récentes',
    commOlderLines: 'Lignes plus anciennes',
    stmtTitle: 'Relevés commerciaux',
    stmtSubtitle: 'Consultez les commissions, relevés, paiements et paies enregistrés de votre équipe commerciale.',
    stmtNote: 'Utilisez les scripts vérifiés pour comptabiliser et clôturer un mois, payer les prestataires Connect ou exporter la paie. Cette page ne lance jamais d’encaissement ni de virement.',
    stmtChooseBody: 'Sélectionnez un membre du personnel pour consulter son registre de commission et ses relevés.',
    stmtReviewQueue: 'File de vérification',
    stmtNothingReview: 'Rien en attente de vérification',
    stmtNothingReviewBody: 'Les éléments de vérification du règlement apparaîtront ici lorsqu’un justificatif nécessitera votre attention.',
    stmtSource: 'Origine',
    stmtCostMissing: 'Coût non enregistré',
    stmtCostIntro: 'Les justificatifs manuels réservés aux administrateurs sont conservés séparément. L’instantané d’origine de la commande n’est pas modifié.',
    stmtNoMissing: 'Aucun justificatif de coût manquant',
    stmtNoMissingBody: 'Les commandes payées attribuées sans instantané de coût apparaîtront ici.',
    stmtCostRecorded: 'Justificatif de coût enregistré.',
    stmtCostFailed: 'Impossible d’enregistrer le justificatif de coût.',
    stmtChooseCurrency: 'Choisissez la devise',
    stmtUnits: '{name} : {n} unités',
    stmtUnitCost: 'Coût unitaire',
    stmtAutelFee: 'Frais Autel absorbés par unité',
    stmtEvidenceReason: 'Justificatif et motif',
    stmtRecordEvidence: 'Enregistrer le justificatif manuel',
    stmtCorrection: 'Ajouter une correction',
    stmtCorrectionIntro: 'Les annulations sont négatives. Les rétablissements ne peuvent pas dépasser le droit initial. Utilisez une référence stable pour une nouvelle tentative.',
    stmtNoLinesToCorrect: 'Aucune ligne à corriger',
    stmtNoLinesToCorrectBody: 'Une ligne de commission positive est requise avant de pouvoir ajouter une correction.',
    stmtCorrectionRecorded: 'Correction enregistrée.',
    stmtCorrectionFailed: 'Échec de la correction.',
    stmtOriginalLine: 'Ligne d’origine',
    stmtSignedAmount: 'Montant signé',
    stmtReference: 'Référence',
    stmtAppend: 'Ajouter la correction',
    // Sales Staff
    staffTitle: 'Personnel commercial',
    staffSubtitle: 'Gérez les personnes, les plans de commission datés et les portefeuilles de partenaires. Les modifications sont auditées.',
    staffNote: 'Seul un super administrateur peut modifier le personnel, les plans ou les affectations de partenaires.',
    staffPeople: 'Personnes',
    staffActive: 'Actifs',
    staffContractors: 'Prestataires',
    staffNoStaff: 'Aucun personnel commercial pour l’instant',
    staffNoStaffBody: 'Ajoutez ci-dessous une personne disposant d’un compte actif existant.',
    staffKind: 'Type',
    staffRole: 'Rôle',
    staffConnect: 'Connect',
    staffEligible: 'Admissible',
    staffNotEligible: 'Pas encore admissible',
    staffEligibility: '{status} : {active}/{target} actifs, {months}/{required} mois',
    staffAdd: 'Ajouter une personne',
    staffCreated: 'Fiche du personnel créée.',
    staffAddFailed: 'Impossible d’ajouter la personne.',
    staffInviteSent: 'Invitation envoyée. La fiche du personnel est créée avec ce rôle, ce responsable et cette date de début lorsqu’elle est acceptée.',
    staffInviteNeedsName: 'Cette adresse n’a pas encore de compte : la personne recevra une invitation. Ajoutez d’abord son nom.',
    staffInvitesTitle: 'Invités, pas encore acceptés',
    staffInvitedOn: 'Invité le {date}',
    staffInviteFailed: 'Non créée lors de l’acceptation : {reason}',
    staffResendInvite: 'Renvoyer l’invitation',
    staffInviteResent: 'Invitation renvoyée.',
    staffCancelInvite: 'Annuler l’invitation',
    staffInviteCancelled: 'Invitation annulée.',
    staffInviteActionFailed: 'Impossible de modifier l’invitation.',
    inviteLinkAction: "Copier le lien d’invitation",
    inviteLinkTitle: "Copier le lien d’invitation",
    inviteLinkField: "Lien d’invitation",
    inviteLinkLoading: "Création d’un nouveau lien...",
    inviteLinkCopy: "Copier",
    inviteLinkCopied: "Copié",
    inviteLinkSelected: "Le lien est sélectionné. Appuyez sur Ctrl+C, ou Cmd+C sur un Mac, pour le copier.",
    inviteLinkEmail: "Envoyer depuis mon compte",
    inviteLinkReplaces: "Ce lien remplace celui des e-mails d’invitation précédents. Il ne fonctionne qu’une fois et expire le {date}.",
    inviteLinkDiscard: "Fermer cette fenêtre supprime le lien. Il ne sera plus jamais affiché.",
    inviteLinkExpired: "Cette invitation a expiré. Renvoyez-la pour en créer une nouvelle, puis copiez ce lien.",
    inviteLinkFailed: "Le lien d’invitation n’a pas pu être créé.",
    inviteLinkClose: "Fermer",
    inviteLinkMailSubject: "Votre invitation EV Initiative",
    inviteLinkMailGreeting: "Bonjour {name},",
    inviteLinkMailGreetingNoName: "Bonjour,",
    inviteLinkMailBody: "Je vous envoie votre invitation EV Initiative depuis ma propre adresse au cas où la précédente ne vous serait pas parvenue. Le lien ci-dessous ne fonctionne qu’une fois et expire le {date}.",
    inviteLinkMailSignoff: "Merci,",
    inviteLinkSignInPage: "Cette personne a déjà un compte EV Initiative avec cette adresse, donc ce lien ouvre la page de connexion. Se connecter avec ce compte accepte l’invitation.",
    inviteLinkMailSignInPage: "Je vous envoie votre invitation EV Initiative depuis ma propre adresse au cas où la précédente ne vous serait pas parvenue. Vous avez déjà un compte EV Initiative avec cette adresse : connectez-vous avec ce compte via le lien ci-dessous pour l’accepter.",
    staffAccountEmail: 'E-mail du compte',
    staffStaffRole: 'Rôle commercial',
    staffContractor: 'Prestataire',
    staffEmployee: 'Salarié',
    staffPayThrough: 'Payer via',
    staffStatesComma: 'États (séparés par des virgules)',
    staffCreate: 'Créer la fiche du personnel',
    staffPersonDetails: 'Détails de la personne',
    staffChooseBody: 'Sélectionnez une ligne ou utilisez Détails de la personne pour consulter sa fiche, ses plans et son portefeuille.',
    staffRecord: 'Fiche du personnel',
    staffAccount: 'Compte',
    staffPartnerBook: 'Portefeuille de partenaires',
    staffNoPartners: 'Aucun partenaire affecté',
    staffNoPartnersBody: 'Affectez un partenaire ci-dessous pour l’ajouter au portefeuille de cette personne.',
    staffMoveAll: "Déplacer tous les partenaires",
    staffMoveAllIntro: "Déplace dès maintenant tous les partenaires que {name} détient aujourd’hui vers un autre SP Manager ou le portefeuille d’EVI. Les bornes déjà lancées gardent leur tampon de lancement.",
    staffMoveAllTo: "Déplacer vers",
    staffHouseBook: "Portefeuille d’EVI",
    staffMoveAllButton: "Déplacer tous les partenaires",
    staffMoveAllDone: "{count} partenaires déplacés.",
    staffDepartureMoves: "À la date de départ, les partenaires de cette personne passent à son Sales Manager, ou au portefeuille d’EVI s’il n’y en a pas.",
    staffDeparted: 'Départ le {date} : {reason}',
    staffUpdated: 'Fiche du personnel mise à jour.',
    staffUpdateFailed: 'Échec de la mise à jour.',
    staffStates: 'États',
    staffEmploymentNote: 'Les changements d’emploi utilisent le passage daté dans Nouvelle révision du plan. Les estampilles de bornes, les affectations de portefeuille et la fenêtre d’activité restent attachées à cette fiche du personnel.',
    staffEndDate: 'Date de fin (facultatif)',
    staffDepartureReason: 'Motif du départ',
    staffGoodStanding: 'En bons termes',
    staffCause: 'Pour faute',
    staffConductIds: 'ID des lignes comptables liées à la conduite (faute uniquement)',
    staffSave: 'Enregistrer les modifications du personnel',
    staffManagerAssignment: 'Affectation datée du responsable',
    staffManagerRecorded: 'Affectation datée du responsable enregistrée.',
    staffPlanRevisions: 'Révisions du plan',
    staffNoPlans: 'Aucune révision du plan',
    staffNoPlansBody: 'Enregistrez une offre datée que cette personne devra accepter.',
    staffRevision: 'Révision',
    staffEffectiveFrom: 'En vigueur à partir du',
    staffEffectiveTo: 'En vigueur jusqu’au',
    staffOngoing: 'En cours',
    staffAssignPartner: 'Affecter un partenaire',
    staffAssignIntro: 'Enregistrez une nouvelle affectation avec son motif. L’historique des affectations précédentes reste intact.',
    staffAssignmentRecorded: 'Affectation enregistrée.',
    staffUnassigned: 'Non affecté',
    staffStandardPlan: 'Plan standard pour les futurs représentants',
    staffSiteClaims: 'Revendications de sites',
    staffClaimsIntro: 'Les sites existants hors ventes nécessitent une approbation. Aucun accès au portefeuille ni aucune commission n’est accordé tant qu’une revendication attend une vérification.',
    staffNoClaims: 'Aucune revendication de site',
    staffNoClaimsBody: 'Les sites commerciaux enregistrés apparaîtront ici pour vérification.',
    staffRegistrant: 'Déclarant',
    staffExisting: 'Existant ({kind}) : {id}',
    staffReviewRecorded: 'Vérification enregistrée.',
    staffReviewFailed: 'Échec de la vérification.',
    staffDecision: 'Décision',
    staffReject: 'Refuser',
    staffApprove: 'Approuver',
    staffRecordDecision: 'Enregistrer la décision',
    staffReassignRecorded: 'Réaffectation enregistrée.',
    staffReassign: 'Réaffecter la revendication',
    staffReassignFailed: 'Échec de la réaffectation.',
    staffRegistrantKind: 'Type du nouveau déclarant',
    // Plan revision form
    planNoMonthLimit: 'Sans limite de mois',
    planStoredStandard: 'Révision standard enregistrée',
    planNew: 'Nouvelle révision du plan',
    planPrefilled: 'Prérempli à partir de la révision standard enregistrée {revision}. Les plans acceptés et les estampilles de bornes existantes restent inchangés.',
    planPrefilledCode: 'Prérempli à partir du standard en vigueur, révision {revision}, pas encore enregistrée. Les plans acceptés et les estampilles de bornes existantes restent inchangés.',
    planLoading: 'chargement',
    planLoadFailed: 'Impossible de charger le standard enregistré. Aucune révision ne peut être enregistrée.',
    planStage: 'Étape du plan',
    planStage1: 'Commission de l’étape 1',
    planStage2: 'Taux de paie de l’étape 2',
    planNoRates: 'Aucun taux enregistré n’est disponible pour cette étape.',
    planCentsReadback: 'Soit {amount}',
    planRecorded: 'Révision enregistrée. Une révision personnelle nécessite une acceptation.',
    planFailed: 'Impossible d’enregistrer la révision.',
    planDatedRevision: 'Révision datée du plan ou du rôle',
    planEmployeeMove: 'Passage daté au statut de salarié et à la paie',
    planRoleOnRevision: 'Rôle pour cette révision',
    planNotice: 'Référence de l’avis écrit',
    planRecord: 'Enregistrer la nouvelle révision',
  },
  zh: {
    // Shared labels and the status values the screens write themselves
    notRecorded: '未记录',
    notSet: '未设置',
    noResults: '无结果',
    person: '成员',
    status: '状态',
    reason: '原因',
    partner: '合作伙伴',
    partners: '合作伙伴',
    chooseAPerson: '选择成员',
    chooseAPartner: '选择合作伙伴',
    nextStep: '下一步',
    ledgerMonth: '账本月份',
    allMonths: '所有月份',
    manager: '经理',
    noManager: '无经理',
    rep: '销售代表',
    roleManager: '经理（拥有自己的销售代表业务组合）',
    territory: '负责区域',
    startDate: '开始日期',
    effectiveDate: '生效日期',
    action: '操作',
    period: '期间',
    currency: '币种',
    location: '场地',
    name: '姓名',
    email: '电子邮箱',
    paymentMethod: '付款方式',
    stripeConnect: 'Stripe Connect',
    payroll: '工资发放',
    payrollEligibility: '转入工资发放的资格',
    activePartners: '活跃合作伙伴',
    marginShare: '利润分成',
    heldUnderCap: '因上限暂扣',
    earned: '已赚取',
    paymentsAndPayroll: '付款和工资发放',
    salesStaff: '销售人员',
    salesPartner: '销售合作伙伴',
    recording: '正在记录...',
    recordAssignment: '记录分配',
    assignmentFailed: '分配失败。',
    pointOne: '{n} 点',
    pointOther: '{n} 点',
    statusActive: '活跃',
    statusInactive: '不活跃',
    statusQualified: '已达标',
    statusNotQualified: '未达标',
    statusAccepted: '已接受',
    statusAwaitingAcceptance: '待接受',
    statusAssigned: '已分配',
    statusOffline: '离线',
    statusNotStarted: '未开始',
    statusPending: '待处理',
    statusNoSubmission60: '60 天内无提交',
    statusBelowPace: '低于 6 个月节奏',
    statusOrderFollowUp: '订单需跟进',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: '销售团队',
    teamSubtitle: '你的网络、团队活动和已记录的收入。',
    teamOpenPerson: '打开成员的团队',
    teamLoading: '正在加载你的团队',
    teamChoose: '选择团队',
    teamEmptyBody: '你有权查看的团队和业务组合数据会显示在这里。',
    tabMap: '地图',
    tabTeam: '团队',
    tabReports: '报告',
    tabScoreboard: '排行榜',
    teamMyBook: '我的业务组合',
    teamWholeTeam: '整个团队',
    teamNetworkMap: '网络地图',
    teamEveryone: '范围内的所有人',
    teamStage: '阶段',
    teamAllStages: '所有阶段',
    teamChargerType: '充电桩类型',
    teamAllTypes: '所有类型',
    teamState: '州',
    teamAllStates: '所有州',
    teamLaunchedFrom: '上线起始日期',
    teamLaunchedThrough: '上线截止日期',
    teamPinLegend: '图钉颜色代表成员。实心圆表示在线，空心圆表示离线，感叹号表示故障。没有地图坐标的场地数：{n}。',
    teamCloseDetails: '关闭场地详情',
    teamCity: '城市',
    teamChargers: '充电桩',
    teamDirectDeal: '直接交易',
    teamLaunchDate: '上线日期',
    teamNotLaunched: '未上线',
    teamSessions30: '会话数（最近 30 天）',
    teamKwh30: 'kWh（最近 30 天）',
    teamNoSites: '没有匹配的场地',
    teamNoSitesBody: '更改筛选条件以查看你业务组合中的其他场地。',
    teamSite: '场地',
    teamDirectReports: '直接下属',
    teamNoDirectReports: '没有直接下属',
    teamNoDirectReportsBody: '带日期的分配记录后会显示在这里。',
    teamActivity: '活动',
    teamEarnedMonth: '本月已赚取',
    teamActivatedMonth: '本月激活',
    teamLaunchedMonth: '本月上线',
    teamPace: '节奏',
    teamOnPace: '符合节奏',
    teamEligibility: '活跃 {active}/{target}，持续 {months}/{required} 个月。',
    teamUsdAverage: '六个月 USD 平均值：{amount}',
    teamNotAvailable: '不可用',
    teamScoreboard: '团队排行榜',
    teamThisWeek: '本周',
    teamThisMonth: '本月',
    teamScoreNote: '仅计数。按达标上线数排名，其次按活跃合作伙伴数和激活数排名。线索、演示和申请使用你经过审计的活动记录。通话和沟通使用合作伙伴备注。不添加任何估算。',
    teamOutreach: '拓展',
    teamLaunches: '上线',
    scoreLeadsStarted: '已开始线索',
    scoreCalls: '通话',
    scoreConversations: '沟通',
    scoreDemos: '演示',
    scoreApplications: '申请',
    scoreApproved: '已批准',
    scoreFirstPaidOrders: '首笔付费订单',
    scoreActivated: '已激活',
    scoreActivePartners: '活跃合作伙伴',
    scoreQualifyingLaunches: '达标上线',
    reportMonthly: '按月份和类别的佣金',
    reportSessions: '按充电桩的会话点数',
    reportYearEnd: '年终合计',
    reportsTitle: '我的报告',
    reportYear: '年份',
    reportKind: '报告',
    reportDownloadFailed: '报告下载失败。',
    reportExport: '导出 CSV',
    reportEmpty: '没有已记录的活动',
    reportEmptyBody: '已记录的收入和付款凭证会显示在这里。',
    reportNote: '账本币种分开记录。年终合计用于辅助准备申报，并非税务表格。',
    activityRecorded: '活动已记录。',
    activityFailed: '无法记录活动。',
    activityLabel: '记录我的活动',
    activityLead: '新线索已开始',
    activityDemo: '演示',
    activityApplication: '申请',
    activityEvidence: '凭证编号',
    activityPlaceholder: '你的活动编号',
    activityRecord: '记录活动',
    // Sales payout run
    payTitle: '付款执行',
    paySubtitle: '先查看已结账的对账单，再确认付款。自动付款在获批前保持关闭。',
    payCycle: '月度周期',
    payCycleBody: '对账单在每月 1 日结账。1 日至 4 日进行审核。5 日或下一个工作日付款。每种币种分别支付。未就绪的账户和失败的转账仍待支付。',
    payStatementMonth: '对账单月份',
    payClose: '结账对账单',
    payPay: '付款并导出工资',
    paySync: '同步收入',
    paySynced: '收入和冲销已同步。',
    payPreview: '仅预览',
    payRequestFailed: '请求失败。',
    payNewLines: '新账本条目：{n}。需要审核对账单。',
    payDate: '付款日期：{date}。',
    payMayProceed: '可以进行付款。',
    payWait: '付款将等待至该日期。',
    payNothing: '没有需要处理的内容',
    payNothingBody: '本月没有可用的对账单或付款余额。',
    payLedgerHeld: '账本与暂扣',
    payPayment: '付款',
    payReview: '审核',
    payHeld: '暂扣：{amount}',
    payConversion: '换汇费用：{amount}',
    payReady: '就绪',
    payPrepareQuote: '准备换汇报价',
    payQuotePrepared: '报价已准备。请重新运行预览。',
    payConfirmCheck: '我已核对这些金额、币种和被阻止的收款方。',
    payConfirmClose: '确认结账对账单',
    payConfirmPay: '确认付款执行',
    payRunRecorded: '执行已记录：{result}',
    payPayroll: '工资导出与对账',
    payDownload: '下载已记录的工资 CSV',
    payPaymentId: '付款或导出 ID',
    payProviderRef: '服务商参考编号',
    payRecordPayroll: '记录已完成的工资发放',
    payPayrollRecorded: '工资付款已记录。',
    payActualCost: '实际换汇费用',
    payActualBody: '以账本币种记录服务商的最终换汇费用。与报价的差额将成为当前未结对账单上的调整。',
    payLedgerCurrency: '账本币种',
    payActualCents: '实际费用（分）',
    payRecordActual: '记录实际费用',
    payActualReconciled: '实际换汇费用已对账。',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: '你业务组合中合作伙伴的活动、场地和下一步。',
    bookLoadingPartners: '正在加载你的合作伙伴...',
    bookQualifyingChargers: '达标充电桩',
    bookLaunches12: '12 个月内上线数',
    bookActivityTarget: '活动目标：8',
    bookLaunches6: '6 个月内上线数',
    bookPaceTarget: '节奏目标：4',
    bookLocationsSubmissions: '场地 / 提交',
    bookLastContact: '最近联系',
    bookLocationsHeading: '场地和提交',
    bookNoLocations: '暂无场地',
    bookNoLocationsBody: '此合作伙伴提交的场地会显示在这里。',
    bookNotes: '备注和下一步',
    bookNoPartners: '你的业务组合中没有合作伙伴',
    bookNoPartnersBody: '已分配的合作伙伴及其场地会显示在这里。',
    bookLoadingSubmission: '正在加载提交...',
    bookLocationType: '场地类型',
    bookAddress: '地址',
    bookContact: '联系人',
    bookConnectivity: '网络连接',
    bookCommissioning: '已授权调试',
    bookAwaitingReview: '等待 EVI 审核',
    bookInstallerExpires: '安装人员访问权限到期',
    bookNotIssued: '未签发',
    bookLaunch: '上线',
    bookAwaitingApproval: '等待 EVI 批准',
    bookOrdersTitle: '订单',
    bookOrdersSubtitle: '归属于你和你业务组合中合作伙伴的客户订单。',
    bookLoadingOrders: '正在加载订单...',
    bookOrderDate: '订单日期',
    bookDirectStaffOrder: '员工直接订单',
    bookOrderTotal: '订单总额',
    bookQty: '数量 {n}',
    bookRegisterDeal: '登记此直接交易',
    bookClaimRecorded: '场地归属申请已记录。现有的非销售场地需要超级管理员审核。',
    bookClaimFailed: '无法记录场地归属申请。',
    bookDealEvidence: '直接交易凭证',
    bookRegisterButton: '登记直接交易',
    bookNoOrders: '暂无订单',
    bookNoOrdersBody: '来自你直接交易和合作伙伴业务组合的订单会显示在这里。',
    bookNewestOrders: '最新订单',
    bookOlderOrders: '更早的订单',
    bookOnboardingTitle: '入驻帮助',
    bookOnboardingSubtitle: '帮助你业务组合中各场地的人员获得所需的访问权限。',
    bookInvite: '邀请用户',
    bookInviteBody: '发送或重新发送邀请。发送遵循当前的邮件政策。',
    bookNoInviteLocations: '暂无可邀请的场地',
    bookNoInviteLocationsBody: '场地必须先在你的业务组合中，你才能邀请其用户。',
    bookInviteRecorded: '邀请已记录。发送遵循当前的邮件政策。',
    bookInviteFailed: '无法发送邀请。',
    bookChooseLocation: '选择场地',
    bookAccess: '访问权限',
    roleBuildingAdmin: '楼宇管理员',
    rolePropertyManager: '物业经理',
    roleDriver: '车主',
    bookInviting: '正在记录邀请...',
    bookSendInvite: '发送或重新发送邀请',
    // Partner and direct site notes
    notesHeading: '通话、拜访和邮件',
    notesIntro: '每次接触请当天记录。备注归 EVI 所有。更正会新增一个版本并保留原始内容。',
    notesEmpty: '暂无备注',
    notesEmptyBody: '在下方记录首次联系和明确的下一步。',
    notesVersion: '版本 {n}',
    notesDue: '截止日期',
    notesAddCorrection: '添加更正版本',
    notesLinksFailed: '无法加载关联的场地和提交。',
    notesRecorded: '备注已记录。',
    notesFailed: '无法记录备注。',
    notesChannel: '渠道',
    notesCall: '通话',
    notesVisit: '拜访',
    notesEmailChannel: '邮件',
    notesProjectStage: '项目阶段',
    notesNote: '备注',
    notesNextDue: '下一步截止日期',
    notesLocationOptional: '场地（可选）',
    notesNoLocation: '无关联场地',
    notesSubmissionOptional: '提交（可选）',
    notesNoSubmission: '无关联提交',
    notesRecordVersion: '记录新版本',
    notesRecordNote: '记录备注',
    notesCancel: '取消更正',
    notesSiteTitle: '直接场地备注',
    notesNoClaims: '没有直接场地归属申请',
    notesNoClaimsBody: '你的直接场地归属申请及其联系记录会显示在这里。',
    notesStaffTitle: '合作伙伴备注和下一步',
    notesStaffIntro: '可为任何合作伙伴记录备注，包括 EVI 的自有合作伙伴。合作伙伴分配给销售代表后，备注仍随合作伙伴保留。',
    notesFindPartner: '查找合作伙伴',
    notesHouseBook: '（EVI 自有业务组合）',
    notesFirstPartners: '前几位合作伙伴',
    notesMorePartners: '更多合作伙伴',
    notesChooseBody: '选择一位合作伙伴以查看或记录联系记录。',
    // My commission and Sales statements
    commPlanRevision: '佣金方案，修订 {n}',
    commOfferRevision: '你的提议，修订 {n}',
    commEffective: '生效期间：自 {date} 起',
    commThrough: '至 {date}',
    commAccepted: '已于 {date} 接受。你已接受的方案仅供查看。',
    commDirectMargin: '硬件直接利润',
    commAboveThreshold: '超出年度门槛部分',
    commPartnerPoints: '合作伙伴会话点数',
    commFleetShare: '车队订阅分成',
    commFleetMeta: '{n} 个月；仅限归属的车队订单',
    commFleetEvery: '在团队期间的每次续订；仅限车队订单',
    commAnnualThreshold: '年度硬件利润门槛（{currency}）',
    commFirstOrder: '首个达标付费订单',
    commFourthCharger: '第四个达标充电桩',
    commBookDuration: '合作伙伴业务组合利润期限',
    commNoMonthLimit: '分配期间无月数限制',
    commMonthsPerPartner: '每位合作伙伴 {n} 个月',
    commContinuation: '良好离职后的延续期',
    commMonths: '{n} 个月',
    commOwnLadder: '自有直接充电桩阶梯',
    commFromCharger: '起始数量（已盖章的达标充电桩）',
    commSessionPoints: '会话点数',
    commLadderNote: '从第四个达标直接充电桩开始计提。前 12 个月为积累期。此后，新上线需要在最近 12 个月内（包括其本身）有 8 个达标上线。未盖章的上线计入活动，但之后不会再获得盖章。现有盖章在你留在团队期间持续产生付款。',
    commBookTiers: '合作伙伴业务组合利润层级',
    commActiveAtLeast: '活跃合作伙伴（至少）',
    commPointsNote: '合作伙伴会话点数在合作伙伴佣金之后适用。员工会话金额以 EVI 的可用余额为上限。车队订阅分成在订阅计费启用后开始；其他订阅不产生分成。',
    commGolden: '已支付的 Golden 奖金：{amount}。',
    commOverrideTiers: '经理团队提成层级',
    commTeamActiveAtLeast: '团队活跃合作伙伴（至少）',
    commGrowth: '经理增长奖金：达到六位活跃合作伙伴时 {six}，下属达到工资发放里程碑时 {payroll}。团队会话点数：{points}。',
    commAccepting: '正在接受...',
    commAccept: '接受此佣金方案',
    commOfferAccepted: '已接受提议。',
    commOfferFailed: '接受提议失败。',
    commStage2: '第 2 阶段信息',
    commStage2Body: '已扣除冲销的已记录佣金，共六个完整月份。这不会让你进入第 2 阶段。超级管理员必须记录已批准的方案修订。',
    commConsecutive: '连续 {n}/{required} 个月',
    commCompletedMonth: '已完成月份',
    commRecordedCommission: '已记录佣金（{currency}）',
    commNoMonths: '暂无已完成的方案月份',
    commNoMonthsBody: '这里只显示自你方案生效日期起已完成的月份。',
    commMonthsAvailable: '已完成月份：{n}/6。',
    commAverage: '平均：{amount}',
    commAgainst: '对比 {amount}',
    commNoCrossCurrency: '；不进行跨币种比较',
    commStatements: '月度对账单',
    commNoStatements: '暂无对账单',
    commNoStatementsBody: '佣金月份结账后会显示你的第一份对账单。',
    commOpening: '期初',
    commReversals: '冲销',
    commPaid: '已支付',
    commClosing: '期末',
    commNoPayments: '暂无付款',
    commNoPaymentsBody: '收入支付后会显示 Connect 付款或工资发放记录。',
    commLedgerAmount: '账本金额',
    commMonth: '月份',
    commStripeTransfer: 'Stripe 转账',
    commNotSent: '未发送',
    commConversionCost: '换汇费用',
    commCashAmount: '付款金额',
    commSentAs: '已按 {amount} 发送',
    commReservedAs: '已按 {amount} 预留',
    commLines: '佣金条目',
    commNoLines: '暂无佣金条目',
    commNoLinesBody: '达标活动会在这里生成佣金条目，之后的更正会单独显示。',
    commCategory: '类别',
    commBasis: '基数',
    commPlainTerms: "如何阅读：基数是你的费率所适用的金额（硬件订单为 EVI 在该订单上的利润）。暂扣表示已赚取但尚未支付：EVI 从该合作伙伴已收取的硬件利润中支付你在该合作伙伴上的收入，最多为其一半，其余部分随该合作伙伴后续订单带来更多利润时支付。第二阶段是 EVI 带底薪的工资计划：满六个月的佣金记录是被考虑的门槛，转入需由 EVI 与你约定新的计划。",
    commRate: '费率',
    commCommission: '佣金',
    commPlanRevisionHeader: '方案修订',
    commTitle: '我的佣金',
    commSubtitle: '你的提议、佣金活动和付款记录。CAD 和 USD 分开记录。',
    commNoOffer: '尚未记录提议',
    commNoOfferBody: 'EVI 会记录一份佣金方案，供你查看并接受。',
    commClosedNote: '已结账的对账单不会更改。更正会以新条目显示。',
    commLoading: '正在加载佣金...',
    commNewestLines: '最新条目',
    commOlderLines: '更早的条目',
    stmtTitle: '销售对账单',
    stmtSubtitle: '查看你销售团队已记录的佣金、对账单、付款和工资发放。',
    stmtNote: '请使用经过审核的脚本来计提和结账月份、向 Connect 承包人付款或导出工资。此页面从不发起收款或转账。',
    stmtChooseBody: '选择一名员工以查看其佣金账本和对账单。',
    stmtReviewQueue: '审核队列',
    stmtNothingReview: '没有待审核的内容',
    stmtNothingReviewBody: '当凭证需要处理时，结算审核项目会显示在这里。',
    stmtSource: '来源',
    stmtCostMissing: '未记录成本',
    stmtCostIntro: '仅限管理员的手动凭证单独存储。原始订单快照不会更改。',
    stmtNoMissing: '没有缺失的成本凭证',
    stmtNoMissingBody: '缺少成本快照的已归属付费订单会显示在这里。',
    stmtCostRecorded: '成本凭证已记录。',
    stmtCostFailed: '无法保存成本凭证。',
    stmtChooseCurrency: '选择币种',
    stmtUnits: '{name}：{n} 件',
    stmtUnitCost: '单位成本',
    stmtAutelFee: '每单位承担的 Autel 费用',
    stmtEvidenceReason: '凭证和原因',
    stmtRecordEvidence: '记录手动凭证',
    stmtCorrection: '追加更正',
    stmtCorrectionIntro: '冲销为负数。恢复金额不能超过原始应得金额。重试时请使用固定的参考编号。',
    stmtNoLinesToCorrect: '没有可更正的条目',
    stmtNoLinesToCorrectBody: '必须有一条正数佣金条目才能追加更正。',
    stmtCorrectionRecorded: '更正已记录。',
    stmtCorrectionFailed: '更正失败。',
    stmtOriginalLine: '原始条目',
    stmtSignedAmount: '带符号金额',
    stmtReference: '参考编号',
    stmtAppend: '追加更正',
    // Sales Staff
    staffTitle: '销售人员',
    staffSubtitle: '管理人员、带日期的佣金方案和合作伙伴业务组合。所有更改都会被审计。',
    staffNote: '只有超级管理员可以更改人员、方案或合作伙伴分配。',
    staffPeople: '人员',
    staffActive: '在职',
    staffContractors: '承包人',
    staffNoStaff: '暂无销售人员',
    staffNoStaffBody: '在下方添加一位拥有现有有效账户的人员。',
    staffKind: '类型',
    staffRole: '角色',
    staffConnect: 'Connect',
    staffEligible: '符合资格',
    staffNotEligible: '暂不符合资格',
    staffEligibility: '{status}：活跃 {active}/{target}，{months}/{required} 个月',
    staffAdd: '添加人员',
    staffCreated: '人员记录已创建。',
    staffAddFailed: '无法添加人员。',
    staffInviteSent: '邀请已发送。对方接受后，将按此角色、经理和开始日期创建员工记录。',
    staffInviteNeedsName: '此邮箱尚无账户，对方将收到邀请。请先填写姓名。',
    staffInvitesTitle: '已邀请，尚未接受',
    staffInvitedOn: '邀请于 {date}',
    staffInviteFailed: '接受时未能创建：{reason}',
    staffResendInvite: '重新发送邀请',
    staffInviteResent: '邀请已重新发送。',
    staffCancelInvite: '取消邀请',
    staffInviteCancelled: '邀请已取消。',
    staffInviteActionFailed: '无法更改该邀请。',
    inviteLinkAction: "复制邀请链接",
    inviteLinkTitle: "复制邀请链接",
    inviteLinkField: "邀请链接",
    inviteLinkLoading: "正在生成新链接...",
    inviteLinkCopy: "复制",
    inviteLinkCopied: "已复制",
    inviteLinkSelected: "链接已选中。按 Ctrl+C（Mac 上按 Cmd+C）即可复制。",
    inviteLinkEmail: "用我的邮箱发送",
    inviteLinkReplaces: "此链接将取代之前邀请邮件中的链接。它只能使用一次，将于 {date} 过期。",
    inviteLinkDiscard: "关闭此窗口后链接将被丢弃，且不会再次显示。",
    inviteLinkExpired: "此邀请已过期。请重新发送以生成新的邀请，然后复制该链接。",
    inviteLinkFailed: "无法生成邀请链接。",
    inviteLinkClose: "关闭",
    inviteLinkMailSubject: "您的 EV Initiative 邀请",
    inviteLinkMailGreeting: "{name}，您好：",
    inviteLinkMailGreetingNoName: "您好：",
    inviteLinkMailBody: "以防之前的邀请邮件没有送达，我用自己的邮箱把您的 EV Initiative 邀请发给您。下面的链接只能使用一次，将于 {date} 过期。",
    inviteLinkMailSignoff: "谢谢，",
    inviteLinkSignInPage: "对方已经有使用此邮箱的 EV Initiative 账户，所以此链接打开的是登录页面。用该账户登录即可接受邀请。",
    inviteLinkMailSignInPage: "以防之前的邀请邮件没有送达，我用自己的邮箱把您的 EV Initiative 邀请发给您。您已经有使用此邮箱的 EV Initiative 账户，请通过下面的链接用该账户登录来接受邀请。",
    staffAccountEmail: '账户邮箱',
    staffStaffRole: '人员角色',
    staffContractor: '承包人',
    staffEmployee: '雇员',
    staffPayThrough: '付款途径',
    staffStatesComma: '州（以逗号分隔）',
    staffCreate: '创建人员记录',
    staffPersonDetails: '人员详情',
    staffChooseBody: '选择一行或使用“人员详情”查看其记录、方案和业务组合。',
    staffRecord: '人员记录',
    staffAccount: '账户',
    staffPartnerBook: '合作伙伴业务组合',
    staffNoPartners: '未分配合作伙伴',
    staffNoPartnersBody: '在下方分配合作伙伴，将其加入此人员的业务组合。',
    staffMoveAll: "移动全部合作伙伴",
    staffMoveAllIntro: "立即将 {name} 当前负责的所有合作伙伴移至另一位 SP Manager 或 EVI 自营业务组合。已上线的充电桩保留其上线时的记录。",
    staffMoveAllTo: "移至",
    staffHouseBook: "EVI 自营业务组合",
    staffMoveAllButton: "移动全部合作伙伴",
    staffMoveAllDone: "已移动 {count} 个合作伙伴。",
    staffDepartureMoves: "离职日期到达时，此人员的合作伙伴将转给其 Sales Manager；如无，则转入 EVI 自营业务组合。",
    staffDeparted: '已于 {date} 离职：{reason}',
    staffUpdated: '人员记录已更新。',
    staffUpdateFailed: '更新失败。',
    staffStates: '州',
    staffEmploymentNote: '雇佣变更请使用“新方案修订”中带日期的转换。充电桩盖章、业务组合分配和活动窗口保留在此人员记录中。',
    staffEndDate: '结束日期（可选）',
    staffDepartureReason: '离职原因',
    staffGoodStanding: '良好离职',
    staffCause: '过失离职',
    staffConductIds: '与行为相关的账本条目 ID（仅限过失离职）',
    staffSave: '保存人员更改',
    staffManagerAssignment: '带日期的经理分配',
    staffManagerRecorded: '带日期的经理分配已记录。',
    staffPlanRevisions: '方案修订',
    staffNoPlans: '没有方案修订',
    staffNoPlansBody: '记录一份带日期的提议，供此人员接受。',
    staffRevision: '修订',
    staffEffectiveFrom: '生效开始',
    staffEffectiveTo: '生效结束',
    staffOngoing: '持续中',
    staffAssignPartner: '分配合作伙伴',
    staffAssignIntro: '记录新的分配及其原因。之前的分配记录保持不变。',
    staffAssignmentRecorded: '分配已记录。',
    staffUnassigned: '未分配',
    staffStandardPlan: '适用于未来销售代表的标准方案',
    staffSiteClaims: '场地归属申请',
    staffClaimsIntro: '现有的非销售场地需要批准。归属申请待审核期间，不授予业务组合访问权限或佣金。',
    staffNoClaims: '没有场地归属申请',
    staffNoClaimsBody: '已登记的销售场地会显示在这里以供审核。',
    staffRegistrant: '登记人',
    staffExisting: '已存在（{kind}）：{id}',
    staffReviewRecorded: '审核已记录。',
    staffReviewFailed: '审核失败。',
    staffDecision: '决定',
    staffReject: '拒绝',
    staffApprove: '批准',
    staffRecordDecision: '记录决定',
    staffReassignRecorded: '重新分配已记录。',
    staffReassign: '重新分配申请',
    staffReassignFailed: '重新分配失败。',
    staffRegistrantKind: '新登记人类型',
    // Plan revision form
    planNoMonthLimit: '无月数限制',
    planStoredStandard: '已保存的标准修订',
    planNew: '新方案修订',
    planPrefilled: '已根据已保存的标准修订（{revision}）预填。已接受的方案和现有充电桩盖章保持不变。',
    planPrefilledCode: '已根据当前标准（修订 {revision}，尚未记录）预填。已接受的方案和现有充电桩盖章保持不变。',
    planLoading: '加载中',
    planLoadFailed: '无法加载已保存的标准。无法记录修订。',
    planStage: '方案阶段',
    planStage1: '第 1 阶段佣金',
    planStage2: '第 2 阶段工资费率',
    planNoRates: '此阶段没有可用的已保存费率。',
    planCentsReadback: '即 {amount}',
    planRecorded: '修订已记录。个人修订需要接受。',
    planFailed: '无法记录修订。',
    planDatedRevision: '带日期的方案或角色修订',
    planEmployeeMove: '带日期的转为雇员并纳入工资发放',
    planRoleOnRevision: '此修订中的角色',
    planNotice: '书面通知编号',
    planRecord: '记录新修订',
  },
  ja: {
    // Shared labels and the status values the screens write themselves
    notRecorded: '記録なし',
    notSet: '未設定',
    noResults: '結果がありません',
    person: 'メンバー',
    status: 'ステータス',
    reason: '理由',
    partner: 'パートナー',
    partners: 'パートナー',
    chooseAPerson: 'メンバーを選択してください',
    chooseAPartner: 'パートナーを選択してください',
    nextStep: '次のステップ',
    ledgerMonth: '台帳の月',
    allMonths: 'すべての月',
    manager: 'マネージャー',
    noManager: 'マネージャーなし',
    rep: '営業担当',
    roleManager: 'マネージャー（自身の営業担当ポートフォリオあり）',
    territory: '担当地域',
    startDate: '開始日',
    effectiveDate: '発効日',
    action: '操作',
    period: '期間',
    currency: '通貨',
    location: 'サイト',
    name: '名前',
    email: 'メールアドレス',
    paymentMethod: '支払方法',
    stripeConnect: 'Stripe Connect',
    payroll: '給与',
    payrollEligibility: '給与支払いの資格',
    activePartners: 'アクティブなパートナー',
    marginShare: 'マージン配分',
    heldUnderCap: '上限による保留',
    earned: '獲得額',
    paymentsAndPayroll: '支払いと給与',
    salesStaff: '営業スタッフ',
    salesPartner: 'セールスパートナー',
    recording: '記録しています...',
    recordAssignment: '割り当てを記録',
    assignmentFailed: '割り当てに失敗しました。',
    pointOne: '{n}ポイント',
    pointOther: '{n}ポイント',
    statusActive: 'アクティブ',
    statusInactive: '非アクティブ',
    statusQualified: '適格',
    statusNotQualified: '非適格',
    statusAccepted: '承諾済み',
    statusAwaitingAcceptance: '承諾待ち',
    statusAssigned: '割り当て済み',
    statusOffline: 'オフライン',
    statusNotStarted: '未開始',
    statusPending: '保留中',
    statusNoSubmission60: '60日間申請なし',
    statusBelowPace: '6か月のペースを下回っています',
    statusOrderFollowUp: '注文のフォローアップが必要',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: '営業チーム',
    teamSubtitle: 'あなたのネットワーク、チームの活動、記録された収益。',
    teamOpenPerson: 'メンバーのチームを開く',
    teamLoading: 'チームを読み込んでいます',
    teamChoose: 'チームを選択してください',
    teamEmptyBody: 'アクセスが許可されたチームとポートフォリオのデータがここに表示されます。',
    tabMap: 'マップ',
    tabTeam: 'チーム',
    tabReports: 'レポート',
    tabScoreboard: 'スコアボード',
    teamMyBook: '自分のポートフォリオ',
    teamWholeTeam: 'チーム全体',
    teamNetworkMap: 'ネットワークマップ',
    teamEveryone: '対象範囲の全員',
    teamStage: 'ステージ',
    teamAllStages: 'すべてのステージ',
    teamChargerType: '充電器の種類',
    teamAllTypes: 'すべての種類',
    teamState: '州',
    teamAllStates: 'すべての州',
    teamLaunchedFrom: '稼働開始（この日から）',
    teamLaunchedThrough: '稼働開始（この日まで）',
    teamPinLegend: 'ピンの色はメンバーを表します。塗りつぶしの円はオンライン、空の円はオフライン、感嘆符は障害を示します。地図座標のないサイト数：{n}。',
    teamCloseDetails: 'サイトの詳細を閉じる',
    teamCity: '都市',
    teamChargers: '充電器',
    teamDirectDeal: '直接取引',
    teamLaunchDate: '稼働開始日',
    teamNotLaunched: '未稼働',
    teamSessions30: 'セッション数（過去30日）',
    teamKwh30: 'kWh（過去30日）',
    teamNoSites: '一致するサイトはありません',
    teamNoSitesBody: 'フィルターを変更すると、ポートフォリオ内の他のサイトを表示できます。',
    teamSite: 'サイト',
    teamDirectReports: '直属の部下',
    teamNoDirectReports: '直属の部下はいません',
    teamNoDirectReportsBody: '日付付きの割り当てが記録されると、ここに表示されます。',
    teamActivity: '活動',
    teamEarnedMonth: '今月の獲得額',
    teamActivatedMonth: '今月のアクティベーション',
    teamLaunchedMonth: '今月の稼働開始',
    teamPace: 'ペース',
    teamOnPace: 'ペースどおり',
    teamEligibility: '{months}/{required}か月間、{active}/{target}がアクティブ。',
    teamUsdAverage: '6か月のUSD平均：{amount}',
    teamNotAvailable: '利用できません',
    teamScoreboard: 'チームのスコアボード',
    teamThisWeek: '今週',
    teamThisMonth: '今月',
    teamScoreNote: '件数のみです。対象となる稼働開始数、次にアクティブなパートナー数とアクティベーション数で順位を付けます。リード、デモ、申込は監査済みの活動記録を使用します。通話と会話はパートナーメモを使用します。推定値は加えません。',
    teamOutreach: 'アウトリーチ',
    teamLaunches: '稼働開始',
    scoreLeadsStarted: 'リード開始',
    scoreCalls: '通話',
    scoreConversations: '会話',
    scoreDemos: 'デモ',
    scoreApplications: '申込',
    scoreApproved: '承認済み',
    scoreFirstPaidOrders: '初回有料注文',
    scoreActivated: 'アクティベーション',
    scoreActivePartners: 'アクティブなパートナー',
    scoreQualifyingLaunches: '対象となる稼働開始',
    reportMonthly: '月別・カテゴリー別のコミッション',
    reportSessions: '充電器別のセッションポイント',
    reportYearEnd: '年末合計',
    reportsTitle: 'マイレポート',
    reportYear: '年',
    reportKind: 'レポート',
    reportDownloadFailed: 'レポートをダウンロードできませんでした。',
    reportExport: 'CSVをエクスポート',
    reportEmpty: '記録された活動はありません',
    reportEmptyBody: '記録された収益と支払いの証跡がここに表示されます。',
    reportNote: '台帳の通貨は別々に管理されます。年末合計は申告の準備に役立つものであり、税務書類ではありません。',
    activityRecorded: '活動を記録しました。',
    activityFailed: '活動を記録できませんでした。',
    activityLabel: '自分の活動を記録',
    activityLead: '新規リードの開始',
    activityDemo: 'デモ',
    activityApplication: '申込',
    activityEvidence: '証跡の参照番号',
    activityPlaceholder: '活動の参照番号',
    activityRecord: '活動を記録',
    // Sales payout run
    payTitle: '支払いの実行',
    paySubtitle: '締め済みの明細書を確認してから、支払いを確定してください。自動支払いは承認されるまでオフのままです。',
    payCycle: '月次サイクル',
    payCycleBody: '明細書は1日に締められます。確認は1日から4日に行います。支払いは5日または翌営業日です。通貨ごとに個別に支払われます。準備ができていないアカウントと失敗した送金は、引き続き支払い対象です。',
    payStatementMonth: '明細書の月',
    payClose: '明細書を締める',
    payPay: '支払いと給与のエクスポート',
    paySync: '収益を同期',
    paySynced: '収益と取り消しを同期しました。',
    payPreview: 'プレビューのみ',
    payRequestFailed: 'リクエストに失敗しました。',
    payNewLines: '新しい台帳明細：{n}件。明細書の確認が必要です。',
    payDate: '支払日：{date}。',
    payMayProceed: '支払いを進めることができます。',
    payWait: '支払いはその日まで待機します。',
    payNothing: '処理するものはありません',
    payNothingBody: 'この月に利用できる明細書または支払いの残高はありません。',
    payLedgerHeld: '台帳と保留',
    payPayment: '支払い',
    payReview: '確認',
    payHeld: '保留：{amount}',
    payConversion: '両替費用：{amount}',
    payReady: '準備完了',
    payPrepareQuote: '両替の見積りを準備',
    payQuotePrepared: '見積りを準備しました。新しいプレビューを実行してください。',
    payConfirmCheck: 'これらの金額、通貨、ブロックされた受取人を確認しました。',
    payConfirmClose: '明細書の締めを確定',
    payConfirmPay: '支払いの実行を確定',
    payRunRecorded: '実行を記録しました：{result}',
    payPayroll: '給与のエクスポートと照合',
    payDownload: '記録済みの給与CSVをダウンロード',
    payPaymentId: '支払いIDまたはエクスポートID',
    payProviderRef: 'プロバイダーの参照番号',
    payRecordPayroll: '完了した給与を記録',
    payPayrollRecorded: '給与の支払いを記録しました。',
    payActualCost: '実際の両替費用',
    payActualBody: 'プロバイダーの最終的な両替費用を台帳の通貨で記録してください。見積りとの差額は、現在未締めの明細書で調整として計上されます。',
    payLedgerCurrency: '台帳の通貨',
    payActualCents: '実際の費用（セント）',
    payRecordActual: '実際の費用を記録',
    payActualReconciled: '実際の両替費用を照合しました。',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'ポートフォリオ内のパートナーの活動、サイト、次のステップ。',
    bookLoadingPartners: 'パートナーを読み込んでいます...',
    bookQualifyingChargers: '対象となる充電器',
    bookLaunches12: '12か月間の稼働開始',
    bookActivityTarget: '活動目標：8',
    bookLaunches6: '6か月間の稼働開始',
    bookPaceTarget: 'ペース目標：4',
    bookLocationsSubmissions: 'サイト / 申請',
    bookLastContact: '最終連絡',
    bookLocationsHeading: 'サイトと申請',
    bookNoLocations: 'まだサイトはありません',
    bookNoLocationsBody: 'このパートナーが申請したサイトがここに表示されます。',
    bookNotes: 'メモと次のステップ',
    bookNoPartners: 'ポートフォリオにパートナーはいません',
    bookNoPartnersBody: '割り当てられたパートナーとそのサイトがここに表示されます。',
    bookLoadingSubmission: '申請を読み込んでいます...',
    bookLocationType: 'サイトの種類',
    bookAddress: '住所',
    bookContact: '連絡先',
    bookConnectivity: '通信環境',
    bookCommissioning: '試運転の承認',
    bookAwaitingReview: 'EVIの審査待ち',
    bookInstallerExpires: '設置業者のアクセス期限',
    bookNotIssued: '未発行',
    bookLaunch: '稼働開始',
    bookAwaitingApproval: 'EVIの承認待ち',
    bookOrdersTitle: '注文',
    bookOrdersSubtitle: 'あなたとポートフォリオ内のパートナーに帰属する顧客の注文。',
    bookLoadingOrders: '注文を読み込んでいます...',
    bookOrderDate: '注文日',
    bookDirectStaffOrder: 'スタッフの直接注文',
    bookOrderTotal: '注文合計',
    bookQty: '数量 {n}',
    bookRegisterDeal: 'この直接取引を登録',
    bookClaimRecorded: 'サイトの担当申請を記録しました。既存の営業以外のサイトには、スーパー管理者の審査が必要です。',
    bookClaimFailed: 'サイトの担当申請を記録できませんでした。',
    bookDealEvidence: '直接取引の証跡',
    bookRegisterButton: '直接取引を登録',
    bookNoOrders: 'まだ注文はありません',
    bookNoOrdersBody: '直接取引とパートナーのポートフォリオからの注文がここに表示されます。',
    bookNewestOrders: '最新の注文',
    bookOlderOrders: '以前の注文',
    bookOnboardingTitle: 'オンボーディングのサポート',
    bookOnboardingSubtitle: 'ポートフォリオ内のサイトの方々が必要なアクセスを得られるよう支援します。',
    bookInvite: 'ユーザーを招待',
    bookInviteBody: '招待を送信または再送信します。配信は現在のメールポリシーに従います。',
    bookNoInviteLocations: '招待できるサイトはまだありません',
    bookNoInviteLocationsBody: 'ユーザーを招待するには、そのサイトがポートフォリオに含まれている必要があります。',
    bookInviteRecorded: '招待を記録しました。配信は現在のメールポリシーに従います。',
    bookInviteFailed: '招待を送信できませんでした。',
    bookChooseLocation: 'サイトを選択してください',
    bookAccess: 'アクセス権',
    roleBuildingAdmin: '建物管理者',
    rolePropertyManager: 'プロパティマネージャー',
    roleDriver: 'ドライバー',
    bookInviting: '招待を記録しています...',
    bookSendInvite: '招待を送信または再送信',
    // Partner and direct site notes
    notesHeading: '通話、訪問、メール',
    notesIntro: '各接触はその日のうちに記録してください。メモはEVIに帰属します。訂正するとバージョンが追加され、元の内容は保持されます。',
    notesEmpty: 'まだメモはありません',
    notesEmptyBody: '最初の連絡と明確な次のステップを下に記録してください。',
    notesVersion: 'バージョン {n}',
    notesDue: '期限',
    notesAddCorrection: '訂正版を追加',
    notesLinksFailed: 'リンクされたサイトと申請を読み込めませんでした。',
    notesRecorded: 'メモを記録しました。',
    notesFailed: 'メモを記録できませんでした。',
    notesChannel: 'チャネル',
    notesCall: '通話',
    notesVisit: '訪問',
    notesEmailChannel: 'メール',
    notesProjectStage: 'プロジェクトのステージ',
    notesNote: 'メモ',
    notesNextDue: '次のステップの期限',
    notesLocationOptional: 'サイト（任意）',
    notesNoLocation: 'リンクされたサイトなし',
    notesSubmissionOptional: '申請（任意）',
    notesNoSubmission: 'リンクされた申請なし',
    notesRecordVersion: '新しいバージョンを記録',
    notesRecordNote: 'メモを記録',
    notesCancel: '訂正をキャンセル',
    notesSiteTitle: '直接サイトのメモ',
    notesNoClaims: '直接サイトの担当申請はありません',
    notesNoClaimsBody: '直接サイトの担当申請とその連絡履歴がここに表示されます。',
    notesStaffTitle: 'パートナーのメモと次のステップ',
    notesStaffIntro: 'EVIの社内パートナーを含め、どのパートナーについてもメモを記録できます。メモは、パートナーが営業担当に割り当てられた後もそのパートナーに残ります。',
    notesFindPartner: 'パートナーを検索',
    notesHouseBook: '（EVI社内ポートフォリオ）',
    notesFirstPartners: '最初のパートナーを表示',
    notesMorePartners: 'さらにパートナーを表示',
    notesChooseBody: 'パートナーを選択すると、連絡履歴を閲覧または記録できます。',
    // My commission and Sales statements
    commPlanRevision: 'コミッションプラン（改訂 {n}）',
    commOfferRevision: 'あなたへのオファー（改訂 {n}）',
    commEffective: '有効期間：{date}から',
    commThrough: '{date}まで',
    commAccepted: '{date}に承諾済み。承諾したプランは読み取り専用で表示されます。',
    commDirectMargin: 'ハードウェアの直接マージン',
    commAboveThreshold: '年間しきい値の超過分',
    commPartnerPoints: 'パートナーのセッションポイント',
    commFleetShare: 'フリートサブスクリプションの配分',
    commFleetMeta: '{n}か月、帰属するフリート注文のみ',
    commFleetEvery: 'チーム在籍中はすべての更新、フリート注文のみ',
    commAnnualThreshold: 'ハードウェアマージンの年間しきい値（{currency}）',
    commFirstOrder: '対象となる最初の有料注文',
    commFourthCharger: '対象となる4台目の充電器',
    commBookDuration: 'パートナーポートフォリオのマージン期間',
    commNoMonthLimit: '割り当て中は月数の上限なし',
    commMonthsPerPartner: 'パートナーごとに{n}か月',
    commContinuation: '円満退職後の継続期間',
    commMonths: '{n}か月',
    commOwnLadder: '自分の直接充電器の段階表',
    commFromCharger: '開始台数（対象のスタンプ付き充電器）',
    commSessionPoints: 'セッションポイント',
    commLadderNote: '対象となる4台目の直接充電器から計上が始まります。最初の12か月は立ち上げ期間です。その後、新しい稼働開始には、それ自体を含めて過去12か月に8件の対象稼働開始が必要です。スタンプなしの稼働開始は活動としてカウントされますが、後からスタンプが付くことはありません。既存のスタンプは、あなたがチームに在籍している間、支払いが続きます。',
    commBookTiers: 'パートナーポートフォリオのマージン段階',
    commActiveAtLeast: 'アクティブなパートナー数（以上）',
    commPointsNote: 'パートナーのセッションポイントは、パートナーのコミッションの後に適用されます。スタッフのセッション額は、EVIの利用可能な残額が上限です。フリートサブスクリプションの配分は、サブスクリプションの請求が有効になると始まります。その他のサブスクリプションは配分の対象になりません。',
    commGolden: '支払い済みのGoldenボーナス：{amount}。',
    commOverrideTiers: 'マネージャーのオーバーライド段階',
    commTeamActiveAtLeast: 'チームのアクティブなパートナー数（以上）',
    commGrowth: 'マネージャーの成長ボーナス：アクティブなパートナー6人で{six}、部下の給与マイルストーン達成で{payroll}。チームのセッションポイント：{points}。',
    commAccepting: '承諾しています...',
    commAccept: 'このコミッションプランを承諾',
    commOfferAccepted: 'オファーを承諾しました。',
    commOfferFailed: 'オファーを承諾できませんでした。',
    commStage2: 'ステージ2の情報',
    commStage2Body: '取り消し後の記録済みコミッション（完了した6か月分）です。これによってステージ2に移行するわけではありません。スーパー管理者が承認済みのプラン改訂を記録する必要があります。',
    commConsecutive: '連続{n}/{required}か月',
    commCompletedMonth: '完了した月',
    commRecordedCommission: '記録済みコミッション（{currency}）',
    commNoMonths: '完了したプランの月はまだありません',
    commNoMonthsBody: 'プランの発効日以降に完了した月のみがここに表示されます。',
    commMonthsAvailable: '完了した月：6か月中{n}か月。',
    commAverage: '平均：{amount}',
    commAgainst: '対 {amount}',
    commNoCrossCurrency: '、通貨間の比較なし',
    commStatements: '月次明細書',
    commNoStatements: 'まだ明細書はありません',
    commNoStatementsBody: 'コミッションの月が締められると、最初の明細書が表示されます。',
    commOpening: '期首',
    commReversals: '取り消し',
    commPaid: '支払済み',
    commClosing: '期末',
    commNoPayments: 'まだ支払いはありません',
    commNoPaymentsBody: '収益が支払われると、Connectの支払いまたは給与の記録が表示されます。',
    commLedgerAmount: '台帳金額',
    commMonth: '月',
    commStripeTransfer: 'Stripe送金',
    commNotSent: '未送信',
    commConversionCost: '両替費用',
    commCashAmount: '支払額',
    commSentAs: '{amount}として送金済み',
    commReservedAs: '{amount}として予約済み',
    commLines: 'コミッション明細',
    commNoLines: 'まだコミッション明細はありません',
    commNoLinesBody: '対象となる活動によってここにコミッション明細が作成され、後の訂正は別に表示されます。',
    commCategory: 'カテゴリー',
    commBasis: '基準額',
    commPlainTerms: "見方：基準額はあなたの料率が適用される金額です（ハードウェア注文では EVI の利益）。保留は獲得済みでまだ支払われていない額です。EVI はパートナーから回収したハードウェア利益の半分までを上限にあなたの収入を支払い、残りはそのパートナーの次の注文で利益が入るにつれて支払われます。ステージ 2 は基本給のある EVI の給与制プランです。手数料の記録が丸 6 か月あることが検討の条件で、移行は EVI とあなたが合意する新しいプランです。",
    commRate: '料率',
    commCommission: 'コミッション',
    commPlanRevisionHeader: 'プランの改訂',
    commTitle: 'マイコミッション',
    commSubtitle: 'あなたへのオファー、コミッションの活動、支払い記録。CADとUSDは別々に管理されます。',
    commNoOffer: 'まだオファーは記録されていません',
    commNoOfferBody: 'EVIがコミッションプランを記録し、あなたが確認して承諾できるようにします。',
    commClosedNote: '締め済みの明細書は変更されません。訂正は新しい明細として表示されます。',
    commLoading: 'コミッションを読み込んでいます...',
    commNewestLines: '最新の明細',
    commOlderLines: '以前の明細',
    stmtTitle: '営業明細書',
    stmtSubtitle: '営業チームの記録済みコミッション、明細書、支払い、給与を確認します。',
    stmtNote: '月の計上と締め、Connect契約者への支払い、給与のエクスポートには、レビュー済みのスクリプトを使用してください。このページから回収や送金が開始されることはありません。',
    stmtChooseBody: 'スタッフを選択すると、そのコミッション台帳と明細書を確認できます。',
    stmtReviewQueue: '確認キュー',
    stmtNothingReview: '確認待ちの項目はありません',
    stmtNothingReviewBody: '証跡に対応が必要になると、精算の確認項目がここに表示されます。',
    stmtSource: 'ソース',
    stmtCostMissing: '未記録の原価',
    stmtCostIntro: '管理者専用の手動証跡は別に保存されます。元の注文スナップショットは変更されません。',
    stmtNoMissing: '不足している原価証跡はありません',
    stmtNoMissingBody: '原価スナップショットのない帰属済みの有料注文がここに表示されます。',
    stmtCostRecorded: '原価の証跡を記録しました。',
    stmtCostFailed: '原価の証跡を保存できませんでした。',
    stmtChooseCurrency: '通貨を選択してください',
    stmtUnits: '{name}：{n}点',
    stmtUnitCost: '単位原価',
    stmtAutelFee: '単位あたりの負担済みAutel手数料',
    stmtEvidenceReason: '証跡と理由',
    stmtRecordEvidence: '手動の証跡を記録',
    stmtCorrection: '訂正を追加',
    stmtCorrectionIntro: '取り消しはマイナスで入力します。復元は元の受給権を超えることはできません。再試行には変わらない参照番号を使用してください。',
    stmtNoLinesToCorrect: '訂正できる明細はありません',
    stmtNoLinesToCorrectBody: '訂正を追加するには、プラスのコミッション明細が必要です。',
    stmtCorrectionRecorded: '訂正を記録しました。',
    stmtCorrectionFailed: '訂正に失敗しました。',
    stmtOriginalLine: '元の明細',
    stmtSignedAmount: '符号付き金額',
    stmtReference: '参照番号',
    stmtAppend: '訂正を追加',
    // Sales Staff
    staffTitle: '営業スタッフ',
    staffSubtitle: 'メンバー、日付付きのコミッションプラン、パートナーのポートフォリオを管理します。変更は監査されます。',
    staffNote: 'スタッフ、プラン、パートナーの割り当てを変更できるのはスーパー管理者のみです。',
    staffPeople: 'メンバー',
    staffActive: 'アクティブ',
    staffContractors: '業務委託者',
    staffNoStaff: 'まだ営業スタッフはいません',
    staffNoStaffBody: '既存の有効なアカウントを持つメンバーを下で追加してください。',
    staffKind: '種別',
    staffRole: '役割',
    staffConnect: 'Connect',
    staffEligible: '対象',
    staffNotEligible: 'まだ対象外',
    staffEligibility: '{status}：アクティブ {active}/{target}、{months}/{required}か月',
    staffAdd: 'メンバーを追加',
    staffCreated: 'スタッフ記録を作成しました。',
    staffAddFailed: 'メンバーを追加できませんでした。',
    staffInviteSent: '招待を送信しました。相手が承諾すると、このロール、マネージャー、開始日でスタッフ記録が作成されます。',
    staffInviteNeedsName: 'このメールアドレスにはまだアカウントがないため、招待が届きます。先に名前を入力してください。',
    staffInvitesTitle: '招待済み、未承諾',
    staffInvitedOn: '{date} に招待',
    staffInviteFailed: '承諾時に作成できませんでした: {reason}',
    staffResendInvite: '招待を再送',
    staffInviteResent: '招待を再送しました。',
    staffCancelInvite: '招待を取り消す',
    staffInviteCancelled: '招待を取り消しました。',
    staffInviteActionFailed: '招待を変更できませんでした。',
    inviteLinkAction: "招待リンクをコピー",
    inviteLinkTitle: "招待リンクをコピー",
    inviteLinkField: "招待リンク",
    inviteLinkLoading: "新しいリンクを作成しています...",
    inviteLinkCopy: "コピー",
    inviteLinkCopied: "コピーしました",
    inviteLinkSelected: "リンクを選択しました。Ctrl+C（Mac では Cmd+C）でコピーしてください。",
    inviteLinkEmail: "自分のメールで送る",
    inviteLinkReplaces: "このリンクは以前の招待メールのリンクに代わるものです。使えるのは1回だけで、{date} に期限切れになります。",
    inviteLinkDiscard: "このウィンドウを閉じるとリンクは破棄され、再表示されません。",
    inviteLinkExpired: "この招待は期限切れです。再送して新しい招待を作成してから、そのリンクをコピーしてください。",
    inviteLinkFailed: "招待リンクを作成できませんでした。",
    inviteLinkClose: "閉じる",
    inviteLinkMailSubject: "EV Initiative へのご招待",
    inviteLinkMailGreeting: "{name} 様",
    inviteLinkMailGreetingNoName: "こんにちは。",
    inviteLinkMailBody: "前回の招待メールが届いていない場合に備えて、私のメールから EV Initiative への招待をお送りします。下のリンクは1回だけ使え、{date} に期限切れになります。",
    inviteLinkMailSignoff: "よろしくお願いします。",
    inviteLinkSignInPage: "このメールアドレスの EV Initiative アカウントがすでにあるため、このリンクはサインインページを開きます。そのアカウントでサインインすると招待が承認されます。",
    inviteLinkMailSignInPage: "前回の招待メールが届いていない場合に備えて、私のメールから EV Initiative への招待をお送りします。このメールアドレスの EV Initiative アカウントをすでにお持ちなので、下のリンクからそのアカウントでサインインして招待を承認してください。",
    staffAccountEmail: 'アカウントのメールアドレス',
    staffStaffRole: 'スタッフの役割',
    staffContractor: '業務委託者',
    staffEmployee: '従業員',
    staffPayThrough: '支払経路',
    staffStatesComma: '州（カンマ区切り）',
    staffCreate: 'スタッフ記録を作成',
    staffPersonDetails: 'メンバーの詳細',
    staffChooseBody: '行を選択するか「メンバーの詳細」を使用して、記録、プラン、ポートフォリオを確認してください。',
    staffRecord: 'スタッフ記録',
    staffAccount: 'アカウント',
    staffPartnerBook: 'パートナーのポートフォリオ',
    staffNoPartners: '割り当てられたパートナーはいません',
    staffNoPartnersBody: '下でパートナーを割り当てると、このメンバーのポートフォリオに追加されます。',
    staffMoveAll: "すべてのパートナーを移動",
    staffMoveAllIntro: "{name} が現在担当しているすべてのパートナーを、今から別の SP Manager または EVI 直轄ポートフォリオに移します。稼働済みの充電器は稼働時の記録を保持します。",
    staffMoveAllTo: "移動先",
    staffHouseBook: "EVI 直轄ポートフォリオ",
    staffMoveAllButton: "すべてのパートナーを移動",
    staffMoveAllDone: "{count} 件のパートナーを移動しました。",
    staffDepartureMoves: "退職日になると、このメンバーのパートナーは担当の Sales Manager に、いない場合は EVI 直轄ポートフォリオに移ります。",
    staffDeparted: '{date}に退職：{reason}',
    staffUpdated: 'スタッフ記録を更新しました。',
    staffUpdateFailed: '更新に失敗しました。',
    staffStates: '州',
    staffEmploymentNote: '雇用形態の変更には、「新しいプラン改訂」の日付付き移行を使用してください。充電器のスタンプ、ポートフォリオの割り当て、活動期間はこのスタッフ記録に残ります。',
    staffEndDate: '終了日（任意）',
    staffDepartureReason: '退職理由',
    staffGoodStanding: '円満退職',
    staffCause: '懲戒事由',
    staffConductIds: '行為に関連する台帳明細ID（懲戒事由の場合のみ）',
    staffSave: 'スタッフの変更を保存',
    staffManagerAssignment: '日付付きのマネージャー割り当て',
    staffManagerRecorded: '日付付きのマネージャー割り当てを記録しました。',
    staffPlanRevisions: 'プランの改訂',
    staffNoPlans: 'プランの改訂はありません',
    staffNoPlansBody: 'このメンバーが承諾する日付付きのオファーを記録してください。',
    staffRevision: '改訂',
    staffEffectiveFrom: '有効開始日',
    staffEffectiveTo: '有効終了日',
    staffOngoing: '継続中',
    staffAssignPartner: 'パートナーを割り当て',
    staffAssignIntro: '新しい割り当てを理由とともに記録してください。以前の割り当て履歴はそのまま残ります。',
    staffAssignmentRecorded: '割り当てを記録しました。',
    staffUnassigned: '未割り当て',
    staffStandardPlan: '今後の営業担当向けの標準プラン',
    staffSiteClaims: 'サイトの担当申請',
    staffClaimsIntro: '既存の営業以外のサイトには承認が必要です。申請が審査待ちの間は、ポートフォリオへのアクセスもコミッションも付与されません。',
    staffNoClaims: 'サイトの担当申請はありません',
    staffNoClaimsBody: '登録された営業サイトが審査のためにここに表示されます。',
    staffRegistrant: '登録者',
    staffExisting: '既存（{kind}）：{id}',
    staffReviewRecorded: '審査を記録しました。',
    staffReviewFailed: '審査に失敗しました。',
    staffDecision: '判断',
    staffReject: '却下',
    staffApprove: '承認',
    staffRecordDecision: '判断を記録',
    staffReassignRecorded: '再割り当てを記録しました。',
    staffReassign: '申請を再割り当て',
    staffReassignFailed: '再割り当てに失敗しました。',
    staffRegistrantKind: '新しい登録者の種別',
    // Plan revision form
    planNoMonthLimit: '月数の上限なし',
    planStoredStandard: '保存済みの標準改訂',
    planNew: '新しいプラン改訂',
    planPrefilled: '保存済みの標準改訂（{revision}）から入力されています。承諾済みのプランと既存の充電器スタンプは変更されません。',
    planPrefilledCode: '現行の標準（改訂{revision}、未記録）から入力されています。承諾済みのプランと既存の充電器スタンプは変更されません。',
    planLoading: '読み込み中',
    planLoadFailed: '保存済みの標準を読み込めませんでした。改訂を記録できません。',
    planStage: 'プランのステージ',
    planStage1: 'ステージ1のコミッション',
    planStage2: 'ステージ2の給与レート',
    planNoRates: 'このステージで利用できる保存済みレートはありません。',
    planCentsReadback: '{amount} に相当',
    planRecorded: '改訂を記録しました。個人向けの改訂には承諾が必要です。',
    planFailed: '改訂を記録できませんでした。',
    planDatedRevision: '日付付きのプランまたは役割の改訂',
    planEmployeeMove: '日付付きの従業員および給与への移行',
    planRoleOnRevision: 'この改訂での役割',
    planNotice: '書面通知の参照番号',
    planRecord: '新しい改訂を記録',
  },
  ru: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Не записано',
    notSet: 'Не задано',
    noResults: 'Нет результатов',
    person: 'Сотрудник',
    status: 'Статус',
    reason: 'Причина',
    partner: 'Партнёр',
    partners: 'Партнёры',
    chooseAPerson: 'Выберите сотрудника',
    chooseAPartner: 'Выберите партнёра',
    nextStep: 'Следующий шаг',
    ledgerMonth: 'Месяц реестра',
    allMonths: 'Все месяцы',
    manager: 'Руководитель',
    noManager: 'Без руководителя',
    rep: 'Представитель',
    roleManager: 'Руководитель со своим портфелем представителя',
    territory: 'Территория',
    startDate: 'Дата начала',
    effectiveDate: 'Дата вступления в силу',
    action: 'Действие',
    period: 'Период',
    currency: 'Валюта',
    location: 'Объект',
    name: 'Имя',
    email: 'Эл. почта',
    paymentMethod: 'Способ выплаты',
    stripeConnect: 'Stripe Connect',
    payroll: 'Зарплатная ведомость',
    payrollEligibility: 'Допуск к зарплатной ведомости',
    activePartners: 'Активные партнёры',
    marginShare: 'Доля маржи',
    heldUnderCap: 'Удержано по лимиту',
    earned: 'Заработано',
    paymentsAndPayroll: 'Выплаты и зарплатная ведомость',
    salesStaff: 'Сотрудник отдела продаж',
    salesPartner: 'Торговый партнёр',
    recording: 'Запись...',
    recordAssignment: 'Записать назначение',
    assignmentFailed: 'Не удалось выполнить назначение.',
    pointOne: '{n} п. п.',
    pointOther: '{n} п. п.',
    statusActive: 'активен',
    statusInactive: 'неактивен',
    statusQualified: 'квалифицирован',
    statusNotQualified: 'не квалифицирован',
    statusAccepted: 'принято',
    statusAwaitingAcceptance: 'ожидает принятия',
    statusAssigned: 'назначен',
    statusOffline: 'не в сети',
    statusNotStarted: 'не начато',
    statusPending: 'в ожидании',
    statusNoSubmission60: 'Нет заявок за 60 дней',
    statusBelowPace: 'Ниже темпа за 6 месяцев',
    statusOrderFollowUp: 'Нужен контроль заказа',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Отдел продаж',
    teamSubtitle: 'Ваша сеть, активность команды и записанные доходы.',
    teamOpenPerson: 'Открыть команду сотрудника',
    teamLoading: 'Загрузка вашей команды',
    teamChoose: 'Выберите команду',
    teamEmptyBody: 'Здесь появляются доступные вам данные команды и портфеля.',
    tabMap: 'Карта',
    tabTeam: 'Команда',
    tabReports: 'Отчёты',
    tabScoreboard: 'Рейтинг',
    teamMyBook: 'Мой портфель',
    teamWholeTeam: 'Вся команда',
    teamNetworkMap: 'Карта сети',
    teamEveryone: 'Все в вашей зоне доступа',
    teamStage: 'Этап',
    teamAllStages: 'Все этапы',
    teamChargerType: 'Тип зарядной станции',
    teamAllTypes: 'Все типы',
    teamState: 'Штат',
    teamAllStates: 'Все штаты',
    teamLaunchedFrom: 'Запуск с',
    teamLaunchedThrough: 'Запуск по',
    teamPinLegend: 'Цвет метки обозначает сотрудника. Закрашенный круг означает «в сети», пустой круг означает «не в сети», а восклицательный знак означает неисправность. Площадок без координат на карте: {n}.',
    teamCloseDetails: 'Закрыть сведения о площадке',
    teamCity: 'Город',
    teamChargers: 'Зарядные станции',
    teamDirectDeal: 'Прямая сделка',
    teamLaunchDate: 'Дата запуска',
    teamNotLaunched: 'Не запущена',
    teamSessions30: 'Сессии, последние 30 дней',
    teamKwh30: 'кВт·ч, последние 30 дней',
    teamNoSites: 'Нет подходящих площадок',
    teamNoSitesBody: 'Измените фильтры, чтобы увидеть другие площадки вашего портфеля.',
    teamSite: 'Площадка',
    teamDirectReports: 'Прямые подчинённые',
    teamNoDirectReports: 'Нет прямых подчинённых',
    teamNoDirectReportsBody: 'Назначения с датами появятся здесь после записи.',
    teamActivity: 'Активность',
    teamEarnedMonth: 'Заработано в этом месяце',
    teamActivatedMonth: 'Активировано в этом месяце',
    teamLaunchedMonth: 'Запущено в этом месяце',
    teamPace: 'Темп',
    teamOnPace: 'В темпе',
    teamEligibility: 'Активных: {active}/{target}, месяцев: {months}/{required}.',
    teamUsdAverage: 'Среднее за шесть месяцев в USD: {amount}',
    teamNotAvailable: 'Недоступно',
    teamScoreboard: 'Рейтинг команды',
    teamThisWeek: 'Эта неделя',
    teamThisMonth: 'Этот месяц',
    teamScoreNote: 'Только количество. Рейтинг строится по квалифицирующим запускам, затем по активным партнёрам и активациям. Лиды, демонстрации и заявки берутся из ваших проверенных записей об активности. Звонки и разговоры берутся из заметок о партнёрах. Оценки не добавляются.',
    teamOutreach: 'Привлечение',
    teamLaunches: 'Запуски',
    scoreLeadsStarted: 'начатые лиды',
    scoreCalls: 'звонки',
    scoreConversations: 'разговоры',
    scoreDemos: 'демонстрации',
    scoreApplications: 'заявки',
    scoreApproved: 'одобрено',
    scoreFirstPaidOrders: 'первые оплаченные заказы',
    scoreActivated: 'активировано',
    scoreActivePartners: 'активные партнёры',
    scoreQualifyingLaunches: 'квалифицирующие запуски',
    reportMonthly: 'Комиссия по месяцам и категориям',
    reportSessions: 'Пункты сессий по зарядным станциям',
    reportYearEnd: 'Итоги года',
    reportsTitle: 'Мои отчёты',
    reportYear: 'Год',
    reportKind: 'Отчёт',
    reportDownloadFailed: 'Не удалось скачать отчёт.',
    reportExport: 'Экспорт в CSV',
    reportEmpty: 'Нет записанной активности',
    reportEmptyBody: 'Здесь появятся записанные доходы и подтверждения выплат.',
    reportNote: 'Валюты реестра учитываются раздельно. Итоги года помогают подготовить отчётность и не являются налоговой формой.',
    activityRecorded: 'Активность записана.',
    activityFailed: 'Не удалось записать активность.',
    activityLabel: 'Записать мою активность',
    activityLead: 'Новый лид',
    activityDemo: 'Демонстрация',
    activityApplication: 'Заявка',
    activityEvidence: 'Ссылка на подтверждение',
    activityPlaceholder: 'Ссылка на вашу активность',
    activityRecord: 'Записать активность',
    // Sales payout run
    payTitle: 'Проведение выплат',
    paySubtitle: 'Проверьте закрытые выписки, затем подтвердите выплаты. Автоматические выплаты остаются отключёнными до утверждения.',
    payCycle: 'Ежемесячный цикл',
    payCycleBody: 'Выписки закрываются 1-го числа. Проверка проходит с 1-го по 4-е число. Выплата производится 5-го числа или в следующий рабочий день. Каждая валюта выплачивается отдельно. Неготовые счета и неудавшиеся переводы остаются к выплате.',
    payStatementMonth: 'Месяц выписки',
    payClose: 'Закрыть выписки',
    payPay: 'Выплатить и экспортировать ведомость',
    paySync: 'Синхронизировать доходы',
    paySynced: 'Доходы и сторнирования синхронизированы.',
    payPreview: 'Только предпросмотр',
    payRequestFailed: 'Запрос не выполнен.',
    payNewLines: 'Новых строк в реестре: {n}. Требуется проверка выписки.',
    payDate: 'Дата выплаты: {date}.',
    payMayProceed: 'Выплаты можно проводить.',
    payWait: 'Выплаты ждут этой даты.',
    payNothing: 'Нечего обрабатывать',
    payNothingBody: 'За этот месяц нет остатка по выписке или выплате.',
    payLedgerHeld: 'Реестр и удержание',
    payPayment: 'Выплата',
    payReview: 'Проверка',
    payHeld: 'Удержано: {amount}',
    payConversion: 'Стоимость конвертации: {amount}',
    payReady: 'Готово',
    payPrepareQuote: 'Подготовить котировку конвертации',
    payQuotePrepared: 'Котировка подготовлена. Запустите новый предпросмотр.',
    payConfirmCheck: 'Суммы, валюты и заблокированные получатели мной проверены.',
    payConfirmClose: 'Подтвердить закрытие выписок',
    payConfirmPay: 'Подтвердить проведение выплат',
    payRunRecorded: 'Проведение записано: {result}',
    payPayroll: 'Экспорт и сверка зарплатной ведомости',
    payDownload: 'Скачать CSV записанной ведомости',
    payPaymentId: 'ID выплаты или экспорта',
    payProviderRef: 'Идентификатор у поставщика',
    payRecordPayroll: 'Записать выплаченную ведомость',
    payPayrollRecorded: 'Выплата по ведомости записана.',
    payActualCost: 'Фактическая стоимость конвертации',
    payActualBody: 'Запишите итоговую стоимость конвертации у поставщика в валюте реестра. Разница с котировкой становится корректировкой в текущей открытой выписке.',
    payLedgerCurrency: 'Валюта реестра',
    payActualCents: 'Фактическая стоимость в центах',
    payRecordActual: 'Записать фактическую стоимость',
    payActualReconciled: 'Фактическая стоимость конвертации сверена.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Активность, объекты и следующие шаги партнёров вашего портфеля.',
    bookLoadingPartners: 'Загрузка ваших партнёров...',
    bookQualifyingChargers: 'Квалифицирующие зарядные станции',
    bookLaunches12: 'Запуски за 12 месяцев',
    bookActivityTarget: 'Цель по активности: 8',
    bookLaunches6: 'Запуски за 6 месяцев',
    bookPaceTarget: 'Цель по темпу: 4',
    bookLocationsSubmissions: 'Объекты / заявки',
    bookLastContact: 'Последний контакт',
    bookLocationsHeading: 'Объекты и заявки',
    bookNoLocations: 'Объектов пока нет',
    bookNoLocationsBody: 'Здесь появятся объекты, отправленные этим партнёром.',
    bookNotes: 'Заметки и следующие шаги',
    bookNoPartners: 'В вашем портфеле нет партнёров',
    bookNoPartnersBody: 'Здесь появятся назначенные партнёры и их объекты.',
    bookLoadingSubmission: 'Загрузка заявки...',
    bookLocationType: 'Тип объекта',
    bookAddress: 'Адрес',
    bookContact: 'Контакт',
    bookConnectivity: 'Связь',
    bookCommissioning: 'Ввод в эксплуатацию разрешён',
    bookAwaitingReview: 'Ожидает проверки EVI',
    bookInstallerExpires: 'Срок доступа монтажника',
    bookNotIssued: 'Не выдан',
    bookLaunch: 'Запуск',
    bookAwaitingApproval: 'Ожидает одобрения EVI',
    bookOrdersTitle: 'Заказы',
    bookOrdersSubtitle: 'Заказы клиентов, закреплённые за вами и партнёрами вашего портфеля.',
    bookLoadingOrders: 'Загрузка заказов...',
    bookOrderDate: 'Дата заказа',
    bookDirectStaffOrder: 'Прямой заказ сотрудника',
    bookOrderTotal: 'Сумма заказа',
    bookQty: 'Кол-во: {n}',
    bookRegisterDeal: 'Зарегистрировать эту прямую сделку',
    bookClaimRecorded: 'Закрепление площадки записано. Существующие площадки вне продаж требуют проверки суперадминистратором.',
    bookClaimFailed: 'Не удалось записать закрепление площадки.',
    bookDealEvidence: 'Подтверждение прямой сделки',
    bookRegisterButton: 'Зарегистрировать прямую сделку',
    bookNoOrders: 'Заказов пока нет',
    bookNoOrdersBody: 'Здесь появятся заказы по вашим прямым сделкам и портфелю партнёров.',
    bookNewestOrders: 'Новые заказы',
    bookOlderOrders: 'Более ранние заказы',
    bookOnboardingTitle: 'Помощь с подключением',
    bookOnboardingSubtitle: 'Помогите людям на объектах вашего портфеля получить нужный доступ.',
    bookInvite: 'Пригласить пользователя',
    bookInviteBody: 'Отправьте приглашение или отправьте его повторно. Доставка выполняется по действующей почтовой политике.',
    bookNoInviteLocations: 'Пока нет объектов для приглашений',
    bookNoInviteLocationsBody: 'Объект должен быть в вашем портфеле, прежде чем вы сможете пригласить его пользователей.',
    bookInviteRecorded: 'Приглашение записано. Доставка выполняется по действующей почтовой политике.',
    bookInviteFailed: 'Не удалось отправить приглашение.',
    bookChooseLocation: 'Выберите объект',
    bookAccess: 'Доступ',
    roleBuildingAdmin: 'Администратор здания',
    rolePropertyManager: 'Управляющий недвижимостью',
    roleDriver: 'Водитель',
    bookInviting: 'Запись приглашения...',
    bookSendInvite: 'Отправить или повторить приглашение',
    // Partner and direct site notes
    notesHeading: 'Звонки, визиты и письма',
    notesIntro: 'Записывайте каждый контакт в тот же день. Заметки принадлежат EVI. Исправление добавляет новую версию и сохраняет исходную.',
    notesEmpty: 'Заметок пока нет',
    notesEmptyBody: 'Запишите ниже первый контакт и понятный следующий шаг.',
    notesVersion: 'Версия {n}',
    notesDue: 'Срок',
    notesAddCorrection: 'Добавить исправленную версию',
    notesLinksFailed: 'Не удалось загрузить связанные объекты и заявки.',
    notesRecorded: 'Заметка записана.',
    notesFailed: 'Не удалось записать заметку.',
    notesChannel: 'Канал',
    notesCall: 'Звонок',
    notesVisit: 'Визит',
    notesEmailChannel: 'Письмо',
    notesProjectStage: 'Этап проекта',
    notesNote: 'Заметка',
    notesNextDue: 'Срок следующего шага',
    notesLocationOptional: 'Объект (необязательно)',
    notesNoLocation: 'Без связанного объекта',
    notesSubmissionOptional: 'Заявка (необязательно)',
    notesNoSubmission: 'Без связанной заявки',
    notesRecordVersion: 'Записать новую версию',
    notesRecordNote: 'Записать заметку',
    notesCancel: 'Отменить исправление',
    notesSiteTitle: 'Заметки по прямым площадкам',
    notesNoClaims: 'Нет закреплённых прямых площадок',
    notesNoClaimsBody: 'Здесь появятся ваши закрепления прямых площадок и история контактов по ним.',
    notesStaffTitle: 'Заметки о партнёрах и следующие шаги',
    notesStaffIntro: 'Записывайте заметки о любом партнёре, включая собственных партнёров EVI. Заметки остаются за партнёром при его назначении представителю.',
    notesFindPartner: 'Найти партнёра',
    notesHouseBook: ' (собственный портфель EVI)',
    notesFirstPartners: 'Первые партнёры',
    notesMorePartners: 'Ещё партнёры',
    notesChooseBody: 'Выберите партнёра, чтобы просмотреть или записать историю контактов.',
    // My commission and Sales statements
    commPlanRevision: 'План комиссии, редакция {n}',
    commOfferRevision: 'Ваше предложение, редакция {n}',
    commEffective: 'Действует с {date}',
    commThrough: 'по {date}',
    commAccepted: 'Принято {date}. Принятый план доступен только для просмотра.',
    commDirectMargin: 'Прямая маржа на оборудовании',
    commAboveThreshold: 'Сверх годового порога',
    commPartnerPoints: 'Партнёрские пункты сессий',
    commFleetShare: 'Доля подписок автопарков',
    commFleetMeta: '{n} мес.; только закреплённые заказы автопарков',
    commFleetEvery: 'Каждое продление, пока вы в команде; только заказы автопарков',
    commAnnualThreshold: 'Годовой порог маржи на оборудовании ({currency})',
    commFirstOrder: 'Первый квалифицирующий оплаченный заказ',
    commFourthCharger: 'Четвёртая квалифицирующая зарядная станция',
    commBookDuration: 'Срок маржи по портфелю партнёров',
    commNoMonthLimit: 'Без ограничения по месяцам на время назначения',
    commMonthsPerPartner: '{n} мес. на партнёра',
    commContinuation: 'Продление после ухода на хороших условиях',
    commMonths: '{n} мес.',
    commOwnLadder: 'Шкала собственных прямых зарядных станций',
    commFromCharger: 'Начиная с квалифицирующей станции с отметкой',
    commSessionPoints: 'Пункты сессий',
    commLadderNote: 'Начисление начинается с четвёртой квалифицирующей прямой зарядной станции. Первые 12 месяцев составляют год наращивания. После этого новому запуску нужно 8 квалифицирующих запусков за последние 12 месяцев, включая его самого. Запуски без отметки учитываются в активности, но никогда не получают отметку позже. Существующие отметки продолжают приносить выплаты, пока вы в команде.',
    commBookTiers: 'Уровни маржи портфеля партнёров',
    commActiveAtLeast: 'Активных партнёров, не менее',
    commPointsNote: 'Партнёрские пункты сессий применяются после комиссии партнёра. Суммы сотрудников по сессиям ограничены доступным остатком EVI. Доля подписок автопарков начисляется, когда включено выставление счетов по подпискам; другие подписки долю не приносят.',
    commGolden: 'Выплаченный бонус Golden: {amount}.',
    commOverrideTiers: 'Уровни комиссии руководителя с команды',
    commTeamActiveAtLeast: 'Активных партнёров команды, не менее',
    commGrowth: 'Бонусы руководителя за рост: {six} при шести активных партнёрах и {payroll} при достижении подчинённым порога зарплатной ведомости. Командные пункты сессий: {points}.',
    commAccepting: 'Принятие...',
    commAccept: 'Принять этот план комиссии',
    commOfferAccepted: 'Предложение принято.',
    commOfferFailed: 'Не удалось принять предложение.',
    commStage2: 'Сведения об этапе 2',
    commStage2Body: 'Шесть полных месяцев записанной комиссии после сторнирований. Это не переводит вас на этап 2. Суперадминистратор должен записать утверждённую редакцию плана.',
    commConsecutive: 'Месяцев подряд: {n} из {required}',
    commCompletedMonth: 'Завершённый месяц',
    commRecordedCommission: 'Записанная комиссия ({currency})',
    commNoMonths: 'Завершённых месяцев плана пока нет',
    commNoMonthsBody: 'Здесь показаны только завершённые месяцы с даты вступления вашего плана в силу.',
    commMonthsAvailable: 'Доступно завершённых месяцев: {n} из 6.',
    commAverage: 'Среднее: {amount}',
    commAgainst: 'против {amount}',
    commNoCrossCurrency: '; без сравнения между валютами',
    commStatements: 'Ежемесячные выписки',
    commNoStatements: 'Выписок пока нет',
    commNoStatementsBody: 'Первая выписка появится после закрытия месяца комиссии.',
    commOpening: 'Начальный остаток',
    commReversals: 'Сторнирования',
    commPaid: 'Выплачено',
    commClosing: 'Конечный остаток',
    commNoPayments: 'Выплат пока нет',
    commNoPaymentsBody: 'Выплаты Connect или записи зарплатной ведомости появятся после выплаты доходов.',
    commLedgerAmount: 'Сумма в реестре',
    commMonth: 'Месяц',
    commStripeTransfer: 'Перевод Stripe',
    commNotSent: 'Не отправлен',
    commConversionCost: 'Стоимость конвертации',
    commCashAmount: 'Сумма выплаты',
    commSentAs: 'Отправлено как {amount}',
    commReservedAs: 'Зарезервировано как {amount}',
    commLines: 'Строки комиссии',
    commNoLines: 'Строк комиссии пока нет',
    commNoLinesBody: 'Квалифицирующая активность создаёт здесь строки комиссии, а последующие исправления показываются отдельно.',
    commCategory: 'Категория',
    commBasis: 'База',
    commPlainTerms: "Как читать: база это сумма, к которой применяется ваша ставка (для заказа оборудования это маржа EVI). Удержано значит заработано, но ещё не выплачено: EVI выплачивает ваш заработок по партнёру из полученной от этого партнёра маржи на оборудование, не более половины, а остальное выплачивается по мере того, как следующие заказы партнёра приносят больше. Этап 2 это план EVI с окладом: шесть полных месяцев комиссии это порог для рассмотрения, а переход это новый план, который EVI согласует с вами.",
    commRate: 'Ставка',
    commCommission: 'Комиссия',
    commPlanRevisionHeader: 'Редакция плана',
    commTitle: 'Моя комиссия',
    commSubtitle: 'Ваше предложение, активность по комиссии и записи о выплатах. CAD и USD учитываются раздельно.',
    commNoOffer: 'Предложение пока не записано',
    commNoOfferBody: 'EVI запишет план комиссии, чтобы вы могли его просмотреть и принять.',
    commClosedNote: 'Закрытые выписки не меняются. Исправления появляются новыми строками.',
    commLoading: 'Загрузка комиссии...',
    commNewestLines: 'Новые строки',
    commOlderLines: 'Более ранние строки',
    stmtTitle: 'Выписки отдела продаж',
    stmtSubtitle: 'Проверяйте записанные комиссии, выписки, выплаты и зарплатную ведомость вашего отдела продаж.',
    stmtNote: 'Используйте проверенные скрипты, чтобы начислить и закрыть месяц, выплатить подрядчикам Connect или экспортировать ведомость. Эта страница никогда не запускает списание или перевод.',
    stmtChooseBody: 'Выберите сотрудника, чтобы проверить его реестр комиссии и выписки.',
    stmtReviewQueue: 'Очередь проверки',
    stmtNothingReview: 'Нет ничего на проверке',
    stmtNothingReviewBody: 'Здесь появятся пункты проверки расчётов, когда подтверждение потребует внимания.',
    stmtSource: 'Источник',
    stmtCostMissing: 'Себестоимость не записана',
    stmtCostIntro: 'Ручные подтверждения, доступные только администраторам, хранятся отдельно. Исходный снимок заказа не меняется.',
    stmtNoMissing: 'Нет недостающих подтверждений себестоимости',
    stmtNoMissingBody: 'Здесь появятся закреплённые оплаченные заказы без снимка себестоимости.',
    stmtCostRecorded: 'Подтверждение себестоимости записано.',
    stmtCostFailed: 'Не удалось сохранить подтверждение себестоимости.',
    stmtChooseCurrency: 'Выберите валюту',
    stmtUnits: '{name}: {n} шт.',
    stmtUnitCost: 'Себестоимость единицы',
    stmtAutelFee: 'Поглощённый сбор Autel за единицу',
    stmtEvidenceReason: 'Подтверждение и причина',
    stmtRecordEvidence: 'Записать ручное подтверждение',
    stmtCorrection: 'Добавить исправление',
    stmtCorrectionIntro: 'Сторнирования указываются со знаком минус. Восстановления не могут превышать исходное право. Для повторной попытки используйте неизменный идентификатор.',
    stmtNoLinesToCorrect: 'Нет строк для исправления',
    stmtNoLinesToCorrectBody: 'Чтобы добавить исправление, нужна положительная строка комиссии.',
    stmtCorrectionRecorded: 'Исправление записано.',
    stmtCorrectionFailed: 'Не удалось внести исправление.',
    stmtOriginalLine: 'Исходная строка',
    stmtSignedAmount: 'Сумма со знаком',
    stmtReference: 'Идентификатор',
    stmtAppend: 'Добавить исправление',
    // Sales Staff
    staffTitle: 'Сотрудники отдела продаж',
    staffSubtitle: 'Управляйте сотрудниками, датированными планами комиссии и портфелями партнёров. Изменения проходят аудит.',
    staffNote: 'Только суперадминистратор может менять сотрудников, планы и назначения партнёров.',
    staffPeople: 'Сотрудники',
    staffActive: 'Активные',
    staffContractors: 'Подрядчики',
    staffNoStaff: 'Сотрудников отдела продаж пока нет',
    staffNoStaffBody: 'Добавьте ниже человека с существующей активной учётной записью.',
    staffKind: 'Вид',
    staffRole: 'Роль',
    staffConnect: 'Connect',
    staffEligible: 'Соответствует',
    staffNotEligible: 'Пока не соответствует',
    staffEligibility: '{status}: активных {active}/{target}, месяцев {months}/{required}',
    staffAdd: 'Добавить сотрудника',
    staffCreated: 'Запись сотрудника создана.',
    staffAddFailed: 'Не удалось добавить сотрудника.',
    staffInviteSent: 'Приглашение отправлено. Запись сотрудника с этой ролью, руководителем и датой начала будет создана, когда приглашение примут.',
    staffInviteNeedsName: 'У этого адреса ещё нет учётной записи, поэтому будет отправлено приглашение. Сначала укажите имя.',
    staffInvitesTitle: 'Приглашены, ещё не приняли',
    staffInvitedOn: 'Приглашение от {date}',
    staffInviteFailed: 'Не создано при принятии: {reason}',
    staffResendInvite: 'Отправить приглашение повторно',
    staffInviteResent: 'Приглашение отправлено повторно.',
    staffCancelInvite: 'Отменить приглашение',
    staffInviteCancelled: 'Приглашение отменено.',
    staffInviteActionFailed: 'Не удалось изменить приглашение.',
    inviteLinkAction: "Скопировать ссылку приглашения",
    inviteLinkTitle: "Скопировать ссылку приглашения",
    inviteLinkField: "Ссылка приглашения",
    inviteLinkLoading: "Создаём новую ссылку...",
    inviteLinkCopy: "Копировать",
    inviteLinkCopied: "Скопировано",
    inviteLinkSelected: "Ссылка выделена. Нажмите Ctrl+C или Cmd+C на Mac, чтобы скопировать её.",
    inviteLinkEmail: "Отправить с моей почты",
    inviteLinkReplaces: "Эта ссылка заменяет ссылку из прежних писем с приглашением. Она работает один раз и истекает {date}.",
    inviteLinkDiscard: "Если закрыть это окно, ссылка пропадёт. Она больше не будет показана.",
    inviteLinkExpired: "Срок действия приглашения истёк. Отправьте его повторно, чтобы создать новое, а затем скопируйте ту ссылку.",
    inviteLinkFailed: "Не удалось создать ссылку приглашения.",
    inviteLinkClose: "Закрыть",
    inviteLinkMailSubject: "Ваше приглашение в EV Initiative",
    inviteLinkMailGreeting: "Здравствуйте, {name}!",
    inviteLinkMailGreetingNoName: "Здравствуйте!",
    inviteLinkMailBody: "Отправляю ваше приглашение в EV Initiative со своей почты на случай, если предыдущее письмо не дошло. Ссылка ниже работает один раз и истекает {date}.",
    inviteLinkMailSignoff: "Спасибо,",
    inviteLinkSignInPage: "У этого человека уже есть аккаунт EV Initiative с этой почтой, поэтому ссылка открывает страницу входа. Вход в этот аккаунт принимает приглашение.",
    inviteLinkMailSignInPage: "Отправляю ваше приглашение в EV Initiative со своей почты на случай, если предыдущее письмо не дошло. У вас уже есть аккаунт EV Initiative с этой почтой, поэтому войдите в него по ссылке ниже, чтобы принять приглашение.",
    staffAccountEmail: 'Эл. почта учётной записи',
    staffStaffRole: 'Роль в продажах',
    staffContractor: 'Подрядчик',
    staffEmployee: 'Штатный сотрудник',
    staffPayThrough: 'Выплата через',
    staffStatesComma: 'Штаты (через запятую)',
    staffCreate: 'Создать запись сотрудника',
    staffPersonDetails: 'Сведения о сотруднике',
    staffChooseBody: 'Выберите строку или используйте «Сведения о сотруднике», чтобы проверить его запись, планы и портфель.',
    staffRecord: 'Запись сотрудника',
    staffAccount: 'Учётная запись',
    staffPartnerBook: 'Портфель партнёров',
    staffNoPartners: 'Партнёры не назначены',
    staffNoPartnersBody: 'Назначьте партнёра ниже, чтобы добавить его в портфель этого сотрудника.',
    staffMoveAll: "Переместить всех партнёров",
    staffMoveAllIntro: "Перемещает всех партнёров, которых {name} ведёт сегодня, к другому SP Manager или в портфель EVI, начиная с этого момента. Уже запущенные зарядные станции сохраняют отметку запуска.",
    staffMoveAllTo: "Переместить к",
    staffHouseBook: "Портфель EVI",
    staffMoveAllButton: "Переместить всех партнёров",
    staffMoveAllDone: "Перемещено партнёров: {count}.",
    staffDepartureMoves: "В дату ухода партнёры этого сотрудника переходят к его Sales Manager, а если его нет, в портфель EVI.",
    staffDeparted: 'Уход {date}: {reason}',
    staffUpdated: 'Запись сотрудника обновлена.',
    staffUpdateFailed: 'Не удалось обновить запись.',
    staffStates: 'Штаты',
    staffEmploymentNote: 'Для изменений трудоустройства используйте датированный переход в разделе «Новая редакция плана». Отметки станций, назначения портфеля и окно активности остаются за этой записью сотрудника.',
    staffEndDate: 'Дата окончания (необязательно)',
    staffDepartureReason: 'Причина ухода',
    staffGoodStanding: 'Хорошие условия',
    staffCause: 'По вине сотрудника',
    staffConductIds: 'ID строк реестра, связанных с нарушениями (только при уходе по вине сотрудника)',
    staffSave: 'Сохранить изменения сотрудника',
    staffManagerAssignment: 'Датированное назначение руководителя',
    staffManagerRecorded: 'Датированное назначение руководителя записано.',
    staffPlanRevisions: 'Редакции плана',
    staffNoPlans: 'Редакций плана нет',
    staffNoPlansBody: 'Запишите датированное предложение, которое этот сотрудник должен принять.',
    staffRevision: 'Редакция',
    staffEffectiveFrom: 'Действует с',
    staffEffectiveTo: 'Действует по',
    staffOngoing: 'Бессрочно',
    staffAssignPartner: 'Назначить партнёра',
    staffAssignIntro: 'Запишите новое назначение с причиной. Прежняя история назначений сохраняется.',
    staffAssignmentRecorded: 'Назначение записано.',
    staffUnassigned: 'Не назначен',
    staffStandardPlan: 'Стандартный план для будущих представителей',
    staffSiteClaims: 'Закрепления площадок',
    staffClaimsIntro: 'Существующие площадки вне продаж требуют одобрения. Пока закрепление ожидает проверки, доступ к портфелю и комиссия не предоставляются.',
    staffNoClaims: 'Закреплений площадок нет',
    staffNoClaimsBody: 'Здесь появятся зарегистрированные площадки продаж для проверки.',
    staffRegistrant: 'Заявитель',
    staffExisting: 'Уже существует ({kind}): {id}',
    staffReviewRecorded: 'Проверка записана.',
    staffReviewFailed: 'Не удалось записать проверку.',
    staffDecision: 'Решение',
    staffReject: 'Отклонить',
    staffApprove: 'Одобрить',
    staffRecordDecision: 'Записать решение',
    staffReassignRecorded: 'Переназначение записано.',
    staffReassign: 'Переназначить закрепление',
    staffReassignFailed: 'Не удалось переназначить.',
    staffRegistrantKind: 'Вид нового заявителя',
    // Plan revision form
    planNoMonthLimit: 'Без ограничения по месяцам',
    planStoredStandard: 'Сохранённая стандартная редакция',
    planNew: 'Новая редакция плана',
    planPrefilled: 'Заполнено по сохранённой стандартной редакции {revision}. Принятые планы и существующие отметки станций не меняются.',
    planPrefilledCode: 'Заполнено по действующему стандарту, редакция {revision}, ещё не записана. Принятые планы и существующие отметки станций не меняются.',
    planLoading: 'загрузка',
    planLoadFailed: 'Не удалось загрузить сохранённый стандарт. Записать редакцию нельзя.',
    planStage: 'Этап плана',
    planStage1: 'Комиссия этапа 1',
    planStage2: 'Ставки зарплатной ведомости этапа 2',
    planNoRates: 'Для этого этапа нет сохранённых ставок.',
    planCentsReadback: 'То есть {amount}',
    planRecorded: 'Редакция записана. Персональная редакция требует принятия.',
    planFailed: 'Не удалось записать редакцию.',
    planDatedRevision: 'Датированная редакция плана или роли',
    planEmployeeMove: 'Датированный перевод в штат и на зарплатную ведомость',
    planRoleOnRevision: 'Роль в этой редакции',
    planNotice: 'Номер письменного уведомления',
    planRecord: 'Записать новую редакцию',
  },
  uk: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Не записано',
    notSet: 'Не задано',
    noResults: 'Немає результатів',
    person: 'Співробітник',
    status: 'Статус',
    reason: 'Причина',
    partner: 'Партнер',
    partners: 'Партнери',
    chooseAPerson: 'Виберіть співробітника',
    chooseAPartner: 'Виберіть партнера',
    nextStep: 'Наступний крок',
    ledgerMonth: 'Місяць реєстру',
    allMonths: 'Усі місяці',
    manager: 'Керівник',
    noManager: 'Без керівника',
    rep: 'Представник',
    roleManager: 'Керівник із власним портфелем представника',
    territory: 'Територія',
    startDate: 'Дата початку',
    effectiveDate: 'Дата набрання чинності',
    action: 'Дія',
    period: 'Період',
    currency: 'Валюта',
    location: "Об'єкт",
    name: "Ім'я",
    email: 'Ел. пошта',
    paymentMethod: 'Спосіб виплати',
    stripeConnect: 'Stripe Connect',
    payroll: 'Зарплатна відомість',
    payrollEligibility: 'Допуск до зарплатної відомості',
    activePartners: 'Активні партнери',
    marginShare: 'Частка маржі',
    heldUnderCap: 'Утримано за лімітом',
    earned: 'Зароблено',
    paymentsAndPayroll: 'Виплати й зарплатна відомість',
    salesStaff: 'Співробітник відділу продажів',
    salesPartner: 'Торговий партнер',
    recording: 'Запис...',
    recordAssignment: 'Записати призначення',
    assignmentFailed: 'Не вдалося виконати призначення.',
    pointOne: '{n} в. п.',
    pointOther: '{n} в. п.',
    statusActive: 'активний',
    statusInactive: 'неактивний',
    statusQualified: 'кваліфікований',
    statusNotQualified: 'некваліфікований',
    statusAccepted: 'прийнято',
    statusAwaitingAcceptance: 'очікує прийняття',
    statusAssigned: 'призначено',
    statusOffline: 'не в мережі',
    statusNotStarted: 'не розпочато',
    statusPending: 'в очікуванні',
    statusNoSubmission60: 'Немає заявок за 60 днів',
    statusBelowPace: 'Нижче темпу за 6 місяців',
    statusOrderFollowUp: 'Потрібен контроль замовлення',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Відділ продажів',
    teamSubtitle: 'Ваша мережа, активність команди та записані доходи.',
    teamOpenPerson: 'Відкрити команду співробітника',
    teamLoading: 'Завантаження вашої команди',
    teamChoose: 'Виберіть команду',
    teamEmptyBody: "Тут з'являються доступні вам дані команди та портфеля.",
    tabMap: 'Карта',
    tabTeam: 'Команда',
    tabReports: 'Звіти',
    tabScoreboard: 'Рейтинг',
    teamMyBook: 'Мій портфель',
    teamWholeTeam: 'Уся команда',
    teamNetworkMap: 'Карта мережі',
    teamEveryone: 'Усі у вашій зоні доступу',
    teamStage: 'Етап',
    teamAllStages: 'Усі етапи',
    teamChargerType: 'Тип зарядної станції',
    teamAllTypes: 'Усі типи',
    teamState: 'Штат',
    teamAllStates: 'Усі штати',
    teamLaunchedFrom: 'Запуск з',
    teamLaunchedThrough: 'Запуск по',
    teamPinLegend: 'Колір мітки позначає співробітника. Зафарбоване коло означає «у мережі», порожнє коло означає «не в мережі», а знак оклику означає несправність. Майданчиків без координат на карті: {n}.',
    teamCloseDetails: 'Закрити відомості про майданчик',
    teamCity: 'Місто',
    teamChargers: 'Зарядні станції',
    teamDirectDeal: 'Пряма угода',
    teamLaunchDate: 'Дата запуску',
    teamNotLaunched: 'Не запущено',
    teamSessions30: 'Сеанси, останні 30 днів',
    teamKwh30: 'кВт·год, останні 30 днів',
    teamNoSites: 'Немає відповідних майданчиків',
    teamNoSitesBody: 'Змініть фільтри, щоб побачити інші майданчики вашого портфеля.',
    teamSite: 'Майданчик',
    teamDirectReports: 'Прямі підлеглі',
    teamNoDirectReports: 'Немає прямих підлеглих',
    teamNoDirectReportsBody: "Призначення з датами з'являться тут після запису.",
    teamActivity: 'Активність',
    teamEarnedMonth: 'Зароблено цього місяця',
    teamActivatedMonth: 'Активовано цього місяця',
    teamLaunchedMonth: 'Запущено цього місяця',
    teamPace: 'Темп',
    teamOnPace: 'У темпі',
    teamEligibility: 'Активних: {active}/{target}, місяців: {months}/{required}.',
    teamUsdAverage: 'Середнє за шість місяців у USD: {amount}',
    teamNotAvailable: 'Недоступно',
    teamScoreboard: 'Рейтинг команди',
    teamThisWeek: 'Цей тиждень',
    teamThisMonth: 'Цей місяць',
    teamScoreNote: 'Лише кількість. Рейтинг будується за кваліфікаційними запусками, потім за активними партнерами та активаціями. Ліди, демонстрації та заявки беруться з ваших перевірених записів про активність. Дзвінки та розмови беруться з нотаток про партнерів. Оцінки не додаються.',
    teamOutreach: 'Залучення',
    teamLaunches: 'Запуски',
    scoreLeadsStarted: 'розпочаті ліди',
    scoreCalls: 'дзвінки',
    scoreConversations: 'розмови',
    scoreDemos: 'демонстрації',
    scoreApplications: 'заявки',
    scoreApproved: 'схвалено',
    scoreFirstPaidOrders: 'перші оплачені замовлення',
    scoreActivated: 'активовано',
    scoreActivePartners: 'активні партнери',
    scoreQualifyingLaunches: 'кваліфікаційні запуски',
    reportMonthly: 'Комісія за місяцями та категоріями',
    reportSessions: 'Пункти сеансів за зарядними станціями',
    reportYearEnd: 'Підсумки року',
    reportsTitle: 'Мої звіти',
    reportYear: 'Рік',
    reportKind: 'Звіт',
    reportDownloadFailed: 'Не вдалося завантажити звіт.',
    reportExport: 'Експорт у CSV',
    reportEmpty: 'Немає записаної активності',
    reportEmptyBody: "Тут з'являться записані доходи та підтвердження виплат.",
    reportNote: 'Валюти реєстру обліковуються окремо. Підсумки року допомагають підготувати звітність і не є податковою формою.',
    activityRecorded: 'Активність записано.',
    activityFailed: 'Не вдалося записати активність.',
    activityLabel: 'Записати мою активність',
    activityLead: 'Новий лід',
    activityDemo: 'Демонстрація',
    activityApplication: 'Заявка',
    activityEvidence: 'Посилання на підтвердження',
    activityPlaceholder: 'Посилання на вашу активність',
    activityRecord: 'Записати активність',
    // Sales payout run
    payTitle: 'Проведення виплат',
    paySubtitle: 'Перегляньте закриті виписки, а потім підтвердіть виплати. Автоматичні виплати залишаються вимкненими до затвердження.',
    payCycle: 'Щомісячний цикл',
    payCycleBody: 'Виписки закриваються 1-го числа. Перевірка триває з 1-го по 4-те число. Виплата здійснюється 5-го числа або наступного робочого дня. Кожна валюта виплачується окремо. Неготові рахунки та невдалі перекази залишаються до виплати.',
    payStatementMonth: 'Місяць виписки',
    payClose: 'Закрити виписки',
    payPay: 'Виплатити й експортувати відомість',
    paySync: 'Синхронізувати доходи',
    paySynced: 'Доходи та сторнування синхронізовано.',
    payPreview: 'Лише попередній перегляд',
    payRequestFailed: 'Запит не виконано.',
    payNewLines: 'Нових рядків у реєстрі: {n}. Потрібна перевірка виписки.',
    payDate: 'Дата виплати: {date}.',
    payMayProceed: 'Виплати можна проводити.',
    payWait: 'Виплати чекають на цю дату.',
    payNothing: 'Немає чого обробляти',
    payNothingBody: 'За цей місяць немає залишку за випискою або виплатою.',
    payLedgerHeld: 'Реєстр і утримання',
    payPayment: 'Виплата',
    payReview: 'Перевірка',
    payHeld: 'Утримано: {amount}',
    payConversion: 'Вартість конвертації: {amount}',
    payReady: 'Готово',
    payPrepareQuote: 'Підготувати котирування конвертації',
    payQuotePrepared: 'Котирування підготовлено. Запустіть новий попередній перегляд.',
    payConfirmCheck: 'Суми, валюти та заблокованих отримувачів мною перевірено.',
    payConfirmClose: 'Підтвердити закриття виписок',
    payConfirmPay: 'Підтвердити проведення виплат',
    payRunRecorded: 'Проведення записано: {result}',
    payPayroll: 'Експорт і звірка зарплатної відомості',
    payDownload: 'Завантажити CSV записаної відомості',
    payPaymentId: 'ID виплати або експорту',
    payProviderRef: 'Ідентифікатор у постачальника',
    payRecordPayroll: 'Записати виплачену відомість',
    payPayrollRecorded: 'Виплату за відомістю записано.',
    payActualCost: 'Фактична вартість конвертації',
    payActualBody: 'Запишіть остаточну вартість конвертації у постачальника у валюті реєстру. Різниця з котируванням стає коригуванням у поточній відкритій виписці.',
    payLedgerCurrency: 'Валюта реєстру',
    payActualCents: 'Фактична вартість у центах',
    payRecordActual: 'Записати фактичну вартість',
    payActualReconciled: 'Фактичну вартість конвертації звірено.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: "Активність, об'єкти та наступні кроки партнерів вашого портфеля.",
    bookLoadingPartners: 'Завантаження ваших партнерів...',
    bookQualifyingChargers: 'Кваліфікаційні зарядні станції',
    bookLaunches12: 'Запуски за 12 місяців',
    bookActivityTarget: 'Ціль з активності: 8',
    bookLaunches6: 'Запуски за 6 місяців',
    bookPaceTarget: 'Ціль з темпу: 4',
    bookLocationsSubmissions: "Об'єкти / заявки",
    bookLastContact: 'Останній контакт',
    bookLocationsHeading: "Об'єкти та заявки",
    bookNoLocations: "Об'єктів поки немає",
    bookNoLocationsBody: "Тут з'являться об'єкти, надіслані цим партнером.",
    bookNotes: 'Нотатки та наступні кроки',
    bookNoPartners: 'У вашому портфелі немає партнерів',
    bookNoPartnersBody: "Тут з'являться призначені партнери та їхні об'єкти.",
    bookLoadingSubmission: 'Завантаження заявки...',
    bookLocationType: "Тип об'єкта",
    bookAddress: 'Адреса',
    bookContact: 'Контакт',
    bookConnectivity: "Зв'язок",
    bookCommissioning: 'Введення в експлуатацію дозволено',
    bookAwaitingReview: 'Очікує перевірки EVI',
    bookInstallerExpires: 'Термін доступу монтажника',
    bookNotIssued: 'Не видано',
    bookLaunch: 'Запуск',
    bookAwaitingApproval: 'Очікує схвалення EVI',
    bookOrdersTitle: 'Замовлення',
    bookOrdersSubtitle: 'Замовлення клієнтів, закріплені за вами та партнерами вашого портфеля.',
    bookLoadingOrders: 'Завантаження замовлень...',
    bookOrderDate: 'Дата замовлення',
    bookDirectStaffOrder: 'Пряме замовлення співробітника',
    bookOrderTotal: 'Сума замовлення',
    bookQty: 'К-сть: {n}',
    bookRegisterDeal: 'Зареєструвати цю пряму угоду',
    bookClaimRecorded: 'Закріплення майданчика записано. Наявні майданчики поза продажами потребують перевірки суперадміністратором.',
    bookClaimFailed: 'Не вдалося записати закріплення майданчика.',
    bookDealEvidence: 'Підтвердження прямої угоди',
    bookRegisterButton: 'Зареєструвати пряму угоду',
    bookNoOrders: 'Замовлень поки немає',
    bookNoOrdersBody: "Тут з'являться замовлення за вашими прямими угодами та портфелем партнерів.",
    bookNewestOrders: 'Нові замовлення',
    bookOlderOrders: 'Раніші замовлення',
    bookOnboardingTitle: 'Допомога з підключенням',
    bookOnboardingSubtitle: "Допоможіть людям на об'єктах вашого портфеля отримати потрібний доступ.",
    bookInvite: 'Запросити користувача',
    bookInviteBody: 'Надішліть запрошення або надішліть його повторно. Доставка виконується за чинною поштовою політикою.',
    bookNoInviteLocations: "Поки немає об'єктів для запрошень",
    bookNoInviteLocationsBody: "Об'єкт має бути у вашому портфелі, перш ніж ви зможете запросити його користувачів.",
    bookInviteRecorded: 'Запрошення записано. Доставка виконується за чинною поштовою політикою.',
    bookInviteFailed: 'Не вдалося надіслати запрошення.',
    bookChooseLocation: "Виберіть об'єкт",
    bookAccess: 'Доступ',
    roleBuildingAdmin: 'Адміністратор будівлі',
    rolePropertyManager: 'Управитель нерухомості',
    roleDriver: 'Водій',
    bookInviting: 'Запис запрошення...',
    bookSendInvite: 'Надіслати або повторити запрошення',
    // Partner and direct site notes
    notesHeading: 'Дзвінки, візити та листи',
    notesIntro: 'Записуйте кожен контакт того ж дня. Нотатки належать EVI. Виправлення додає нову версію та зберігає оригінал.',
    notesEmpty: 'Нотаток поки немає',
    notesEmptyBody: 'Запишіть нижче перший контакт і зрозумілий наступний крок.',
    notesVersion: 'Версія {n}',
    notesDue: 'Термін',
    notesAddCorrection: 'Додати виправлену версію',
    notesLinksFailed: "Не вдалося завантажити пов'язані об'єкти та заявки.",
    notesRecorded: 'Нотатку записано.',
    notesFailed: 'Не вдалося записати нотатку.',
    notesChannel: 'Канал',
    notesCall: 'Дзвінок',
    notesVisit: 'Візит',
    notesEmailChannel: 'Лист',
    notesProjectStage: 'Етап проєкту',
    notesNote: 'Нотатка',
    notesNextDue: 'Термін наступного кроку',
    notesLocationOptional: "Об'єкт (необов'язково)",
    notesNoLocation: "Без пов'язаного об'єкта",
    notesSubmissionOptional: "Заявка (необов'язково)",
    notesNoSubmission: "Без пов'язаної заявки",
    notesRecordVersion: 'Записати нову версію',
    notesRecordNote: 'Записати нотатку',
    notesCancel: 'Скасувати виправлення',
    notesSiteTitle: 'Нотатки щодо прямих майданчиків',
    notesNoClaims: 'Немає закріплених прямих майданчиків',
    notesNoClaimsBody: "Тут з'являться ваші закріплення прямих майданчиків та історія контактів щодо них.",
    notesStaffTitle: 'Нотатки про партнерів і наступні кроки',
    notesStaffIntro: 'Записуйте нотатки про будь-якого партнера, зокрема власних партнерів EVI. Нотатки залишаються за партнером, коли його призначають представникові.',
    notesFindPartner: 'Знайти партнера',
    notesHouseBook: ' (власний портфель EVI)',
    notesFirstPartners: 'Перші партнери',
    notesMorePartners: 'Ще партнери',
    notesChooseBody: 'Виберіть партнера, щоб переглянути або записати історію контактів.',
    // My commission and Sales statements
    commPlanRevision: 'План комісії, редакція {n}',
    commOfferRevision: 'Ваша пропозиція, редакція {n}',
    commEffective: 'Діє з {date}',
    commThrough: 'по {date}',
    commAccepted: 'Прийнято {date}. Прийнятий план доступний лише для перегляду.',
    commDirectMargin: 'Пряма маржа на обладнанні',
    commAboveThreshold: 'Понад річний поріг',
    commPartnerPoints: 'Партнерські пункти сеансів',
    commFleetShare: 'Частка підписок автопарків',
    commFleetMeta: '{n} міс.; лише закріплені замовлення автопарків',
    commFleetEvery: 'Кожне продовження, поки ви в команді; лише замовлення автопарків',
    commAnnualThreshold: 'Річний поріг маржі на обладнанні ({currency})',
    commFirstOrder: 'Перше кваліфікаційне оплачене замовлення',
    commFourthCharger: 'Четверта кваліфікаційна зарядна станція',
    commBookDuration: 'Строк маржі за портфелем партнерів',
    commNoMonthLimit: 'Без обмеження за місяцями на час призначення',
    commMonthsPerPartner: '{n} міс. на партнера',
    commContinuation: 'Продовження після відходу на добрих умовах',
    commMonths: '{n} міс.',
    commOwnLadder: 'Шкала власних прямих зарядних станцій',
    commFromCharger: 'Починаючи з кваліфікаційної станції з позначкою',
    commSessionPoints: 'Пункти сеансів',
    commLadderNote: 'Нарахування починається з четвертої кваліфікаційної прямої зарядної станції. Перші 12 місяців є роком нарощування. Після цього новому запуску потрібно 8 кваліфікаційних запусків за останні 12 місяців, включно з ним самим. Запуски без позначки враховуються в активності, але ніколи не отримують позначку пізніше. Наявні позначки й надалі приносять виплати, поки ви в команді.',
    commBookTiers: 'Рівні маржі портфеля партнерів',
    commActiveAtLeast: 'Активних партнерів, не менше',
    commPointsNote: 'Партнерські пункти сеансів застосовуються після комісії партнера. Суми співробітників за сеансами обмежені доступним залишком EVI. Частка підписок автопарків нараховується, коли ввімкнено виставлення рахунків за підписками; інші підписки частки не приносять.',
    commGolden: 'Виплачений бонус Golden: {amount}.',
    commOverrideTiers: 'Рівні комісії керівника з команди',
    commTeamActiveAtLeast: 'Активних партнерів команди, не менше',
    commGrowth: 'Бонуси керівника за зростання: {six} за наявності шести активних партнерів і {payroll} після досягнення підлеглим порогу зарплатної відомості. Командні пункти сеансів: {points}.',
    commAccepting: 'Прийняття...',
    commAccept: 'Прийняти цей план комісії',
    commOfferAccepted: 'Пропозицію прийнято.',
    commOfferFailed: 'Не вдалося прийняти пропозицію.',
    commStage2: 'Відомості про етап 2',
    commStage2Body: 'Шість повних місяців записаної комісії після сторнувань. Це не переводить вас на етап 2. Суперадміністратор має записати затверджену редакцію плану.',
    commConsecutive: 'Місяців поспіль: {n} з {required}',
    commCompletedMonth: 'Завершений місяць',
    commRecordedCommission: 'Записана комісія ({currency})',
    commNoMonths: 'Завершених місяців плану поки немає',
    commNoMonthsBody: 'Тут показано лише завершені місяці від дати набрання чинності вашим планом.',
    commMonthsAvailable: 'Доступно завершених місяців: {n} з 6.',
    commAverage: 'Середнє: {amount}',
    commAgainst: 'проти {amount}',
    commNoCrossCurrency: '; без порівняння між валютами',
    commStatements: 'Щомісячні виписки',
    commNoStatements: 'Виписок поки немає',
    commNoStatementsBody: "Перша виписка з'явиться після закриття місяця комісії.",
    commOpening: 'Початковий залишок',
    commReversals: 'Сторнування',
    commPaid: 'Виплачено',
    commClosing: 'Кінцевий залишок',
    commNoPayments: 'Виплат поки немає',
    commNoPaymentsBody: "Виплати Connect або записи зарплатної відомості з'являться після виплати доходів.",
    commLedgerAmount: 'Сума в реєстрі',
    commMonth: 'Місяць',
    commStripeTransfer: 'Переказ Stripe',
    commNotSent: 'Не надіслано',
    commConversionCost: 'Вартість конвертації',
    commCashAmount: 'Сума виплати',
    commSentAs: 'Надіслано як {amount}',
    commReservedAs: 'Зарезервовано як {amount}',
    commLines: 'Рядки комісії',
    commNoLines: 'Рядків комісії поки немає',
    commNoLinesBody: 'Кваліфікаційна активність створює тут рядки комісії, а подальші виправлення показуються окремо.',
    commCategory: 'Категорія',
    commBasis: 'База',
    commPlainTerms: "Як читати: база це сума, до якої застосовується ваша ставка (для замовлення обладнання це маржа EVI). Утримано означає зароблено, але ще не виплачено: EVI виплачує ваш заробіток за партнером з отриманої від цього партнера маржі на обладнання, не більше половини, а решта виплачується, коли наступні замовлення партнера приносять більше. Етап 2 це план EVI з окладом: шість повних місяців комісії це поріг для розгляду, а перехід це новий план, який EVI погоджує з вами.",
    commRate: 'Ставка',
    commCommission: 'Комісія',
    commPlanRevisionHeader: 'Редакція плану',
    commTitle: 'Моя комісія',
    commSubtitle: 'Ваша пропозиція, активність за комісією та записи про виплати. CAD і USD обліковуються окремо.',
    commNoOffer: 'Пропозицію ще не записано',
    commNoOfferBody: 'EVI запише план комісії, щоб ви могли його переглянути й прийняти.',
    commClosedNote: "Закриті виписки не змінюються. Виправлення з'являються новими рядками.",
    commLoading: 'Завантаження комісії...',
    commNewestLines: 'Нові рядки',
    commOlderLines: 'Раніші рядки',
    stmtTitle: 'Виписки відділу продажів',
    stmtSubtitle: 'Перевіряйте записані комісії, виписки, виплати та зарплатну відомість вашого відділу продажів.',
    stmtNote: 'Використовуйте перевірені скрипти, щоб нарахувати й закрити місяць, виплатити підрядникам Connect або експортувати відомість. Ця сторінка ніколи не запускає списання чи переказ.',
    stmtChooseBody: 'Виберіть співробітника, щоб перевірити його реєстр комісії та виписки.',
    stmtReviewQueue: 'Черга перевірки',
    stmtNothingReview: 'Немає нічого на перевірці',
    stmtNothingReviewBody: "Тут з'являться пункти перевірки розрахунків, коли підтвердження потребуватиме уваги.",
    stmtSource: 'Джерело',
    stmtCostMissing: 'Собівартість не записано',
    stmtCostIntro: 'Ручні підтвердження, доступні лише адміністраторам, зберігаються окремо. Початковий знімок замовлення не змінюється.',
    stmtNoMissing: 'Немає відсутніх підтверджень собівартості',
    stmtNoMissingBody: "Тут з'являться закріплені оплачені замовлення без знімка собівартості.",
    stmtCostRecorded: 'Підтвердження собівартості записано.',
    stmtCostFailed: 'Не вдалося зберегти підтвердження собівартості.',
    stmtChooseCurrency: 'Виберіть валюту',
    stmtUnits: '{name}: {n} шт.',
    stmtUnitCost: 'Собівартість одиниці',
    stmtAutelFee: 'Поглинутий збір Autel за одиницю',
    stmtEvidenceReason: 'Підтвердження та причина',
    stmtRecordEvidence: 'Записати ручне підтвердження',
    stmtCorrection: 'Додати виправлення',
    stmtCorrectionIntro: 'Сторнування вказуються зі знаком мінус. Відновлення не можуть перевищувати початкове право. Для повторної спроби використовуйте незмінний ідентифікатор.',
    stmtNoLinesToCorrect: 'Немає рядків для виправлення',
    stmtNoLinesToCorrectBody: 'Щоб додати виправлення, потрібен додатний рядок комісії.',
    stmtCorrectionRecorded: 'Виправлення записано.',
    stmtCorrectionFailed: 'Не вдалося внести виправлення.',
    stmtOriginalLine: 'Початковий рядок',
    stmtSignedAmount: 'Сума зі знаком',
    stmtReference: 'Ідентифікатор',
    stmtAppend: 'Додати виправлення',
    // Sales Staff
    staffTitle: 'Співробітники відділу продажів',
    staffSubtitle: 'Керуйте співробітниками, датованими планами комісії та портфелями партнерів. Зміни проходять аудит.',
    staffNote: 'Лише суперадміністратор може змінювати співробітників, плани та призначення партнерів.',
    staffPeople: 'Співробітники',
    staffActive: 'Активні',
    staffContractors: 'Підрядники',
    staffNoStaff: 'Співробітників відділу продажів поки немає',
    staffNoStaffBody: 'Додайте нижче людину з наявним активним обліковим записом.',
    staffKind: 'Вид',
    staffRole: 'Роль',
    staffConnect: 'Connect',
    staffEligible: 'Відповідає',
    staffNotEligible: 'Поки не відповідає',
    staffEligibility: '{status}: активних {active}/{target}, місяців {months}/{required}',
    staffAdd: 'Додати співробітника',
    staffCreated: 'Запис співробітника створено.',
    staffAddFailed: 'Не вдалося додати співробітника.',
    staffInviteSent: 'Запрошення надіслано. Запис співробітника з цією роллю, керівником і датою початку буде створено, коли запрошення приймуть.',
    staffInviteNeedsName: 'Ця адреса ще не має облікового запису, тож буде надіслано запрошення. Спочатку вкажіть ім’я.',
    staffInvitesTitle: 'Запрошені, ще не прийняли',
    staffInvitedOn: 'Запрошено {date}',
    staffInviteFailed: 'Не створено під час прийняття: {reason}',
    staffResendInvite: 'Надіслати запрошення повторно',
    staffInviteResent: 'Запрошення надіслано повторно.',
    staffCancelInvite: 'Скасувати запрошення',
    staffInviteCancelled: 'Запрошення скасовано.',
    staffInviteActionFailed: 'Не вдалося змінити запрошення.',
    inviteLinkAction: "Скопіювати посилання запрошення",
    inviteLinkTitle: "Скопіювати посилання запрошення",
    inviteLinkField: "Посилання запрошення",
    inviteLinkLoading: "Створюємо нове посилання...",
    inviteLinkCopy: "Копіювати",
    inviteLinkCopied: "Скопійовано",
    inviteLinkSelected: "Посилання виділено. Натисніть Ctrl+C або Cmd+C на Mac, щоб скопіювати його.",
    inviteLinkEmail: "Надіслати з моєї пошти",
    inviteLinkReplaces: "Це посилання замінює посилання з попередніх листів із запрошенням. Воно працює один раз і спливає {date}.",
    inviteLinkDiscard: "Якщо закрити це вікно, посилання зникне. Його більше не буде показано.",
    inviteLinkExpired: "Термін дії запрошення минув. Надішліть його повторно, щоб створити нове, а потім скопіюйте те посилання.",
    inviteLinkFailed: "Не вдалося створити посилання запрошення.",
    inviteLinkClose: "Закрити",
    inviteLinkMailSubject: "Ваше запрошення до EV Initiative",
    inviteLinkMailGreeting: "Вітаю, {name}!",
    inviteLinkMailGreetingNoName: "Вітаю!",
    inviteLinkMailBody: "Надсилаю ваше запрошення до EV Initiative зі своєї пошти на випадок, якщо попередній лист не дійшов. Посилання нижче працює один раз і спливає {date}.",
    inviteLinkMailSignoff: "Дякую,",
    inviteLinkSignInPage: "Ця людина вже має акаунт EV Initiative з цією поштою, тому посилання відкриває сторінку входу. Вхід у цей акаунт приймає запрошення.",
    inviteLinkMailSignInPage: "Надсилаю ваше запрошення до EV Initiative зі своєї пошти на випадок, якщо попередній лист не дійшов. Ви вже маєте акаунт EV Initiative з цією поштою, тож увійдіть у нього за посиланням нижче, щоб прийняти запрошення.",
    staffAccountEmail: 'Ел. пошта облікового запису',
    staffStaffRole: 'Роль у продажах',
    staffContractor: 'Підрядник',
    staffEmployee: 'Штатний співробітник',
    staffPayThrough: 'Виплата через',
    staffStatesComma: 'Штати (через кому)',
    staffCreate: 'Створити запис співробітника',
    staffPersonDetails: 'Відомості про співробітника',
    staffChooseBody: 'Виберіть рядок або скористайтеся полем «Відомості про співробітника», щоб перевірити його запис, плани та портфель.',
    staffRecord: 'Запис співробітника',
    staffAccount: 'Обліковий запис',
    staffPartnerBook: 'Портфель партнерів',
    staffNoPartners: 'Партнерів не призначено',
    staffNoPartnersBody: 'Призначте партнера нижче, щоб додати його до портфеля цього співробітника.',
    staffMoveAll: "Перемістити всіх партнерів",
    staffMoveAllIntro: "Переміщує всіх партнерів, яких {name} веде сьогодні, до іншого SP Manager або в портфель EVI, починаючи з цього моменту. Уже запущені зарядні станції зберігають позначку запуску.",
    staffMoveAllTo: "Перемістити до",
    staffHouseBook: "Портфель EVI",
    staffMoveAllButton: "Перемістити всіх партнерів",
    staffMoveAllDone: "Переміщено партнерів: {count}.",
    staffDepartureMoves: "У дату звільнення партнери цього співробітника переходять до його Sales Manager, а якщо його немає, у портфель EVI.",
    staffDeparted: 'Відхід {date}: {reason}',
    staffUpdated: 'Запис співробітника оновлено.',
    staffUpdateFailed: 'Не вдалося оновити запис.',
    staffStates: 'Штати',
    staffEmploymentNote: 'Для змін у працевлаштуванні використовуйте датований перехід у розділі «Нова редакція плану». Позначки станцій, призначення портфеля та вікно активності залишаються за цим записом співробітника.',
    staffEndDate: "Дата завершення (необов'язково)",
    staffDepartureReason: 'Причина відходу',
    staffGoodStanding: 'Добрі умови',
    staffCause: 'З вини співробітника',
    staffConductIds: "ID рядків реєстру, пов'язаних із порушеннями (лише при відході з вини співробітника)",
    staffSave: 'Зберегти зміни співробітника',
    staffManagerAssignment: 'Датоване призначення керівника',
    staffManagerRecorded: 'Датоване призначення керівника записано.',
    staffPlanRevisions: 'Редакції плану',
    staffNoPlans: 'Редакцій плану немає',
    staffNoPlansBody: 'Запишіть датовану пропозицію, яку цей співробітник має прийняти.',
    staffRevision: 'Редакція',
    staffEffectiveFrom: 'Діє з',
    staffEffectiveTo: 'Діє по',
    staffOngoing: 'Безстроково',
    staffAssignPartner: 'Призначити партнера',
    staffAssignIntro: 'Запишіть нове призначення з причиною. Попередня історія призначень зберігається.',
    staffAssignmentRecorded: 'Призначення записано.',
    staffUnassigned: 'Не призначено',
    staffStandardPlan: 'Стандартний план для майбутніх представників',
    staffSiteClaims: 'Закріплення майданчиків',
    staffClaimsIntro: 'Наявні майданчики поза продажами потребують схвалення. Поки закріплення очікує перевірки, доступ до портфеля та комісія не надаються.',
    staffNoClaims: 'Закріплень майданчиків немає',
    staffNoClaimsBody: "Тут з'являться зареєстровані майданчики продажів для перевірки.",
    staffRegistrant: 'Заявник',
    staffExisting: 'Уже існує ({kind}): {id}',
    staffReviewRecorded: 'Перевірку записано.',
    staffReviewFailed: 'Не вдалося записати перевірку.',
    staffDecision: 'Рішення',
    staffReject: 'Відхилити',
    staffApprove: 'Схвалити',
    staffRecordDecision: 'Записати рішення',
    staffReassignRecorded: 'Перепризначення записано.',
    staffReassign: 'Перепризначити закріплення',
    staffReassignFailed: 'Не вдалося перепризначити.',
    staffRegistrantKind: 'Вид нового заявника',
    // Plan revision form
    planNoMonthLimit: 'Без обмеження за місяцями',
    planStoredStandard: 'Збережена стандартна редакція',
    planNew: 'Нова редакція плану',
    planPrefilled: 'Заповнено за збереженою стандартною редакцією {revision}. Прийняті плани та наявні позначки станцій не змінюються.',
    planPrefilledCode: 'Заповнено за чинним стандартом, редакція {revision}, ще не записана. Прийняті плани та наявні позначки станцій не змінюються.',
    planLoading: 'завантаження',
    planLoadFailed: 'Не вдалося завантажити збережений стандарт. Записати редакцію неможливо.',
    planStage: 'Етап плану',
    planStage1: 'Комісія етапу 1',
    planStage2: 'Ставки зарплатної відомості етапу 2',
    planNoRates: 'Для цього етапу немає збережених ставок.',
    planCentsReadback: 'Тобто {amount}',
    planRecorded: 'Редакцію записано. Персональна редакція потребує прийняття.',
    planFailed: 'Не вдалося записати редакцію.',
    planDatedRevision: 'Датована редакція плану або ролі',
    planEmployeeMove: 'Датований перехід у штат і на зарплатну відомість',
    planRoleOnRevision: 'Роль у цій редакції',
    planNotice: 'Номер письмового повідомлення',
    planRecord: 'Записати нову редакцію',
  },
  hy: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Գրանցված չէ',
    notSet: 'Սահմանված չէ',
    noResults: 'Արդյունքներ չկան',
    person: 'Աշխատակից',
    status: 'Կարգավիճակ',
    reason: 'Պատճառ',
    partner: 'Գործընկեր',
    partners: 'Գործընկերներ',
    chooseAPerson: 'Ընտրեք աշխատակցին',
    chooseAPartner: 'Ընտրեք գործընկերոջը',
    nextStep: 'Հաջորդ քայլ',
    ledgerMonth: 'Գրանցամատյանի ամիս',
    allMonths: 'Բոլոր ամիսները',
    manager: 'Ղեկավար',
    noManager: 'Առանց ղեկավարի',
    rep: 'Ներկայացուցիչ',
    roleManager: 'Ղեկավար՝ ներկայացուցչի սեփական պորտֆելով',
    territory: 'Տարածք',
    startDate: 'Սկզբի ամսաթիվ',
    effectiveDate: 'Ուժի մեջ մտնելու ամսաթիվ',
    action: 'Գործողություն',
    period: 'Ժամանակահատված',
    currency: 'Արժույթ',
    location: 'Կայք',
    name: 'Անուն',
    email: 'Էլ. փոստ',
    paymentMethod: 'Վճարման եղանակ',
    stripeConnect: 'Stripe Connect',
    payroll: 'Աշխատավարձ',
    payrollEligibility: 'Աշխատավարձի անցնելու իրավունք',
    activePartners: 'Ակտիվ գործընկերներ',
    marginShare: 'Մարժայի բաժին',
    heldUnderCap: 'Պահված՝ սահմանաչափի պատճառով',
    earned: 'Վաստակած',
    paymentsAndPayroll: 'Վճարումներ և աշխատավարձ',
    salesStaff: 'Վաճառքի աշխատակից',
    salesPartner: 'Վաճառքի Գործընկեր',
    recording: 'Գրանցվում է...',
    recordAssignment: 'Գրանցել նշանակումը',
    assignmentFailed: 'Նշանակումը չհաջողվեց։',
    pointOne: '{n} միավոր',
    pointOther: '{n} միավոր',
    statusActive: 'ակտիվ',
    statusInactive: 'ոչ ակտիվ',
    statusQualified: 'որակավորված',
    statusNotQualified: 'չորակավորված',
    statusAccepted: 'ընդունված',
    statusAwaitingAcceptance: 'սպասում է ընդունման',
    statusAssigned: 'նշանակված',
    statusOffline: 'անցանց',
    statusNotStarted: 'չսկսված',
    statusPending: 'սպասման մեջ',
    statusNoSubmission60: '60 օրում հայտ չկա',
    statusBelowPace: '6 ամսվա տեմպից ցածր',
    statusOrderFollowUp: 'Անհրաժեշտ է պատվերի հետևում',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Վաճառքի թիմ',
    teamSubtitle: 'Ձեր ցանցը, թիմի ակտիվությունը և գրանցված եկամուտը։',
    teamOpenPerson: 'Բացել աշխատակցի թիմը',
    teamLoading: 'Ձեր թիմը բեռնվում է',
    teamChoose: 'Ընտրեք թիմ',
    teamEmptyBody: 'Ձեզ հասանելի թիմի և պորտֆելի տվյալները հայտնվում են այստեղ։',
    tabMap: 'Քարտեզ',
    tabTeam: 'Թիմ',
    tabReports: 'Հաշվետվություններ',
    tabScoreboard: 'Վարկանիշ',
    teamMyBook: 'Իմ պորտֆելը',
    teamWholeTeam: 'Ամբողջ թիմը',
    teamNetworkMap: 'Ցանցի քարտեզ',
    teamEveryone: 'Բոլորը ձեր շրջանակում',
    teamStage: 'Փուլ',
    teamAllStages: 'Բոլոր փուլերը',
    teamChargerType: 'Լիցքավորիչի տեսակ',
    teamAllTypes: 'Բոլոր տեսակները',
    teamState: 'Նահանգ',
    teamAllStates: 'Բոլոր նահանգները',
    teamLaunchedFrom: 'Գործարկված՝ սկսած',
    teamLaunchedThrough: 'Գործարկված՝ մինչև',
    teamPinLegend: 'Նշիչի գույնը ցույց է տալիս աշխատակցին։ Լցված շրջանը նշանակում է առցանց, դատարկ շրջանը՝ անցանց, իսկ բացականչական նշանը՝ անսարքություն։ Քարտեզի կոորդինատներ չունեցող կայքեր՝ {n}։',
    teamCloseDetails: 'Փակել կայքի մանրամասները',
    teamCity: 'Քաղաք',
    teamChargers: 'Լիցքավորիչներ',
    teamDirectDeal: 'Ուղիղ գործարք',
    teamLaunchDate: 'Գործարկման ամսաթիվ',
    teamNotLaunched: 'Գործարկված չէ',
    teamSessions30: 'Լիցքավորումներ, վերջին 30 օր',
    teamKwh30: 'կՎտ·ժ, վերջին 30 օր',
    teamNoSites: 'Համապատասխան կայքեր չկան',
    teamNoSitesBody: 'Փոխեք զտիչները՝ ձեր պորտֆելի այլ կայքերը տեսնելու համար։',
    teamSite: 'Կայք',
    teamDirectReports: 'Անմիջական ենթականեր',
    teamNoDirectReports: 'Անմիջական ենթականեր չկան',
    teamNoDirectReportsBody: 'Ամսաթվով նշանակումները կհայտնվեն այստեղ գրանցվելուց հետո։',
    teamActivity: 'Ակտիվություն',
    teamEarnedMonth: 'Վաստակած այս ամիս',
    teamActivatedMonth: 'Ակտիվացված այս ամիս',
    teamLaunchedMonth: 'Գործարկված այս ամիս',
    teamPace: 'Տեմպ',
    teamOnPace: 'Տեմպի մեջ է',
    teamEligibility: '{active}/{target} ակտիվ՝ {months}/{required} ամիս։',
    teamUsdAverage: 'Վեց ամսվա միջինը USD-ով՝ {amount}',
    teamNotAvailable: 'Հասանելի չէ',
    teamScoreboard: 'Թիմի վարկանիշ',
    teamThisWeek: 'Այս շաբաթ',
    teamThisMonth: 'Այս ամիս',
    teamScoreNote: 'Միայն քանակներ։ Դասակարգումը կատարվում է որակավորվող գործարկումներով, ապա ակտիվ գործընկերներով և ակտիվացումներով։ Լիդերը, ցուցադրությունները և դիմումներն օգտագործում են ձեր աուդիտված ակտիվության գրառումները։ Զանգերն ու զրույցներն օգտագործում են գործընկերների նշումները։ Գնահատականներ չեն ավելացվում։',
    teamOutreach: 'Ներգրավում',
    teamLaunches: 'Գործարկումներ',
    scoreLeadsStarted: 'սկսված լիդեր',
    scoreCalls: 'զանգեր',
    scoreConversations: 'զրույցներ',
    scoreDemos: 'ցուցադրություններ',
    scoreApplications: 'դիմումներ',
    scoreApproved: 'հաստատված',
    scoreFirstPaidOrders: 'առաջին վճարված պատվերներ',
    scoreActivated: 'ակտիվացված',
    scoreActivePartners: 'ակտիվ գործընկերներ',
    scoreQualifyingLaunches: 'որակավորվող գործարկումներ',
    reportMonthly: 'Միջնորդավճար ըստ ամիսների և կատեգորիաների',
    reportSessions: 'Լիցքավորման միավորներ ըստ լիցքավորիչների',
    reportYearEnd: 'Տարեվերջյան ընդհանուր գումարներ',
    reportsTitle: 'Իմ հաշվետվությունները',
    reportYear: 'Տարի',
    reportKind: 'Հաշվետվություն',
    reportDownloadFailed: 'Չհաջողվեց ներբեռնել հաշվետվությունը։',
    reportExport: 'Արտահանել CSV',
    reportEmpty: 'Գրանցված ակտիվություն չկա',
    reportEmptyBody: 'Գրանցված եկամուտները և վճարման ապացույցները կհայտնվեն այստեղ։',
    reportNote: 'Գրանցամատյանի արժույթները պահվում են առանձին։ Տարեվերջյան գումարներն օգնում են հայտարարագրերի պատրաստմանը և հարկային ձև չեն։',
    activityRecorded: 'Ակտիվությունը գրանցվեց։',
    activityFailed: 'Չհաջողվեց գրանցել ակտիվությունը։',
    activityLabel: 'Գրանցել իմ ակտիվությունը',
    activityLead: 'Նոր լիդ է սկսվել',
    activityDemo: 'Ցուցադրություն',
    activityApplication: 'Դիմում',
    activityEvidence: 'Ապացույցի հղում',
    activityPlaceholder: 'Ձեր ակտիվության հղումը',
    activityRecord: 'Գրանցել ակտիվությունը',
    // Sales payout run
    payTitle: 'Վճարումների իրականացում',
    paySubtitle: 'Դիտեք փակված քաղվածքները, ապա հաստատեք վճարումները։ Ավտոմատ վճարումներն անջատված են մինչև հաստատումը։',
    payCycle: 'Ամսական ցիկլ',
    payCycleBody: 'Քաղվածքները փակվում են ամսի 1-ին։ Ստուգումը կատարվում է 1-ից 4-ը։ Վճարումը կատարվում է 5-ին կամ հաջորդ աշխատանքային օրը։ Յուրաքանչյուր արժույթ վճարվում է առանձին։ Չպատրաստված հաշիվները և ձախողված փոխանցումները մնում են վճարման ենթակա։',
    payStatementMonth: 'Քաղվածքի ամիս',
    payClose: 'Փակել քաղվածքները',
    payPay: 'Վճարել և արտահանել աշխատավարձը',
    paySync: 'Համաժամացնել եկամուտները',
    paySynced: 'Եկամուտներն ու հետադարձումները համաժամացվեցին։',
    payPreview: 'Միայն նախադիտում',
    payRequestFailed: 'Հարցումը ձախողվեց։',
    payNewLines: 'Գրանցամատյանի նոր տողեր՝ {n}։ Անհրաժեշտ է քաղվածքի ստուգում։',
    payDate: 'Վճարման ամսաթիվ՝ {date}։',
    payMayProceed: 'Վճարումները կարող են շարունակվել։',
    payWait: 'Վճարումները սպասում են այդ ամսաթվին։',
    payNothing: 'Մշակելու բան չկա',
    payNothingBody: 'Այս ամսվա համար քաղվածքի կամ վճարման մնացորդ հասանելի չէ։',
    payLedgerHeld: 'Գրանցամատյան և պահված',
    payPayment: 'Վճարում',
    payReview: 'Ստուգում',
    payHeld: 'Պահված՝ {amount}',
    payConversion: 'Փոխարկման ծախս՝ {amount}',
    payReady: 'Պատրաստ է',
    payPrepareQuote: 'Պատրաստել փոխարկման գնանշում',
    payQuotePrepared: 'Գնանշումը պատրաստ է։ Գործարկեք նոր նախադիտում։',
    payConfirmCheck: 'Ես ստուգել եմ այս գումարները, արժույթները և արգելափակված ստացողներին։',
    payConfirmClose: 'Հաստատել քաղվածքների փակումը',
    payConfirmPay: 'Հաստատել վճարումների իրականացումը',
    payRunRecorded: 'Իրականացումը գրանցվեց՝ {result}',
    payPayroll: 'Աշխատավարձի արտահանում և համադրում',
    payDownload: 'Ներբեռնել գրանցված աշխատավարձի CSV-ն',
    payPaymentId: 'Վճարման կամ արտահանման ID',
    payProviderRef: 'Մատակարարի հղում',
    payRecordPayroll: 'Գրանցել ավարտված աշխատավարձը',
    payPayrollRecorded: 'Աշխատավարձի վճարումը գրանցվեց։',
    payActualCost: 'Փոխարկման փաստացի ծախս',
    payActualBody: 'Գրանցեք մատակարարի վերջնական փոխարկման ծախսը գրանցամատյանի արժույթով։ Գնանշումից տարբերությունը դառնում է ճշգրտում ընթացիկ բաց քաղվածքում։',
    payLedgerCurrency: 'Գրանցամատյանի արժույթ',
    payActualCents: 'Փաստացի ծախսը ցենտերով',
    payRecordActual: 'Գրանցել փաստացի ծախսը',
    payActualReconciled: 'Փոխարկման փաստացի ծախսը համադրվեց։',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Ձեր պորտֆելի գործընկերների ակտիվությունը, կայքերը և հաջորդ քայլերը։',
    bookLoadingPartners: 'Ձեր գործընկերները բեռնվում են...',
    bookQualifyingChargers: 'Որակավորվող լիցքավորիչներ',
    bookLaunches12: 'Գործարկումներ 12 ամսում',
    bookActivityTarget: 'Ակտիվության նպատակ՝ 8',
    bookLaunches6: 'Գործարկումներ 6 ամսում',
    bookPaceTarget: 'Տեմպի նպատակ՝ 4',
    bookLocationsSubmissions: 'Կայքեր / հայտեր',
    bookLastContact: 'Վերջին կապ',
    bookLocationsHeading: 'Կայքեր և հայտեր',
    bookNoLocations: 'Կայքեր դեռ չկան',
    bookNoLocationsBody: 'Այս գործընկերոջ ներկայացրած կայքերը կհայտնվեն այստեղ։',
    bookNotes: 'Նշումներ և հաջորդ քայլեր',
    bookNoPartners: 'Ձեր պորտֆելում գործընկերներ չկան',
    bookNoPartnersBody: 'Նշանակված գործընկերները և նրանց կայքերը կհայտնվեն այստեղ։',
    bookLoadingSubmission: 'Հայտը բեռնվում է...',
    bookLocationType: 'Կայքի տեսակ',
    bookAddress: 'Հասցե',
    bookContact: 'Կոնտակտ',
    bookConnectivity: 'Կապ',
    bookCommissioning: 'Շահագործման հանձնումը թույլատրված է',
    bookAwaitingReview: 'Սպասում է EVI-ի ստուգմանը',
    bookInstallerExpires: 'Տեղադրողի մուտքի ժամկետ',
    bookNotIssued: 'Տրված չէ',
    bookLaunch: 'Գործարկում',
    bookAwaitingApproval: 'Սպասում է EVI-ի հաստատմանը',
    bookOrdersTitle: 'Պատվերներ',
    bookOrdersSubtitle: 'Ձեզ և ձեր պորտֆելի գործընկերներին վերագրված հաճախորդների պատվերները։',
    bookLoadingOrders: 'Պատվերները բեռնվում են...',
    bookOrderDate: 'Պատվերի ամսաթիվ',
    bookDirectStaffOrder: 'Աշխատակցի ուղիղ պատվեր',
    bookOrderTotal: 'Պատվերի ընդհանուր գումար',
    bookQty: 'Քանակ {n}',
    bookRegisterDeal: 'Գրանցել այս ուղիղ գործարքը',
    bookClaimRecorded: 'Կայքի ամրագրումը գրանցվեց։ Գոյություն ունեցող ոչ վաճառքային կայքերը պահանջում են գերադմինիստրատորի ստուգում։',
    bookClaimFailed: 'Չհաջողվեց գրանցել կայքի ամրագրումը։',
    bookDealEvidence: 'Ուղիղ գործարքի ապացույց',
    bookRegisterButton: 'Գրանցել ուղիղ գործարքը',
    bookNoOrders: 'Պատվերներ դեռ չկան',
    bookNoOrdersBody: 'Ձեր ուղիղ գործարքների և գործընկերների պորտֆելի պատվերները կհայտնվեն այստեղ։',
    bookNewestOrders: 'Նորագույն պատվերներ',
    bookOlderOrders: 'Ավելի հին պատվերներ',
    bookOnboardingTitle: 'Միացման օգնություն',
    bookOnboardingSubtitle: 'Օգնեք ձեր պորտֆելի կայքերի մարդկանց ստանալ անհրաժեշտ մուտքը։',
    bookInvite: 'Հրավիրել օգտատիրոջ',
    bookInviteBody: 'Ուղարկեք կամ կրկին ուղարկեք հրավեր։ Առաքումը կատարվում է գործող փոստային քաղաքականությամբ։',
    bookNoInviteLocations: 'Հրավերի համար կայքեր դեռ չկան',
    bookNoInviteLocationsBody: 'Կայքը պետք է լինի ձեր պորտֆելում, որպեսզի կարողանաք հրավիրել դրա օգտատերերին։',
    bookInviteRecorded: 'Հրավերը գրանցվեց։ Առաքումը կատարվում է գործող փոստային քաղաքականությամբ։',
    bookInviteFailed: 'Չհաջողվեց ուղարկել հրավերը։',
    bookChooseLocation: 'Ընտրեք կայք',
    bookAccess: 'Մուտք',
    roleBuildingAdmin: 'Շենքի ադմինիստրատոր',
    rolePropertyManager: 'Գույքի կառավարիչ',
    roleDriver: 'Վարորդ',
    bookInviting: 'Հրավերը գրանցվում է...',
    bookSendInvite: 'Ուղարկել կամ կրկին ուղարկել հրավերը',
    // Partner and direct site notes
    notesHeading: 'Զանգեր, այցեր և նամակներ',
    notesIntro: 'Գրանցեք յուրաքանչյուր շփում նույն օրը։ Նշումները պատկանում են EVI-ին։ Ուղղումն ավելացնում է նոր տարբերակ և պահպանում բնօրինակը։',
    notesEmpty: 'Նշումներ դեռ չկան',
    notesEmptyBody: 'Ստորև գրանցեք առաջին կապը և հստակ հաջորդ քայլը։',
    notesVersion: 'Տարբերակ {n}',
    notesDue: 'Ժամկետ',
    notesAddCorrection: 'Ավելացնել ուղղված տարբերակ',
    notesLinksFailed: 'Չհաջողվեց բեռնել կապված կայքերն ու հայտերը։',
    notesRecorded: 'Նշումը գրանցվեց։',
    notesFailed: 'Չհաջողվեց գրանցել նշումը։',
    notesChannel: 'Ալիք',
    notesCall: 'Զանգ',
    notesVisit: 'Այց',
    notesEmailChannel: 'Նամակ',
    notesProjectStage: 'Նախագծի փուլ',
    notesNote: 'Նշում',
    notesNextDue: 'Հաջորդ քայլի ժամկետ',
    notesLocationOptional: 'Կայք (ըստ ցանկության)',
    notesNoLocation: 'Կապված կայք չկա',
    notesSubmissionOptional: 'Հայտ (ըստ ցանկության)',
    notesNoSubmission: 'Կապված հայտ չկա',
    notesRecordVersion: 'Գրանցել նոր տարբերակ',
    notesRecordNote: 'Գրանցել նշումը',
    notesCancel: 'Չեղարկել ուղղումը',
    notesSiteTitle: 'Ուղիղ կայքերի նշումներ',
    notesNoClaims: 'Ուղիղ կայքերի ամրագրումներ չկան',
    notesNoClaimsBody: 'Ձեր ուղիղ կայքերի ամրագրումները և դրանց կապի պատմությունը կհայտնվեն այստեղ։',
    notesStaffTitle: 'Գործընկերների նշումներ և հաջորդ քայլեր',
    notesStaffIntro: 'Գրանցեք նշումներ ցանկացած գործընկերոջ համար, ներառյալ EVI-ի սեփական գործընկերները։ Նշումները մնում են գործընկերոջ հետ, երբ նրան նշանակում են ներկայացուցչին։',
    notesFindPartner: 'Գտնել գործընկեր',
    notesHouseBook: ' (EVI-ի սեփական պորտֆել)',
    notesFirstPartners: 'Առաջին գործընկերները',
    notesMorePartners: 'Այլ գործընկերներ',
    notesChooseBody: 'Ընտրեք գործընկեր՝ կապի պատմությունը դիտելու կամ գրանցելու համար։',
    // My commission and Sales statements
    commPlanRevision: 'Միջնորդավճարի պլան, խմբագրություն {n}',
    commOfferRevision: 'Ձեր առաջարկը, խմբագրություն {n}',
    commEffective: 'Գործում է {date}-ից',
    commThrough: 'մինչև {date}',
    commAccepted: 'Ընդունված է {date}։ Ձեր ընդունած պլանը ցուցադրվում է միայն ընթերցման համար։',
    commDirectMargin: 'Սարքավորումների ուղիղ մարժա',
    commAboveThreshold: 'Տարեկան շեմից բարձր',
    commPartnerPoints: 'Գործընկերների լիցքավորման միավորներ',
    commFleetShare: 'Ավտոպարկի բաժանորդագրությունների բաժին',
    commFleetMeta: '{n} ամիս, միայն վերագրված ավտոպարկի պատվերներ',
    commFleetEvery: 'Յուրաքանչյուր երկարաձգում թիմում լինելու ընթացքում, միայն ավտոպարկի պատվերներ',
    commAnnualThreshold: 'Սարքավորումների մարժայի տարեկան շեմ ({currency})',
    commFirstOrder: 'Առաջին որակավորվող վճարված պատվեր',
    commFourthCharger: 'Չորրորդ որակավորվող լիցքավորիչ',
    commBookDuration: 'Գործընկերների պորտֆելի մարժայի տևողություն',
    commNoMonthLimit: 'Առանց ամիսների սահմանափակման նշանակման ընթացքում',
    commMonthsPerPartner: '{n} ամիս յուրաքանչյուր գործընկերոջ համար',
    commContinuation: 'Շարունակություն՝ լավ պայմաններով հեռանալուց հետո',
    commMonths: '{n} ամիս',
    commOwnLadder: 'Սեփական ուղիղ լիցքավորիչների սանդղակ',
    commFromCharger: 'Սկսած որակավորվող դրոշմված լիցքավորիչից',
    commSessionPoints: 'Լիցքավորման միավորներ',
    commLadderNote: 'Հաշվեգրումը սկսվում է չորրորդ որակավորվող ուղիղ լիցքավորիչից։ Առաջին 12 ամիսը կայացման տարի է։ Դրանից հետո նոր գործարկմանն անհրաժեշտ է 8 որակավորվող գործարկում վերջին 12 ամսում՝ ներառյալ ինքը։ Չդրոշմված գործարկումները հաշվվում են ակտիվության մեջ, բայց հետագայում երբեք դրոշմ չեն ստանում։ Գոյություն ունեցող դրոշմները շարունակում են վճարվել, քանի դեռ թիմում եք։',
    commBookTiers: 'Գործընկերների պորտֆելի մարժայի մակարդակներ',
    commActiveAtLeast: 'Ակտիվ գործընկերներ, առնվազն',
    commPointsNote: 'Գործընկերների լիցքավորման միավորները կիրառվում են գործընկերոջ միջնորդավճարից հետո։ Աշխատակիցների լիցքավորման գումարները սահմանափակված են EVI-ի հասանելի մնացորդով։ Ավտոպարկի բաժանորդագրությունների բաժինը սկսվում է, երբ բաժանորդագրությունների հաշվարկը ակտիվ է։ Այլ բաժանորդագրությունները բաժին չեն բերում։',
    commGolden: 'Վճարված Golden բոնուս՝ {amount}։',
    commOverrideTiers: 'Ղեկավարի թիմային միջնորդավճարի մակարդակներ',
    commTeamActiveAtLeast: 'Թիմի ակտիվ գործընկերներ, առնվազն',
    commGrowth: 'Ղեկավարի աճի բոնուսներ՝ {six} վեց ակտիվ գործընկերի դեպքում և {payroll} ենթակայի աշխատավարձի շեմին հասնելիս։ Թիմի լիցքավորման միավորներ՝ {points}։',
    commAccepting: 'Ընդունվում է...',
    commAccept: 'Ընդունել այս միջնորդավճարի պլանը',
    commOfferAccepted: 'Առաջարկն ընդունվեց։',
    commOfferFailed: 'Չհաջողվեց ընդունել առաջարկը։',
    commStage2: '2-րդ փուլի տեղեկություններ',
    commStage2Body: 'Գրանցված միջնորդավճարի վեց ավարտված ամիս՝ հետադարձումներից հետո։ Սա ձեզ չի տեղափոխում 2-րդ փուլ։ Գերադմինիստրատորը պետք է գրանցի պլանի հաստատված խմբագրություն։',
    commConsecutive: '{n}/{required} անընդմեջ ամիս',
    commCompletedMonth: 'Ավարտված ամիս',
    commRecordedCommission: 'Գրանցված միջնորդավճար ({currency})',
    commNoMonths: 'Պլանի ավարտված ամիսներ դեռ չկան',
    commNoMonthsBody: 'Այստեղ հայտնվում են միայն ձեր պլանի ուժի մեջ մտնելու ամսաթվից ավարտված ամիսները։',
    commMonthsAvailable: 'Հասանելի է 6 ավարտված ամսից {n}-ը։',
    commAverage: 'Միջին՝ {amount}',
    commAgainst: 'ընդդեմ {amount}',
    commNoCrossCurrency: ', առանց արժույթների միջև համեմատության',
    commStatements: 'Ամսական քաղվածքներ',
    commNoStatements: 'Քաղվածքներ դեռ չկան',
    commNoStatementsBody: 'Ձեր առաջին քաղվածքը կհայտնվի միջնորդավճարի ամիսը փակվելուց հետո։',
    commOpening: 'Սկզբնական մնացորդ',
    commReversals: 'Հետադարձումներ',
    commPaid: 'Վճարված',
    commClosing: 'Վերջնական մնացորդ',
    commNoPayments: 'Վճարումներ դեռ չկան',
    commNoPaymentsBody: 'Connect վճարումները կամ աշխատավարձի գրառումները կհայտնվեն եկամուտները վճարվելուց հետո։',
    commLedgerAmount: 'Գրանցված գումար',
    commMonth: 'Ամիս',
    commStripeTransfer: 'Stripe փոխանցում',
    commNotSent: 'Ուղարկված չէ',
    commConversionCost: 'Փոխարկման ծախս',
    commCashAmount: 'Վճարման գումար',
    commSentAs: 'Ուղարկված է որպես {amount}',
    commReservedAs: 'Ամրագրված է որպես {amount}',
    commLines: 'Միջնորդավճարի տողեր',
    commNoLines: 'Միջնորդավճարի տողեր դեռ չկան',
    commNoLinesBody: 'Որակավորվող ակտիվությունն այստեղ ստեղծում է միջնորդավճարի տողեր, իսկ հետագա ուղղումները ցուցադրվում են առանձին։',
    commCategory: 'Կատեգորիա',
    commBasis: 'Հիմք',
    commPlainTerms: "Ինչպես կարդալ․ հիմքը այն գումարն է, որին կիրառվում է ձեր դրույքը (սարքավորման պատվերի դեպքում՝ EVI-ի մարժան)։ Պահված է նշանակում վաստակված, բայց դեռ չվճարված․ EVI-ը գործընկերից ստացված սարքավորման մարժայից վճարում է ձեր վաստակը, առավելագույնը կեսը, իսկ մնացածը վճարվում է, երբ գործընկերոջ հաջորդ պատվերները ավելին են բերում։ 2-րդ փուլը EVI-ի հիմնական աշխատավարձով ծրագիրն է․ վեց ամբողջական ամսվա միջնորդավճարը դիտարկման շեմն է, իսկ անցումը նոր ծրագիր է, որը EVI-ը համաձայնեցնում է ձեզ հետ։",
    commRate: 'Դրույք',
    commCommission: 'Միջնորդավճար',
    commPlanRevisionHeader: 'Պլանի խմբագրություն',
    commTitle: 'Իմ միջնորդավճարը',
    commSubtitle: 'Ձեր առաջարկը, միջնորդավճարի ակտիվությունը և վճարումների գրառումները։ CAD-ը և USD-ն պահվում են առանձին։',
    commNoOffer: 'Առաջարկ դեռ գրանցված չէ',
    commNoOfferBody: 'EVI-ն կգրանցի միջնորդավճարի պլան, որպեսզի դուք այն դիտեք և ընդունեք։',
    commClosedNote: 'Փակված քաղվածքները չեն փոխվում։ Ուղղումները հայտնվում են որպես նոր տողեր։',
    commLoading: 'Միջնորդավճարը բեռնվում է...',
    commNewestLines: 'Նորագույն տողեր',
    commOlderLines: 'Ավելի հին տողեր',
    stmtTitle: 'Վաճառքի քաղվածքներ',
    stmtSubtitle: 'Դիտեք ձեր վաճառքի թիմի գրանցված միջնորդավճարները, քաղվածքները, վճարումները և աշխատավարձը։',
    stmtNote: 'Օգտագործեք ստուգված սկրիպտները՝ ամիսը հաշվեգրելու և փակելու, Connect կապալառուներին վճարելու կամ աշխատավարձն արտահանելու համար։ Այս էջը երբեք չի սկսում գանձում կամ փոխանցում։',
    stmtChooseBody: 'Ընտրեք աշխատակցի՝ նրա միջնորդավճարի գրանցամատյանը և քաղվածքները դիտելու համար։',
    stmtReviewQueue: 'Ստուգման հերթ',
    stmtNothingReview: 'Ստուգման սպասող բան չկա',
    stmtNothingReviewBody: 'Հաշվարկի ստուգման կետերը կհայտնվեն այստեղ, երբ ապացույցը ուշադրության կարիք ունենա։',
    stmtSource: 'Աղբյուր',
    stmtCostMissing: 'Ինքնարժեքը գրանցված չէ',
    stmtCostIntro: 'Միայն ադմինիստրատորներին հասանելի ձեռքով ապացույցները պահվում են առանձին։ Պատվերի սկզբնական պատկերը չի փոխվում։',
    stmtNoMissing: 'Ինքնարժեքի բացակայող ապացույցներ չկան',
    stmtNoMissingBody: 'Ինքնարժեքի պատկեր չունեցող վերագրված վճարված պատվերները կհայտնվեն այստեղ։',
    stmtCostRecorded: 'Ինքնարժեքի ապացույցը գրանցվեց։',
    stmtCostFailed: 'Չհաջողվեց պահպանել ինքնարժեքի ապացույցը։',
    stmtChooseCurrency: 'Ընտրեք արժույթ',
    stmtUnits: '{name}՝ {n} հատ',
    stmtUnitCost: 'Մեկ հատի ինքնարժեք',
    stmtAutelFee: 'Autel-ի կլանված վճարը մեկ հատի համար',
    stmtEvidenceReason: 'Ապացույց և պատճառ',
    stmtRecordEvidence: 'Գրանցել ձեռքով ապացույց',
    stmtCorrection: 'Ավելացնել ուղղում',
    stmtCorrectionIntro: 'Հետադարձումները բացասական են։ Վերականգնումները չեն կարող գերազանցել սկզբնական իրավունքը։ Կրկնակի փորձի համար օգտագործեք անփոփոխ հղում։',
    stmtNoLinesToCorrect: 'Ուղղելու տողեր չկան',
    stmtNoLinesToCorrectBody: 'Ուղղում ավելացնելու համար անհրաժեշտ է միջնորդավճարի դրական տող։',
    stmtCorrectionRecorded: 'Ուղղումը գրանցվեց։',
    stmtCorrectionFailed: 'Ուղղումը ձախողվեց։',
    stmtOriginalLine: 'Սկզբնական տող',
    stmtSignedAmount: 'Նշանով գումար',
    stmtReference: 'Հղում',
    stmtAppend: 'Ավելացնել ուղղումը',
    // Sales Staff
    staffTitle: 'Վաճառքի աշխատակիցներ',
    staffSubtitle: 'Կառավարեք աշխատակիցներին, ամսաթվով միջնորդավճարի պլանները և գործընկերների պորտֆելները։ Փոփոխություններն անցնում են աուդիտ։',
    staffNote: 'Միայն գերադմինիստրատորը կարող է փոխել աշխատակիցներին, պլանները կամ գործընկերների նշանակումները։',
    staffPeople: 'Աշխատակիցներ',
    staffActive: 'Ակտիվ',
    staffContractors: 'Կապալառուներ',
    staffNoStaff: 'Վաճառքի աշխատակիցներ դեռ չկան',
    staffNoStaffBody: 'Ստորև ավելացրեք գործող ակտիվ հաշիվ ունեցող անձի։',
    staffKind: 'Տեսակ',
    staffRole: 'Դեր',
    staffConnect: 'Connect',
    staffEligible: 'Իրավասու',
    staffNotEligible: 'Դեռ իրավասու չէ',
    staffEligibility: '{status}՝ {active}/{target} ակտիվ, {months}/{required} ամիս',
    staffAdd: 'Ավելացնել աշխատակից',
    staffCreated: 'Աշխատակցի գրառումը ստեղծվեց։',
    staffAddFailed: 'Չհաջողվեց ավելացնել աշխատակցին։',
    staffInviteSent: 'Հրավերն ուղարկված է։ Աշխատակցի գրառումը այս դերով, ղեկավարով և մեկնարկի ամսաթվով կստեղծվի, երբ հրավերն ընդունվի։',
    staffInviteNeedsName: 'Այս էլ. հասցեն դեռ հաշիվ չունի, ուստի կուղարկվի հրավեր։ Նախ ավելացրեք անունը։',
    staffInvitesTitle: 'Հրավիրված, դեռ չընդունված',
    staffInvitedOn: 'Հրավիրվել է {date}',
    staffInviteFailed: 'Չստեղծվեց ընդունելիս՝ {reason}',
    staffResendInvite: 'Կրկին ուղարկել հրավերը',
    staffInviteResent: 'Հրավերը կրկին ուղարկվեց։',
    staffCancelInvite: 'Չեղարկել հրավերը',
    staffInviteCancelled: 'Հրավերը չեղարկվեց։',
    staffInviteActionFailed: 'Չհաջողվեց փոխել հրավերը։',
    inviteLinkAction: "Պատճենել հրավերի հղումը",
    inviteLinkTitle: "Պատճենել հրավերի հղումը",
    inviteLinkField: "Հրավերի հղում",
    inviteLinkLoading: "Ստեղծվում է նոր հղում...",
    inviteLinkCopy: "Պատճենել",
    inviteLinkCopied: "Պատճենված է",
    inviteLinkSelected: "Հղումը նշված է։ Սեղմեք Ctrl+C, կամ Mac-ում Cmd+C, պատճենելու համար։",
    inviteLinkEmail: "Ուղարկել իմ փոստից",
    inviteLinkReplaces: "Այս հղումը փոխարինում է նախորդ հրավերի նամակների հղմանը։ Այն աշխատում է մեկ անգամ և ժամկետանց է դառնում {date}։",
    inviteLinkDiscard: "Այս պատուհանը փակելիս հղումը կորչում է։ Այն այլևս չի ցուցադրվի։",
    inviteLinkExpired: "Այս հրավերի ժամկետն անցել է։ Կրկին ուղարկեք այն՝ նորը ստեղծելու համար, ապա պատճենեք այդ հղումը։",
    inviteLinkFailed: "Չհաջողվեց ստեղծել հրավերի հղումը։",
    inviteLinkClose: "Փակել",
    inviteLinkMailSubject: "Ձեր հրավերը EV Initiative-ին",
    inviteLinkMailGreeting: "Բարև, {name},",
    inviteLinkMailGreetingNoName: "Բարև,",
    inviteLinkMailBody: "Ուղարկում եմ ձեր EV Initiative հրավերը իմ սեփական փոստից, եթե նախորդը ձեզ չի հասել։ Ստորև նշված հղումը աշխատում է մեկ անգամ և ժամկետանց է դառնում {date}։",
    inviteLinkMailSignoff: "Շնորհակալություն,",
    inviteLinkSignInPage: "Այս անձն արդեն ունի EV Initiative հաշիվ այս էլ. փոստով, ուստի այս հղումը բացում է մուտքի էջը։ Այդ հաշվով մուտք գործելն ընդունում է հրավերը։",
    inviteLinkMailSignInPage: "Ուղարկում եմ ձեր EV Initiative հրավերը իմ սեփական փոստից, եթե նախորդը ձեզ չի հասել։ Դուք արդեն ունեք EV Initiative հաշիվ այս էլ. փոստով, ուստի հրավերն ընդունելու համար մուտք գործեք այդ հաշվով ստորև նշված հղումով։",
    staffAccountEmail: 'Հաշվի էլ. փոստ',
    staffStaffRole: 'Աշխատակցի դեր',
    staffContractor: 'Կապալառու',
    staffEmployee: 'Աշխատող',
    staffPayThrough: 'Վճարման ուղի',
    staffStatesComma: 'Նահանգներ (ստորակետով բաժանված)',
    staffCreate: 'Ստեղծել աշխատակցի գրառում',
    staffPersonDetails: 'Աշխատակցի մանրամասներ',
    staffChooseBody: 'Ընտրեք տող կամ օգտագործեք «Աշխատակցի մանրամասներ» դաշտը՝ նրա գրառումը, պլանները և պորտֆելը դիտելու համար։',
    staffRecord: 'Աշխատակցի գրառում',
    staffAccount: 'Հաշիվ',
    staffPartnerBook: 'Գործընկերների պորտֆել',
    staffNoPartners: 'Գործընկերներ նշանակված չեն',
    staffNoPartnersBody: 'Ստորև նշանակեք գործընկեր՝ նրան այս աշխատակցի պորտֆելին ավելացնելու համար։',
    staffMoveAll: "Տեղափոխել բոլոր գործընկերներին",
    staffMoveAllIntro: "Այս պահից տեղափոխում է {name}-ի այսօրվա բոլոր գործընկերներին մեկ այլ SP Manager-ի կամ EVI-ի պորտֆել։ Արդեն գործարկված լիցքավորիչները պահպանում են գործարկման նշումը։",
    staffMoveAllTo: "Տեղափոխել",
    staffHouseBook: "EVI-ի պորտֆել",
    staffMoveAllButton: "Տեղափոխել բոլոր գործընկերներին",
    staffMoveAllDone: "Տեղափոխված գործընկերներ՝ {count}։",
    staffDepartureMoves: "Հեռանալու օրը այս աշխատակցի գործընկերները անցնում են իր Sales Manager-ին, իսկ եթե չկա՝ EVI-ի պորտֆել։",
    staffDeparted: 'Հեռացել է {date}, պատճառը՝ {reason}',
    staffUpdated: 'Աշխատակցի գրառումը թարմացվեց։',
    staffUpdateFailed: 'Թարմացումը ձախողվեց։',
    staffStates: 'Նահանգներ',
    staffEmploymentNote: 'Զբաղվածության փոփոխությունների համար օգտագործեք ամսաթվով անցումը «Պլանի նոր խմբագրություն» բաժնում։ Լիցքավորիչների դրոշմները, պորտֆելի նշանակումները և ակտիվության պատուհանը մնում են այս աշխատակցի գրառման հետ։',
    staffEndDate: 'Ավարտի ամսաթիվ (ըստ ցանկության)',
    staffDepartureReason: 'Հեռանալու պատճառ',
    staffGoodStanding: 'Լավ պայմաններով',
    staffCause: 'Մեղքի պատճառով',
    staffConductIds: 'Վարքագծի հետ կապված գրանցամատյանի տողերի ID-ներ (միայն մեղքի դեպքում)',
    staffSave: 'Պահպանել աշխատակցի փոփոխությունները',
    staffManagerAssignment: 'Ղեկավարի ամսաթվով նշանակում',
    staffManagerRecorded: 'Ղեկավարի ամսաթվով նշանակումը գրանցվեց։',
    staffPlanRevisions: 'Պլանի խմբագրություններ',
    staffNoPlans: 'Պլանի խմբագրություններ չկան',
    staffNoPlansBody: 'Գրանցեք ամսաթվով առաջարկ, որը այս աշխատակիցը պետք է ընդունի։',
    staffRevision: 'Խմբագրություն',
    staffEffectiveFrom: 'Գործում է սկսած',
    staffEffectiveTo: 'Գործում է մինչև',
    staffOngoing: 'Շարունակական',
    staffAssignPartner: 'Նշանակել գործընկեր',
    staffAssignIntro: 'Գրանցեք նոր նշանակում՝ դրա պատճառով։ Նախորդ նշանակումների պատմությունը մնում է անփոփոխ։',
    staffAssignmentRecorded: 'Նշանակումը գրանցվեց։',
    staffUnassigned: 'Չնշանակված',
    staffStandardPlan: 'Ստանդարտ պլան ապագա ներկայացուցիչների համար',
    staffSiteClaims: 'Կայքերի ամրագրումներ',
    staffClaimsIntro: 'Գոյություն ունեցող ոչ վաճառքային կայքերը պահանջում են հաստատում։ Քանի դեռ ամրագրումը սպասում է ստուգման, պորտֆելի մուտք կամ միջնորդավճար չի տրամադրվում։',
    staffNoClaims: 'Կայքերի ամրագրումներ չկան',
    staffNoClaimsBody: 'Գրանցված վաճառքային կայքերը կհայտնվեն այստեղ ստուգման համար։',
    staffRegistrant: 'Գրանցող',
    staffExisting: 'Արդեն գոյություն ունի ({kind})՝ {id}',
    staffReviewRecorded: 'Ստուգումը գրանցվեց։',
    staffReviewFailed: 'Ստուգումը ձախողվեց։',
    staffDecision: 'Որոշում',
    staffReject: 'Մերժել',
    staffApprove: 'Հաստատել',
    staffRecordDecision: 'Գրանցել որոշումը',
    staffReassignRecorded: 'Վերանշանակումը գրանցվեց։',
    staffReassign: 'Վերանշանակել ամրագրումը',
    staffReassignFailed: 'Վերանշանակումը ձախողվեց։',
    staffRegistrantKind: 'Նոր գրանցողի տեսակ',
    // Plan revision form
    planNoMonthLimit: 'Առանց ամիսների սահմանափակման',
    planStoredStandard: 'Պահպանված ստանդարտ խմբագրություն',
    planNew: 'Պլանի նոր խմբագրություն',
    planPrefilled: 'Նախապես լրացված է պահպանված ստանդարտ խմբագրությունից ({revision})։ Ընդունված պլանները և լիցքավորիչների գոյություն ունեցող դրոշմները մնում են անփոփոխ։',
    planPrefilledCode: 'Նախապես լրացված է գործող ստանդարտից՝ խմբագրություն {revision}, դեռ չգրանցված։ Ընդունված պլանները և լիցքավորիչների գոյություն ունեցող դրոշմները մնում են անփոփոխ։',
    planLoading: 'բեռնվում է',
    planLoadFailed: 'Չհաջողվեց բեռնել պահպանված ստանդարտը։ Խմբագրությունը հնարավոր չէ գրանցել։',
    planStage: 'Պլանի փուլ',
    planStage1: '1-ին փուլի միջնորդավճար',
    planStage2: '2-րդ փուլի աշխատավարձի դրույքներ',
    planNoRates: 'Այս փուլի համար պահպանված դրույքներ հասանելի չեն։',
    planCentsReadback: 'Այսինքն՝ {amount}',
    planRecorded: 'Խմբագրությունը գրանցվեց։ Անձնական խմբագրությունը պահանջում է ընդունում։',
    planFailed: 'Չհաջողվեց գրանցել խմբագրությունը։',
    planDatedRevision: 'Պլանի կամ դերի ամսաթվով խմբագրություն',
    planEmployeeMove: 'Ամսաթվով անցում աշխատողի կարգավիճակի և աշխատավարձի',
    planRoleOnRevision: 'Դերն այս խմբագրությունում',
    planNotice: 'Գրավոր ծանուցման հղում',
    planRecord: 'Գրանցել նոր խմբագրություն',
  },
  ko: {
    // Shared labels and the status values the screens write themselves
    notRecorded: '기록 없음',
    notSet: '설정 안 됨',
    noResults: '결과 없음',
    person: '구성원',
    status: '상태',
    reason: '사유',
    partner: '파트너',
    partners: '파트너',
    chooseAPerson: '구성원을 선택하세요',
    chooseAPartner: '파트너를 선택하세요',
    nextStep: '다음 단계',
    ledgerMonth: '원장 월',
    allMonths: '모든 월',
    manager: '매니저',
    noManager: '매니저 없음',
    rep: '영업 담당자',
    roleManager: '매니저(자체 영업 담당자 포트폴리오 보유)',
    territory: '담당 지역',
    startDate: '시작일',
    effectiveDate: '발효일',
    action: '작업',
    period: '기간',
    currency: '통화',
    location: '사이트',
    name: '이름',
    email: '이메일',
    paymentMethod: '지급 방법',
    stripeConnect: 'Stripe Connect',
    payroll: '급여',
    payrollEligibility: '급여 전환 자격',
    activePartners: '활성 파트너',
    marginShare: '마진 배분',
    heldUnderCap: '한도로 보류됨',
    earned: '적립액',
    paymentsAndPayroll: '지급 및 급여',
    salesStaff: '영업 직원',
    salesPartner: '세일즈 파트너',
    recording: '기록하는 중...',
    recordAssignment: '배정 기록',
    assignmentFailed: '배정에 실패했습니다.',
    pointOne: '{n}포인트',
    pointOther: '{n}포인트',
    statusActive: '활성',
    statusInactive: '비활성',
    statusQualified: '자격 충족',
    statusNotQualified: '자격 미충족',
    statusAccepted: '수락됨',
    statusAwaitingAcceptance: '수락 대기',
    statusAssigned: '배정됨',
    statusOffline: '오프라인',
    statusNotStarted: '시작 안 됨',
    statusPending: '대기 중',
    statusNoSubmission60: '60일간 제출 없음',
    statusBelowPace: '6개월 페이스 미달',
    statusOrderFollowUp: '주문 후속 조치 필요',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: '영업팀',
    teamSubtitle: '내 네트워크, 팀 활동, 기록된 수익입니다.',
    teamOpenPerson: '구성원의 팀 열기',
    teamLoading: '팀을 불러오는 중',
    teamChoose: '팀을 선택하세요',
    teamEmptyBody: '접근 권한이 있는 팀 및 포트폴리오 데이터가 여기에 표시됩니다.',
    tabMap: '지도',
    tabTeam: '팀',
    tabReports: '보고서',
    tabScoreboard: '점수판',
    teamMyBook: '내 포트폴리오',
    teamWholeTeam: '전체 팀',
    teamNetworkMap: '네트워크 지도',
    teamEveryone: '범위 내 모든 구성원',
    teamStage: '단계',
    teamAllStages: '모든 단계',
    teamChargerType: '충전기 유형',
    teamAllTypes: '모든 유형',
    teamState: '주',
    teamAllStates: '모든 주',
    teamLaunchedFrom: '가동 개시 시작일',
    teamLaunchedThrough: '가동 개시 종료일',
    teamPinLegend: '핀 색상은 구성원을 나타냅니다. 채워진 원은 온라인, 빈 원은 오프라인, 느낌표는 고장을 뜻합니다. 지도 좌표가 없는 사이트: {n}개.',
    teamCloseDetails: '사이트 세부 정보 닫기',
    teamCity: '도시',
    teamChargers: '충전기',
    teamDirectDeal: '직접 거래',
    teamLaunchDate: '가동 개시일',
    teamNotLaunched: '미가동',
    teamSessions30: '세션(최근 30일)',
    teamKwh30: 'kWh(최근 30일)',
    teamNoSites: '일치하는 사이트가 없습니다',
    teamNoSitesBody: '필터를 변경하면 포트폴리오의 다른 사이트를 볼 수 있습니다.',
    teamSite: '사이트',
    teamDirectReports: '직속 팀원',
    teamNoDirectReports: '직속 팀원이 없습니다',
    teamNoDirectReportsBody: '날짜가 지정된 배정이 기록되면 여기에 표시됩니다.',
    teamActivity: '활동',
    teamEarnedMonth: '이번 달 적립액',
    teamActivatedMonth: '이번 달 활성화',
    teamLaunchedMonth: '이번 달 가동 개시',
    teamPace: '페이스',
    teamOnPace: '페이스 유지',
    teamEligibility: '{months}/{required}개월 동안 {active}/{target} 활성.',
    teamUsdAverage: '6개월 USD 평균: {amount}',
    teamNotAvailable: '사용할 수 없음',
    teamScoreboard: '팀 점수판',
    teamThisWeek: '이번 주',
    teamThisMonth: '이번 달',
    teamScoreNote: '건수만 표시합니다. 적격 가동 개시 수, 그다음 활성 파트너 수와 활성화 수로 순위를 매깁니다. 리드, 데모, 신청은 감사된 활동 기록을 사용합니다. 통화와 대화는 파트너 메모를 사용합니다. 추정치는 추가하지 않습니다.',
    teamOutreach: '아웃리치',
    teamLaunches: '가동 개시',
    scoreLeadsStarted: '리드 시작',
    scoreCalls: '통화',
    scoreConversations: '대화',
    scoreDemos: '데모',
    scoreApplications: '신청',
    scoreApproved: '승인',
    scoreFirstPaidOrders: '첫 유료 주문',
    scoreActivated: '활성화',
    scoreActivePartners: '활성 파트너',
    scoreQualifyingLaunches: '적격 가동 개시',
    reportMonthly: '월별 및 카테고리별 커미션',
    reportSessions: '충전기별 세션 포인트',
    reportYearEnd: '연말 합계',
    reportsTitle: '내 보고서',
    reportYear: '연도',
    reportKind: '보고서',
    reportDownloadFailed: '보고서를 다운로드하지 못했습니다.',
    reportExport: 'CSV 내보내기',
    reportEmpty: '기록된 활동이 없습니다',
    reportEmptyBody: '기록된 수익과 지급 증빙이 여기에 표시됩니다.',
    reportNote: '원장 통화는 별도로 유지됩니다. 연말 합계는 신고 준비를 돕기 위한 것이며 세금 양식이 아닙니다.',
    activityRecorded: '활동을 기록했습니다.',
    activityFailed: '활동을 기록하지 못했습니다.',
    activityLabel: '내 활동 기록',
    activityLead: '신규 리드 시작',
    activityDemo: '데모',
    activityApplication: '신청',
    activityEvidence: '증빙 참조 번호',
    activityPlaceholder: '활동 참조 번호',
    activityRecord: '활동 기록',
    // Sales payout run
    payTitle: '지급 실행',
    paySubtitle: '마감된 명세서를 검토한 다음 지급을 확정하세요. 자동 지급은 승인될 때까지 꺼져 있습니다.',
    payCycle: '월간 주기',
    payCycleBody: '명세서는 매월 1일에 마감됩니다. 검토는 1일부터 4일까지입니다. 지급은 5일 또는 다음 영업일에 이루어집니다. 통화별로 따로 지급됩니다. 준비되지 않은 계정과 실패한 송금은 계속 지급 대상으로 남습니다.',
    payStatementMonth: '명세서 월',
    payClose: '명세서 마감',
    payPay: '지급 및 급여 내보내기',
    paySync: '수익 동기화',
    paySynced: '수익과 취소 내역을 동기화했습니다.',
    payPreview: '미리 보기만',
    payRequestFailed: '요청에 실패했습니다.',
    payNewLines: '새 원장 항목: {n}개. 명세서 검토가 필요합니다.',
    payDate: '지급일: {date}.',
    payMayProceed: '지급을 진행할 수 있습니다.',
    payWait: '해당 날짜까지 지급이 대기합니다.',
    payNothing: '처리할 항목이 없습니다',
    payNothingBody: '이번 달에 사용할 수 있는 명세서 또는 지급 잔액이 없습니다.',
    payLedgerHeld: '원장 및 보류',
    payPayment: '지급',
    payReview: '검토',
    payHeld: '보류: {amount}',
    payConversion: '환전 비용: {amount}',
    payReady: '준비됨',
    payPrepareQuote: '환전 견적 준비',
    payQuotePrepared: '견적을 준비했습니다. 새 미리 보기를 실행하세요.',
    payConfirmCheck: '이 금액, 통화, 차단된 수령인을 검토했습니다.',
    payConfirmClose: '명세서 마감 확정',
    payConfirmPay: '지급 실행 확정',
    payRunRecorded: '실행을 기록했습니다: {result}',
    payPayroll: '급여 내보내기 및 대사',
    payDownload: '기록된 급여 CSV 다운로드',
    payPaymentId: '지급 또는 내보내기 ID',
    payProviderRef: '제공업체 참조 번호',
    payRecordPayroll: '완료된 급여 기록',
    payPayrollRecorded: '급여 지급을 기록했습니다.',
    payActualCost: '실제 환전 비용',
    payActualBody: '제공업체의 최종 환전 비용을 원장 통화로 기록하세요. 견적과의 차이는 현재 열려 있는 명세서의 조정 항목이 됩니다.',
    payLedgerCurrency: '원장 통화',
    payActualCents: '실제 비용(센트)',
    payRecordActual: '실제 비용 기록',
    payActualReconciled: '실제 환전 비용을 대사했습니다.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: '내 포트폴리오에 있는 파트너의 활동, 사이트, 다음 단계입니다.',
    bookLoadingPartners: '파트너를 불러오는 중...',
    bookQualifyingChargers: '적격 충전기',
    bookLaunches12: '12개월 가동 개시',
    bookActivityTarget: '활동 목표: 8',
    bookLaunches6: '6개월 가동 개시',
    bookPaceTarget: '페이스 목표: 4',
    bookLocationsSubmissions: '사이트 / 제출',
    bookLastContact: '마지막 연락',
    bookLocationsHeading: '사이트 및 제출',
    bookNoLocations: '아직 사이트가 없습니다',
    bookNoLocationsBody: '이 파트너가 제출한 사이트가 여기에 표시됩니다.',
    bookNotes: '메모 및 다음 단계',
    bookNoPartners: '포트폴리오에 파트너가 없습니다',
    bookNoPartnersBody: '배정된 파트너와 해당 사이트가 여기에 표시됩니다.',
    bookLoadingSubmission: '제출 내용을 불러오는 중...',
    bookLocationType: '사이트 유형',
    bookAddress: '주소',
    bookContact: '연락처',
    bookConnectivity: '통신 연결',
    bookCommissioning: '시운전 승인됨',
    bookAwaitingReview: 'EVI 검토 대기 중',
    bookInstallerExpires: '설치 기사 접근 만료',
    bookNotIssued: '발급 안 됨',
    bookLaunch: '가동 개시',
    bookAwaitingApproval: 'EVI 승인 대기 중',
    bookOrdersTitle: '주문',
    bookOrdersSubtitle: '나와 내 포트폴리오의 파트너에게 귀속된 고객 주문입니다.',
    bookLoadingOrders: '주문을 불러오는 중...',
    bookOrderDate: '주문일',
    bookDirectStaffOrder: '직원 직접 주문',
    bookOrderTotal: '주문 합계',
    bookQty: '수량 {n}',
    bookRegisterDeal: '이 직접 거래 등록',
    bookClaimRecorded: '사이트 귀속 신청을 기록했습니다. 영업 외 기존 사이트는 최고 관리자의 검토가 필요합니다.',
    bookClaimFailed: '사이트 귀속 신청을 기록하지 못했습니다.',
    bookDealEvidence: '직접 거래 증빙',
    bookRegisterButton: '직접 거래 등록',
    bookNoOrders: '아직 주문이 없습니다',
    bookNoOrdersBody: '직접 거래와 파트너 포트폴리오의 주문이 여기에 표시됩니다.',
    bookNewestOrders: '최신 주문',
    bookOlderOrders: '이전 주문',
    bookOnboardingTitle: '온보딩 지원',
    bookOnboardingSubtitle: '내 포트폴리오에 있는 사이트의 사람들이 필요한 접근 권한을 받도록 도와주세요.',
    bookInvite: '사용자 초대',
    bookInviteBody: '초대를 보내거나 다시 보냅니다. 발송은 현재 메일 정책을 따릅니다.',
    bookNoInviteLocations: '아직 초대할 사이트가 없습니다',
    bookNoInviteLocationsBody: '사이트의 사용자를 초대하려면 먼저 해당 사이트가 내 포트폴리오에 있어야 합니다.',
    bookInviteRecorded: '초대를 기록했습니다. 발송은 현재 메일 정책을 따릅니다.',
    bookInviteFailed: '초대를 보내지 못했습니다.',
    bookChooseLocation: '사이트를 선택하세요',
    bookAccess: '접근 권한',
    roleBuildingAdmin: '건물 관리자',
    rolePropertyManager: '부동산 관리자',
    roleDriver: '드라이버',
    bookInviting: '초대를 기록하는 중...',
    bookSendInvite: '초대 보내기 또는 다시 보내기',
    // Partner and direct site notes
    notesHeading: '통화, 방문, 이메일',
    notesIntro: '모든 접촉은 당일에 기록하세요. 메모는 EVI에 귀속됩니다. 수정하면 새 버전이 추가되고 원본은 보존됩니다.',
    notesEmpty: '아직 메모가 없습니다',
    notesEmptyBody: '아래에 첫 연락과 명확한 다음 단계를 기록하세요.',
    notesVersion: '버전 {n}',
    notesDue: '기한',
    notesAddCorrection: '수정 버전 추가',
    notesLinksFailed: '연결된 사이트와 제출 내용을 불러오지 못했습니다.',
    notesRecorded: '메모를 기록했습니다.',
    notesFailed: '메모를 기록하지 못했습니다.',
    notesChannel: '채널',
    notesCall: '통화',
    notesVisit: '방문',
    notesEmailChannel: '이메일',
    notesProjectStage: '프로젝트 단계',
    notesNote: '메모',
    notesNextDue: '다음 단계 기한',
    notesLocationOptional: '사이트(선택 사항)',
    notesNoLocation: '연결된 사이트 없음',
    notesSubmissionOptional: '제출(선택 사항)',
    notesNoSubmission: '연결된 제출 없음',
    notesRecordVersion: '새 버전 기록',
    notesRecordNote: '메모 기록',
    notesCancel: '수정 취소',
    notesSiteTitle: '직접 사이트 메모',
    notesNoClaims: '직접 사이트 귀속 신청이 없습니다',
    notesNoClaimsBody: '직접 사이트 귀속 신청과 연락 기록이 여기에 표시됩니다.',
    notesStaffTitle: '파트너 메모 및 다음 단계',
    notesStaffIntro: 'EVI의 자체 파트너를 포함해 모든 파트너에 대한 메모를 기록하세요. 파트너가 영업 담당자에게 배정되어도 메모는 파트너에게 남습니다.',
    notesFindPartner: '파트너 찾기',
    notesHouseBook: ' (EVI 자체 포트폴리오)',
    notesFirstPartners: '파트너 목록 처음으로',
    notesMorePartners: '파트너 더 보기',
    notesChooseBody: '파트너를 선택해 연락 기록을 보거나 기록하세요.',
    // My commission and Sales statements
    commPlanRevision: '커미션 플랜, 개정 {n}',
    commOfferRevision: '내 제안, 개정 {n}',
    commEffective: '적용 기간: {date}부터',
    commThrough: '{date}까지',
    commAccepted: '{date}에 수락함. 수락한 플랜은 읽기 전용으로 표시됩니다.',
    commDirectMargin: '하드웨어 직접 마진',
    commAboveThreshold: '연간 기준 초과분',
    commPartnerPoints: '파트너 세션 포인트',
    commFleetShare: '플릿 구독 배분',
    commFleetMeta: '{n}개월, 귀속된 플릿 주문만 해당',
    commFleetEvery: '팀 소속 기간 동안 모든 갱신, 플릿 주문만 해당',
    commAnnualThreshold: '연간 하드웨어 마진 기준({currency})',
    commFirstOrder: '첫 적격 유료 주문',
    commFourthCharger: '네 번째 적격 충전기',
    commBookDuration: '파트너 포트폴리오 마진 기간',
    commNoMonthLimit: '배정 기간 동안 개월 수 제한 없음',
    commMonthsPerPartner: '파트너당 {n}개월',
    commContinuation: '원만한 퇴사 후 지속 기간',
    commMonths: '{n}개월',
    commOwnLadder: '본인 직접 충전기 단계표',
    commFromCharger: '시작 기준(적격 스탬프 충전기 수)',
    commSessionPoints: '세션 포인트',
    commLadderNote: '적립은 네 번째 적격 직접 충전기부터 시작됩니다. 처음 12개월은 준비 기간입니다. 그 이후 새 가동 개시는 자신을 포함해 최근 12개월 동안 적격 가동 개시가 8건 있어야 합니다. 스탬프가 없는 가동 개시는 활동으로 집계되지만 나중에 스탬프를 받지 않습니다. 기존 스탬프는 팀에 있는 동안 계속 지급됩니다.',
    commBookTiers: '파트너 포트폴리오 마진 구간',
    commActiveAtLeast: '활성 파트너(최소)',
    commPointsNote: '파트너 세션 포인트는 파트너 커미션 이후에 적용됩니다. 직원 세션 금액은 EVI의 사용 가능한 잔여분을 상한으로 합니다. 플릿 구독 배분은 구독 청구가 활성화되면 시작되며, 다른 구독은 배분 대상이 아닙니다.',
    commGolden: '지급된 Golden 보너스: {amount}.',
    commOverrideTiers: '매니저 오버라이드 구간',
    commTeamActiveAtLeast: '팀 활성 파트너(최소)',
    commGrowth: '매니저 성장 보너스: 활성 파트너 6명 달성 시 {six}, 팀원의 급여 전환 기준 달성 시 {payroll}. 팀 세션 포인트: {points}.',
    commAccepting: '수락하는 중...',
    commAccept: '이 커미션 플랜 수락',
    commOfferAccepted: '제안을 수락했습니다.',
    commOfferFailed: '제안을 수락하지 못했습니다.',
    commStage2: '2단계 정보',
    commStage2Body: '취소분을 반영한 기록된 커미션의 완료된 6개월입니다. 이것으로 2단계로 전환되지는 않습니다. 최고 관리자가 승인된 플랜 개정을 기록해야 합니다.',
    commConsecutive: '연속 {n}/{required}개월',
    commCompletedMonth: '완료된 월',
    commRecordedCommission: '기록된 커미션({currency})',
    commNoMonths: '아직 완료된 플랜 월이 없습니다',
    commNoMonthsBody: '플랜 발효일 이후 완료된 월만 여기에 표시됩니다.',
    commMonthsAvailable: '완료된 월 6개 중 {n}개를 사용할 수 있습니다.',
    commAverage: '평균: {amount}',
    commAgainst: '대 {amount}',
    commNoCrossCurrency: ', 통화 간 비교 없음',
    commStatements: '월간 명세서',
    commNoStatements: '아직 명세서가 없습니다',
    commNoStatementsBody: '커미션 월이 마감되면 첫 명세서가 표시됩니다.',
    commOpening: '기초',
    commReversals: '취소',
    commPaid: '지급됨',
    commClosing: '기말',
    commNoPayments: '아직 지급 내역이 없습니다',
    commNoPaymentsBody: '수익이 지급되면 Connect 지급 또는 급여 기록이 표시됩니다.',
    commLedgerAmount: '원장 금액',
    commMonth: '월',
    commStripeTransfer: 'Stripe 송금',
    commNotSent: '보내지 않음',
    commConversionCost: '환전 비용',
    commCashAmount: '지급 금액',
    commSentAs: '{amount}(으)로 송금됨',
    commReservedAs: '{amount}(으)로 예약됨',
    commLines: '커미션 항목',
    commNoLines: '아직 커미션 항목이 없습니다',
    commNoLinesBody: '적격 활동이 있으면 여기에 커미션 항목이 생성되며, 이후 수정은 별도로 표시됩니다.',
    commCategory: '카테고리',
    commBasis: '기준 금액',
    commPlainTerms: "읽는 법: 기준 금액은 요율이 적용되는 금액입니다(하드웨어 주문은 EVI의 마진). 보류는 벌었지만 아직 지급되지 않은 금액입니다. EVI는 해당 파트너에게서 회수한 하드웨어 마진의 절반까지에서 그 파트너에 대한 수입을 지급하고, 나머지는 파트너의 다음 주문이 마진을 더 가져올 때 지급됩니다. 2단계는 기본급이 있는 EVI 급여 플랜입니다. 커미션 기록 6개월을 모두 채우는 것이 검토 기준이며, 전환은 EVI와 합의하는 새 플랜입니다.",
    commRate: '요율',
    commCommission: '커미션',
    commPlanRevisionHeader: '플랜 개정',
    commTitle: '내 커미션',
    commSubtitle: '내 제안, 커미션 활동, 지급 기록입니다. CAD와 USD는 별도로 유지됩니다.',
    commNoOffer: '아직 기록된 제안이 없습니다',
    commNoOfferBody: 'EVI가 검토하고 수락할 수 있도록 커미션 플랜을 기록합니다.',
    commClosedNote: '마감된 명세서는 변경되지 않습니다. 수정 사항은 새 항목으로 표시됩니다.',
    commLoading: '커미션을 불러오는 중...',
    commNewestLines: '최신 항목',
    commOlderLines: '이전 항목',
    stmtTitle: '영업 명세서',
    stmtSubtitle: '영업팀의 기록된 커미션, 명세서, 지급, 급여를 검토하세요.',
    stmtNote: '검토된 스크립트를 사용해 월을 적립 및 마감하고, Connect 계약자에게 지급하거나 급여를 내보내세요. 이 페이지는 수금이나 송금을 시작하지 않습니다.',
    stmtChooseBody: '직원을 선택해 커미션 원장과 명세서를 검토하세요.',
    stmtReviewQueue: '검토 대기열',
    stmtNothingReview: '검토 대기 중인 항목이 없습니다',
    stmtNothingReviewBody: '증빙에 확인이 필요하면 정산 검토 항목이 여기에 표시됩니다.',
    stmtSource: '출처',
    stmtCostMissing: '기록되지 않은 원가',
    stmtCostIntro: '관리자 전용 수동 증빙은 별도로 저장됩니다. 원래 주문 스냅샷은 변경되지 않습니다.',
    stmtNoMissing: '누락된 원가 증빙이 없습니다',
    stmtNoMissingBody: '원가 스냅샷이 없는 귀속된 유료 주문이 여기에 표시됩니다.',
    stmtCostRecorded: '원가 증빙을 기록했습니다.',
    stmtCostFailed: '원가 증빙을 저장하지 못했습니다.',
    stmtChooseCurrency: '통화를 선택하세요',
    stmtUnits: '{name}: {n}개',
    stmtUnitCost: '단위 원가',
    stmtAutelFee: '단위당 부담한 Autel 수수료',
    stmtEvidenceReason: '증빙 및 사유',
    stmtRecordEvidence: '수동 증빙 기록',
    stmtCorrection: '수정 추가',
    stmtCorrectionIntro: '취소는 음수로 입력합니다. 복원은 원래 권리를 초과할 수 없습니다. 재시도에는 변하지 않는 참조 번호를 사용하세요.',
    stmtNoLinesToCorrect: '수정할 항목이 없습니다',
    stmtNoLinesToCorrectBody: '수정을 추가하려면 양수 커미션 항목이 필요합니다.',
    stmtCorrectionRecorded: '수정을 기록했습니다.',
    stmtCorrectionFailed: '수정에 실패했습니다.',
    stmtOriginalLine: '원래 항목',
    stmtSignedAmount: '부호 있는 금액',
    stmtReference: '참조 번호',
    stmtAppend: '수정 추가',
    // Sales Staff
    staffTitle: '영업 직원',
    staffSubtitle: '구성원, 날짜가 지정된 커미션 플랜, 파트너 포트폴리오를 관리합니다. 변경 사항은 감사됩니다.',
    staffNote: '최고 관리자만 직원, 플랜, 파트너 배정을 변경할 수 있습니다.',
    staffPeople: '구성원',
    staffActive: '활성',
    staffContractors: '계약자',
    staffNoStaff: '아직 영업 직원이 없습니다',
    staffNoStaffBody: '아래에서 기존 활성 계정이 있는 사람을 추가하세요.',
    staffKind: '유형',
    staffRole: '역할',
    staffConnect: 'Connect',
    staffEligible: '자격 있음',
    staffNotEligible: '아직 자격 없음',
    staffEligibility: '{status}: 활성 {active}/{target}, {months}/{required}개월',
    staffAdd: '구성원 추가',
    staffCreated: '직원 기록을 만들었습니다.',
    staffAddFailed: '구성원을 추가하지 못했습니다.',
    staffInviteSent: '초대를 보냈습니다. 상대가 수락하면 이 역할, 관리자, 시작일로 직원 기록이 생성됩니다.',
    staffInviteNeedsName: '이 이메일은 아직 계정이 없어 초대가 발송됩니다. 먼저 이름을 입력하세요.',
    staffInvitesTitle: '초대됨, 아직 수락 전',
    staffInvitedOn: '{date} 초대',
    staffInviteFailed: '수락 시 생성되지 않음: {reason}',
    staffResendInvite: '초대 다시 보내기',
    staffInviteResent: '초대를 다시 보냈습니다.',
    staffCancelInvite: '초대 취소',
    staffInviteCancelled: '초대를 취소했습니다.',
    staffInviteActionFailed: '초대를 변경하지 못했습니다.',
    inviteLinkAction: "초대 링크 복사",
    inviteLinkTitle: "초대 링크 복사",
    inviteLinkField: "초대 링크",
    inviteLinkLoading: "새 링크를 만드는 중...",
    inviteLinkCopy: "복사",
    inviteLinkCopied: "복사됨",
    inviteLinkSelected: "링크가 선택되었습니다. Ctrl+C(Mac에서는 Cmd+C)를 눌러 복사하세요.",
    inviteLinkEmail: "내 이메일로 보내기",
    inviteLinkReplaces: "이 링크는 이전 초대 이메일의 링크를 대체합니다. 한 번만 사용할 수 있으며 {date}에 만료됩니다.",
    inviteLinkDiscard: "이 창을 닫으면 링크가 사라지며 다시 표시되지 않습니다.",
    inviteLinkExpired: "이 초대는 만료되었습니다. 다시 보내 새 초대를 만든 다음 그 링크를 복사하세요.",
    inviteLinkFailed: "초대 링크를 만들 수 없습니다.",
    inviteLinkClose: "닫기",
    inviteLinkMailSubject: "EV Initiative 초대",
    inviteLinkMailGreeting: "{name}님, 안녕하세요.",
    inviteLinkMailGreetingNoName: "안녕하세요.",
    inviteLinkMailBody: "이전 초대 이메일이 도착하지 않았을 수 있어 제 이메일로 EV Initiative 초대를 보내드립니다. 아래 링크는 한 번만 사용할 수 있으며 {date}에 만료됩니다.",
    inviteLinkMailSignoff: "감사합니다.",
    inviteLinkSignInPage: "이 이메일로 된 EV Initiative 계정이 이미 있어서 이 링크는 로그인 페이지를 엽니다. 그 계정으로 로그인하면 초대가 수락됩니다.",
    inviteLinkMailSignInPage: "이전 초대 이메일이 도착하지 않았을 수 있어 제 이메일로 EV Initiative 초대를 보내드립니다. 이 이메일로 된 EV Initiative 계정이 이미 있으시니, 아래 링크에서 그 계정으로 로그인해 초대를 수락해 주세요.",
    staffAccountEmail: '계정 이메일',
    staffStaffRole: '직원 역할',
    staffContractor: '계약자',
    staffEmployee: '정규직 직원',
    staffPayThrough: '지급 경로',
    staffStatesComma: '주(쉼표로 구분)',
    staffCreate: '직원 기록 만들기',
    staffPersonDetails: '구성원 세부 정보',
    staffChooseBody: '행을 선택하거나 구성원 세부 정보를 사용해 기록, 플랜, 포트폴리오를 검토하세요.',
    staffRecord: '직원 기록',
    staffAccount: '계정',
    staffPartnerBook: '파트너 포트폴리오',
    staffNoPartners: '배정된 파트너가 없습니다',
    staffNoPartnersBody: '아래에서 파트너를 배정하면 이 구성원의 포트폴리오에 추가됩니다.',
    staffMoveAll: "모든 파트너 이동",
    staffMoveAllIntro: "{name}님이 현재 담당하는 모든 파트너를 지금부터 다른 SP Manager 또는 EVI 직영 포트폴리오로 옮깁니다. 이미 가동된 충전기는 가동 시 기록을 유지합니다.",
    staffMoveAllTo: "이동할 곳",
    staffHouseBook: "EVI 직영 포트폴리오",
    staffMoveAllButton: "모든 파트너 이동",
    staffMoveAllDone: "파트너 {count}곳을 옮겼습니다.",
    staffDepartureMoves: "퇴사일이 되면 이 구성원의 파트너는 담당 Sales Manager에게, 없으면 EVI 직영 포트폴리오로 이동합니다.",
    staffDeparted: '{date} 퇴사: {reason}',
    staffUpdated: '직원 기록을 업데이트했습니다.',
    staffUpdateFailed: '업데이트에 실패했습니다.',
    staffStates: '주',
    staffEmploymentNote: '고용 형태 변경에는 새 플랜 개정의 날짜 지정 전환을 사용하세요. 충전기 스탬프, 포트폴리오 배정, 활동 기간은 이 직원 기록에 유지됩니다.',
    staffEndDate: '종료일(선택 사항)',
    staffDepartureReason: '퇴사 사유',
    staffGoodStanding: '원만한 퇴사',
    staffCause: '귀책 사유',
    staffConductIds: '행위 관련 원장 항목 ID(귀책 사유인 경우만)',
    staffSave: '직원 변경 사항 저장',
    staffManagerAssignment: '날짜 지정 매니저 배정',
    staffManagerRecorded: '날짜 지정 매니저 배정을 기록했습니다.',
    staffPlanRevisions: '플랜 개정',
    staffNoPlans: '플랜 개정이 없습니다',
    staffNoPlansBody: '이 구성원이 수락할 날짜 지정 제안을 기록하세요.',
    staffRevision: '개정',
    staffEffectiveFrom: '적용 시작',
    staffEffectiveTo: '적용 종료',
    staffOngoing: '진행 중',
    staffAssignPartner: '파트너 배정',
    staffAssignIntro: '사유와 함께 새 배정을 기록하세요. 이전 배정 기록은 그대로 유지됩니다.',
    staffAssignmentRecorded: '배정을 기록했습니다.',
    staffUnassigned: '미배정',
    staffStandardPlan: '향후 영업 담당자용 표준 플랜',
    staffSiteClaims: '사이트 귀속 신청',
    staffClaimsIntro: '영업 외 기존 사이트는 승인이 필요합니다. 신청이 검토를 기다리는 동안에는 포트폴리오 접근 권한이나 커미션이 부여되지 않습니다.',
    staffNoClaims: '사이트 귀속 신청이 없습니다',
    staffNoClaimsBody: '등록된 영업 사이트가 검토를 위해 여기에 표시됩니다.',
    staffRegistrant: '등록자',
    staffExisting: '기존 항목({kind}): {id}',
    staffReviewRecorded: '검토를 기록했습니다.',
    staffReviewFailed: '검토에 실패했습니다.',
    staffDecision: '결정',
    staffReject: '거부',
    staffApprove: '승인',
    staffRecordDecision: '결정 기록',
    staffReassignRecorded: '재배정을 기록했습니다.',
    staffReassign: '신청 재배정',
    staffReassignFailed: '재배정에 실패했습니다.',
    staffRegistrantKind: '새 등록자 유형',
    // Plan revision form
    planNoMonthLimit: '개월 수 제한 없음',
    planStoredStandard: '저장된 표준 개정',
    planNew: '새 플랜 개정',
    planPrefilled: '저장된 표준 개정({revision})에서 미리 채웠습니다. 수락된 플랜과 기존 충전기 스탬프는 변경되지 않습니다.',
    planPrefilledCode: '현재 표준(개정 {revision}, 아직 기록되지 않음)에서 미리 채웠습니다. 수락된 플랜과 기존 충전기 스탬프는 변경되지 않습니다.',
    planLoading: '불러오는 중',
    planLoadFailed: '저장된 표준을 불러오지 못했습니다. 개정을 기록할 수 없습니다.',
    planStage: '플랜 단계',
    planStage1: '1단계 커미션',
    planStage2: '2단계 급여 요율',
    planNoRates: '이 단계에 사용할 수 있는 저장된 요율이 없습니다.',
    planCentsReadback: '{amount}에 해당',
    planRecorded: '개정을 기록했습니다. 개인 개정은 수락이 필요합니다.',
    planFailed: '개정을 기록하지 못했습니다.',
    planDatedRevision: '날짜 지정 플랜 또는 역할 개정',
    planEmployeeMove: '날짜 지정 정규직 및 급여 전환',
    planRoleOnRevision: '이 개정의 역할',
    planNotice: '서면 통지 참조 번호',
    planRecord: '새 개정 기록',
  },
  it: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Non registrato',
    notSet: 'Non impostato',
    noResults: 'Nessun risultato',
    person: 'Persona',
    status: 'Stato',
    reason: 'Motivo',
    partner: 'Partner',
    partners: 'Partner',
    chooseAPerson: 'Scegli una persona',
    chooseAPartner: 'Scegli un partner',
    nextStep: 'Prossimo passo',
    ledgerMonth: 'Mese contabile',
    allMonths: 'Tutti i mesi',
    manager: 'Responsabile',
    noManager: 'Nessun responsabile',
    rep: 'Rappresentante',
    roleManager: 'Responsabile, con un proprio portafoglio da rappresentante',
    territory: 'Territorio',
    startDate: 'Data di inizio',
    effectiveDate: 'Data di decorrenza',
    action: 'Azione',
    period: 'Periodo',
    currency: 'Valuta',
    location: 'Posizione',
    name: 'Nome',
    email: 'Email',
    paymentMethod: 'Metodo di pagamento',
    stripeConnect: 'Stripe Connect',
    payroll: 'Busta paga',
    payrollEligibility: 'Idoneità alla busta paga',
    activePartners: 'Partner attivi',
    marginShare: 'Quota di margine',
    heldUnderCap: 'Trattenuto per il limite',
    earned: 'Maturato',
    paymentsAndPayroll: 'Pagamenti e buste paga',
    salesStaff: 'Personale di vendita',
    salesPartner: 'Sales Partner',
    recording: 'Registrazione...',
    recordAssignment: 'Registra l’assegnazione',
    assignmentFailed: 'Assegnazione non riuscita.',
    pointOne: '{n} punto',
    pointOther: '{n} punti',
    statusActive: 'attivo',
    statusInactive: 'inattivo',
    statusQualified: 'qualificato',
    statusNotQualified: 'non qualificato',
    statusAccepted: 'accettato',
    statusAwaitingAcceptance: 'in attesa di accettazione',
    statusAssigned: 'assegnato',
    statusOffline: 'offline',
    statusNotStarted: 'non avviato',
    statusPending: 'in sospeso',
    statusNoSubmission60: 'Nessun invio in 60 giorni',
    statusBelowPace: 'Sotto il ritmo di 6 mesi',
    statusOrderFollowUp: 'Ordine da seguire',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Team vendite',
    teamSubtitle: 'La tua rete, l’attività del team e i guadagni registrati.',
    teamOpenPerson: 'Apri il team di una persona',
    teamLoading: 'Caricamento del tuo team',
    teamChoose: 'Scegli un team',
    teamEmptyBody: 'Qui compaiono i dati del team e del portafoglio a cui hai accesso.',
    tabMap: 'Mappa',
    tabTeam: 'Team',
    tabReports: 'Report',
    tabScoreboard: 'Classifica',
    teamMyBook: 'Il mio portafoglio',
    teamWholeTeam: 'Tutto il team',
    teamNetworkMap: 'Mappa della rete',
    teamEveryone: 'Tutte le persone nel tuo ambito',
    teamStage: 'Fase',
    teamAllStages: 'Tutte le fasi',
    teamChargerType: 'Tipo di caricatore',
    teamAllTypes: 'Tutti i tipi',
    teamState: 'Stato',
    teamAllStates: 'Tutti gli stati',
    teamLaunchedFrom: 'Lanciato dal',
    teamLaunchedThrough: 'Lanciato fino al',
    teamPinLegend: 'Il colore del segnaposto identifica la persona. Un cerchio pieno indica online, un cerchio vuoto offline e un punto esclamativo un guasto. Siti senza coordinate sulla mappa: {n}.',
    teamCloseDetails: 'Chiudi i dettagli del sito',
    teamCity: 'Città',
    teamChargers: 'Caricatori',
    teamDirectDeal: 'Vendita diretta',
    teamLaunchDate: 'Data di lancio',
    teamNotLaunched: 'Non lanciato',
    teamSessions30: 'Sessioni, ultimi 30 giorni',
    teamKwh30: 'kWh, ultimi 30 giorni',
    teamNoSites: 'Nessun sito corrispondente',
    teamNoSitesBody: 'Modifica i filtri per vedere altri siti del tuo portafoglio.',
    teamSite: 'Sito',
    teamDirectReports: 'Riporti diretti',
    teamNoDirectReports: 'Nessun riporto diretto',
    teamNoDirectReportsBody: 'Le assegnazioni datate compaiono qui una volta registrate.',
    teamActivity: 'Attività',
    teamEarnedMonth: 'Maturato questo mese',
    teamActivatedMonth: 'Attivati questo mese',
    teamLaunchedMonth: 'Lanciati questo mese',
    teamPace: 'Ritmo',
    teamOnPace: 'In linea con il ritmo',
    teamEligibility: '{active}/{target} attivi per {months}/{required} mesi.',
    teamUsdAverage: 'Media semestrale in USD: {amount}',
    teamNotAvailable: 'Non disponibile',
    teamScoreboard: 'Classifica del team',
    teamThisWeek: 'Questa settimana',
    teamThisMonth: 'Questo mese',
    teamScoreNote: 'Solo conteggi. Classifica per lanci idonei, poi per partner attivi e attivazioni. Lead, dimostrazioni e candidature usano i tuoi registri di attività verificati. Chiamate e conversazioni usano le note sui partner. Non vengono aggiunte stime.',
    teamOutreach: 'Contatti',
    teamLaunches: 'Lanci',
    scoreLeadsStarted: 'lead avviati',
    scoreCalls: 'chiamate',
    scoreConversations: 'conversazioni',
    scoreDemos: 'dimostrazioni',
    scoreApplications: 'candidature',
    scoreApproved: 'approvati',
    scoreFirstPaidOrders: 'primi ordini pagati',
    scoreActivated: 'attivati',
    scoreActivePartners: 'partner attivi',
    scoreQualifyingLaunches: 'lanci idonei',
    reportMonthly: 'Provvigioni per mese e categoria',
    reportSessions: 'Punti sessione per caricatore',
    reportYearEnd: 'Totali di fine anno',
    reportsTitle: 'I miei report',
    reportYear: 'Anno',
    reportKind: 'Report',
    reportDownloadFailed: 'Download del report non riuscito.',
    reportExport: 'Esporta CSV',
    reportEmpty: 'Nessuna attività registrata',
    reportEmptyBody: 'Qui compariranno i guadagni registrati e le prove di pagamento.',
    reportNote: 'Le valute contabili restano separate. I totali di fine anno aiutano a preparare le dichiarazioni e non sono un modulo fiscale.',
    activityRecorded: 'Attività registrata.',
    activityFailed: 'Impossibile registrare l’attività.',
    activityLabel: 'Registra la mia attività',
    activityLead: 'Nuovo lead avviato',
    activityDemo: 'Dimostrazione',
    activityApplication: 'Candidatura',
    activityEvidence: 'Riferimento della prova',
    activityPlaceholder: 'Riferimento della tua attività',
    activityRecord: 'Registra l’attività',
    // Sales payout run
    payTitle: 'Esecuzione dei pagamenti',
    paySubtitle: 'Controlla gli estratti conto chiusi, poi conferma i pagamenti. I pagamenti automatici restano disattivati fino all’approvazione.',
    payCycle: 'Ciclo mensile',
    payCycleBody: 'Gli estratti conto si chiudono il giorno 1. La verifica si svolge dal giorno 1 al 4. Il pagamento avviene il giorno 5 o il giorno feriale successivo. Ogni valuta viene pagata separatamente. Gli account non pronti e i trasferimenti non riusciti restano da pagare.',
    payStatementMonth: 'Mese dell’estratto conto',
    payClose: 'Chiudi gli estratti conto',
    payPay: 'Paga ed esporta le buste paga',
    paySync: 'Sincronizza i guadagni',
    paySynced: 'Guadagni e storni sincronizzati.',
    payPreview: 'Solo anteprima',
    payRequestFailed: 'Richiesta non riuscita.',
    payNewLines: 'Nuove righe contabili: {n}. È necessaria la verifica dell’estratto conto.',
    payDate: 'Data di pagamento: {date}.',
    payMayProceed: 'I pagamenti possono procedere.',
    payWait: 'I pagamenti attendono fino a quella data.',
    payNothing: 'Niente da elaborare',
    payNothingBody: 'Nessun saldo di estratto conto o di pagamento è disponibile per questo mese.',
    payLedgerHeld: 'Contabile e trattenuto',
    payPayment: 'Pagamento',
    payReview: 'Verifica',
    payHeld: 'Trattenuto: {amount}',
    payConversion: 'Costo di conversione: {amount}',
    payReady: 'Pronto',
    payPrepareQuote: 'Prepara il preventivo di conversione',
    payQuotePrepared: 'Preventivo preparato. Genera una nuova anteprima.',
    payConfirmCheck: 'Ho verificato questi importi, le valute e i destinatari bloccati.',
    payConfirmClose: 'Conferma la chiusura degli estratti conto',
    payConfirmPay: 'Conferma l’esecuzione dei pagamenti',
    payRunRecorded: 'Esecuzione registrata: {result}',
    payPayroll: 'Esportazione e riconciliazione delle buste paga',
    payDownload: 'Scarica il CSV delle buste paga registrate',
    payPaymentId: 'ID del pagamento o dell’esportazione',
    payProviderRef: 'Riferimento del fornitore',
    payRecordPayroll: 'Registra le buste paga completate',
    payPayrollRecorded: 'Pagamento delle buste paga registrato.',
    payActualCost: 'Costo effettivo di conversione',
    payActualBody: 'Registra il costo di conversione finale del fornitore nella valuta contabile. Una differenza rispetto al preventivo diventa una rettifica sull’estratto conto aperto corrente.',
    payLedgerCurrency: 'Valuta contabile',
    payActualCents: 'Costo effettivo in centesimi',
    payRecordActual: 'Registra il costo effettivo',
    payActualReconciled: 'Costo effettivo di conversione riconciliato.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Attività, posizioni e prossimi passi dei partner del tuo portafoglio.',
    bookLoadingPartners: 'Caricamento dei tuoi partner...',
    bookQualifyingChargers: 'Caricatori idonei',
    bookLaunches12: 'Lanci in 12 mesi',
    bookActivityTarget: 'Obiettivo di attività: 8',
    bookLaunches6: 'Lanci in 6 mesi',
    bookPaceTarget: 'Obiettivo di ritmo: 4',
    bookLocationsSubmissions: 'Posizioni / invii',
    bookLastContact: 'Ultimo contatto',
    bookLocationsHeading: 'Posizioni e invii',
    bookNoLocations: 'Ancora nessuna posizione',
    bookNoLocationsBody: 'Qui compariranno le posizioni inviate da questo partner.',
    bookNotes: 'Note e prossimi passi',
    bookNoPartners: 'Nessun partner nel tuo portafoglio',
    bookNoPartnersBody: 'Qui compariranno i partner assegnati e le loro posizioni.',
    bookLoadingSubmission: 'Caricamento dell’invio...',
    bookLocationType: 'Tipo di posizione',
    bookAddress: 'Indirizzo',
    bookContact: 'Contatto',
    bookConnectivity: 'Connettività',
    bookCommissioning: 'Messa in servizio autorizzata',
    bookAwaitingReview: 'In attesa della verifica di EVI',
    bookInstallerExpires: 'Scadenza dell’accesso dell’installatore',
    bookNotIssued: 'Non rilasciato',
    bookLaunch: 'Lancio',
    bookAwaitingApproval: 'In attesa dell’approvazione di EVI',
    bookOrdersTitle: 'Ordini',
    bookOrdersSubtitle: 'Ordini dei clienti attribuiti a te e ai partner del tuo portafoglio.',
    bookLoadingOrders: 'Caricamento degli ordini...',
    bookOrderDate: 'Data dell’ordine',
    bookDirectStaffOrder: 'Ordine diretto del personale',
    bookOrderTotal: 'Totale dell’ordine',
    bookQty: 'Qtà {n}',
    bookRegisterDeal: 'Registra questa vendita diretta',
    bookClaimRecorded: 'Rivendicazione del sito registrata. I siti esistenti non di vendita richiedono la verifica di un super amministratore.',
    bookClaimFailed: 'Impossibile registrare la rivendicazione del sito.',
    bookDealEvidence: 'Prova della vendita diretta',
    bookRegisterButton: 'Registra la vendita diretta',
    bookNoOrders: 'Ancora nessun ordine',
    bookNoOrdersBody: 'Qui compariranno gli ordini delle tue vendite dirette e del tuo portafoglio di partner.',
    bookNewestOrders: 'Ordini più recenti',
    bookOlderOrders: 'Ordini meno recenti',
    bookOnboardingTitle: 'Aiuto all’onboarding',
    bookOnboardingSubtitle: 'Aiuta le persone delle posizioni del tuo portafoglio a ottenere l’accesso di cui hanno bisogno.',
    bookInvite: 'Invita una persona',
    bookInviteBody: 'Invia o reinvia un invito. La consegna segue la politica email in vigore.',
    bookNoInviteLocations: 'Ancora nessuna posizione per gli inviti',
    bookNoInviteLocationsBody: 'Una posizione deve essere nel tuo portafoglio prima che tu possa invitarne gli utenti.',
    bookInviteRecorded: 'Invito registrato. La consegna segue la politica email in vigore.',
    bookInviteFailed: 'Impossibile inviare l’invito.',
    bookChooseLocation: 'Scegli una posizione',
    bookAccess: 'Accesso',
    roleBuildingAdmin: 'Amministratore dell’edificio',
    rolePropertyManager: 'Gestore dell’immobile',
    roleDriver: 'Conducente',
    bookInviting: 'Registrazione dell’invito...',
    bookSendInvite: 'Invia o reinvia l’invito',
    // Partner and direct site notes
    notesHeading: 'Chiamate, visite ed email',
    notesIntro: 'Registra ogni contatto lo stesso giorno. Le note appartengono a EVI. Una correzione aggiunge una versione e conserva l’originale.',
    notesEmpty: 'Ancora nessuna nota',
    notesEmptyBody: 'Registra qui sotto il primo contatto e un prossimo passo chiaro.',
    notesVersion: 'Versione {n}',
    notesDue: 'Scadenza',
    notesAddCorrection: 'Aggiungi una versione corretta',
    notesLinksFailed: 'Impossibile caricare le posizioni e gli invii collegati.',
    notesRecorded: 'Nota registrata.',
    notesFailed: 'Impossibile registrare la nota.',
    notesChannel: 'Canale',
    notesCall: 'Chiamata',
    notesVisit: 'Visita',
    notesEmailChannel: 'Email',
    notesProjectStage: 'Fase del progetto',
    notesNote: 'Nota',
    notesNextDue: 'Scadenza del prossimo passo',
    notesLocationOptional: 'Posizione (facoltativa)',
    notesNoLocation: 'Nessuna posizione collegata',
    notesSubmissionOptional: 'Invio (facoltativo)',
    notesNoSubmission: 'Nessun invio collegato',
    notesRecordVersion: 'Registra la nuova versione',
    notesRecordNote: 'Registra la nota',
    notesCancel: 'Annulla la correzione',
    notesSiteTitle: 'Note sui siti diretti',
    notesNoClaims: 'Nessuna rivendicazione di siti diretti',
    notesNoClaimsBody: 'Qui compariranno le tue rivendicazioni di siti diretti e la loro cronologia dei contatti.',
    notesStaffTitle: 'Note sui partner e prossimi passi',
    notesStaffIntro: 'Registra note per qualsiasi partner, compresi i partner interni di EVI. Le note restano al partner quando viene assegnato a un rappresentante.',
    notesFindPartner: 'Trova un partner',
    notesHouseBook: ' (portafoglio interno di EVI)',
    notesFirstPartners: 'Primi partner',
    notesMorePartners: 'Altri partner',
    notesChooseBody: 'Seleziona un partner per leggere o registrare la cronologia dei contatti.',
    // My commission and Sales statements
    commPlanRevision: 'Piano provvigionale, revisione {n}',
    commOfferRevision: 'La tua offerta, revisione {n}',
    commEffective: 'In vigore dal {date}',
    commThrough: 'al {date}',
    commAccepted: 'Accettato il {date}. Il tuo piano accettato è mostrato in sola lettura.',
    commDirectMargin: 'Margine diretto sull’hardware',
    commAboveThreshold: 'Oltre la soglia annuale',
    commPartnerPoints: 'Punti sessione dei partner',
    commFleetShare: 'Quota sugli abbonamenti flotta',
    commFleetMeta: '{n} mesi; solo ordini flotta attribuiti',
    commFleetEvery: 'Ogni rinnovo finché fai parte del team; solo ordini flotta',
    commAnnualThreshold: 'Soglia annuale del margine hardware ({currency})',
    commFirstOrder: 'Primo ordine pagato idoneo',
    commFourthCharger: 'Quarto caricatore idoneo',
    commBookDuration: 'Durata del margine sul portafoglio partner',
    commNoMonthLimit: 'Nessun limite di mesi finché assegnato',
    commMonthsPerPartner: '{n} mesi per partner',
    commContinuation: 'Continuazione dopo un’uscita in buoni rapporti',
    commMonths: '{n} mesi',
    commOwnLadder: 'Scala dei caricatori diretti propri',
    commFromCharger: 'Dal caricatore idoneo con timbro',
    commSessionPoints: 'Punti sessione',
    commLadderNote: 'La maturazione inizia dal quarto caricatore diretto idoneo. I primi 12 mesi sono un anno di avviamento. Dopo, un nuovo lancio richiede 8 lanci idonei negli ultimi 12 mesi, compreso sé stesso. I lanci senza timbro contano per l’attività ma non ricevono mai un timbro in seguito. I timbri esistenti continuano a pagare finché fai parte del team.',
    commBookTiers: 'Fasce di margine del portafoglio partner',
    commActiveAtLeast: 'Partner attivi, almeno',
    commPointsNote: 'I punti sessione dei partner si applicano dopo la provvigione del partner. Gli importi di sessione del personale sono limitati al residuo disponibile di EVI. La quota sugli abbonamenti flotta inizia quando la fatturazione degli abbonamenti è attiva; gli altri abbonamenti non generano una quota.',
    commGolden: 'Bonus Golden pagato: {amount}.',
    commOverrideTiers: 'Fasce di provvigione del responsabile sul team',
    commTeamActiveAtLeast: 'Partner attivi del team, almeno',
    commGrowth: 'Bonus di crescita del responsabile: {six} con sei partner attivi e {payroll} al traguardo di busta paga del riporto. Punti sessione del team: {points}.',
    commAccepting: 'Accettazione...',
    commAccept: 'Accetta questo piano provvigionale',
    commOfferAccepted: 'Offerta accettata.',
    commOfferFailed: 'Accettazione dell’offerta non riuscita.',
    commStage2: 'Informazioni sulla Fase 2',
    commStage2Body: 'Sei mesi completi di provvigioni registrate, al netto degli storni. Questo non ti porta alla Fase 2. Un super amministratore deve registrare una revisione del piano approvata.',
    commConsecutive: '{n} di {required} mesi consecutivi',
    commCompletedMonth: 'Mese completato',
    commRecordedCommission: 'Provvigioni registrate ({currency})',
    commNoMonths: 'Ancora nessun mese del piano completato',
    commNoMonthsBody: 'Qui compaiono solo i mesi completati dalla data di decorrenza del tuo piano.',
    commMonthsAvailable: '{n} di 6 mesi completati disponibili.',
    commAverage: 'Media: {amount}',
    commAgainst: 'rispetto a {amount}',
    commNoCrossCurrency: '; nessun confronto tra valute',
    commStatements: 'Estratti conto mensili',
    commNoStatements: 'Ancora nessun estratto conto',
    commNoStatementsBody: 'Il tuo primo estratto conto compare dopo la chiusura di un mese di provvigioni.',
    commOpening: 'Apertura',
    commReversals: 'Storni',
    commPaid: 'Pagato',
    commClosing: 'Chiusura',
    commNoPayments: 'Ancora nessun pagamento',
    commNoPaymentsBody: 'I pagamenti Connect o le registrazioni delle buste paga compariranno dopo il pagamento dei guadagni.',
    commLedgerAmount: 'Importo contabile',
    commMonth: 'Mese',
    commStripeTransfer: 'Trasferimento Stripe',
    commNotSent: 'Non inviato',
    commConversionCost: 'Costo di conversione',
    commCashAmount: 'Importo versato',
    commSentAs: 'Inviato come {amount}',
    commReservedAs: 'Riservato come {amount}',
    commLines: 'Righe provvigionali',
    commNoLines: 'Ancora nessuna riga provvigionale',
    commNoLinesBody: 'Le attività idonee creano qui righe provvigionali, con le eventuali correzioni successive mostrate separatamente.',
    commCategory: 'Categoria',
    commBasis: 'Base',
    commPlainTerms: "Come leggere: la base è l'importo a cui si applica la tua aliquota (per un ordine di hardware, il margine di EVI). Trattenuto significa guadagnato ma non ancora pagato: EVI paga i tuoi guadagni su un partner dal margine hardware incassato da quel partner, fino alla metà, e il resto viene pagato man mano che i suoi ordini successivi portano di più. La fase 2 è il piano a stipendio di EVI con uno stipendio base: sei mesi completi di commissioni sono la soglia per essere considerati, e il passaggio è un nuovo piano concordato con te.",
    commRate: 'Aliquota',
    commCommission: 'Provvigione',
    commPlanRevisionHeader: 'Revisione del piano',
    commTitle: 'Le mie provvigioni',
    commSubtitle: 'La tua offerta, l’attività provvigionale e le registrazioni dei pagamenti. CAD e USD restano separati.',
    commNoOffer: 'Ancora nessuna offerta registrata',
    commNoOfferBody: 'EVI registrerà un piano provvigionale da farti controllare e accettare.',
    commClosedNote: 'Gli estratti conto chiusi non cambiano. Le correzioni compaiono come nuove righe.',
    commLoading: 'Caricamento delle provvigioni...',
    commNewestLines: 'Righe più recenti',
    commOlderLines: 'Righe meno recenti',
    stmtTitle: 'Estratti conto vendite',
    stmtSubtitle: 'Controlla provvigioni, estratti conto, pagamenti e buste paga registrati del tuo team vendite.',
    stmtNote: 'Usa gli script verificati per maturare e chiudere un mese, pagare i collaboratori Connect o esportare le buste paga. Questa pagina non avvia mai un incasso né un trasferimento.',
    stmtChooseBody: 'Seleziona un membro del personale per controllarne il registro provvigionale e gli estratti conto.',
    stmtReviewQueue: 'Coda di verifica',
    stmtNothingReview: 'Niente in attesa di verifica',
    stmtNothingReviewBody: 'Qui compariranno gli elementi di verifica del regolamento quando una prova richiede attenzione.',
    stmtSource: 'Origine',
    stmtCostMissing: 'Costo non registrato',
    stmtCostIntro: 'Le prove manuali riservate agli amministratori sono conservate separatamente. L’istantanea originale dell’ordine non viene modificata.',
    stmtNoMissing: 'Nessuna prova di costo mancante',
    stmtNoMissingBody: 'Qui compariranno gli ordini pagati attribuiti privi di un’istantanea dei costi.',
    stmtCostRecorded: 'Prova di costo registrata.',
    stmtCostFailed: 'Impossibile salvare la prova di costo.',
    stmtChooseCurrency: 'Scegli la valuta',
    stmtUnits: '{name}: {n} unità',
    stmtUnitCost: 'Costo unitario',
    stmtAutelFee: 'Commissione Autel assorbita per unità',
    stmtEvidenceReason: 'Prova e motivo',
    stmtRecordEvidence: 'Registra la prova manuale',
    stmtCorrection: 'Aggiungi una correzione',
    stmtCorrectionIntro: 'Gli storni sono negativi. I ripristini non possono superare il diritto originale. Usa un riferimento stabile per un nuovo tentativo.',
    stmtNoLinesToCorrect: 'Nessuna riga da correggere',
    stmtNoLinesToCorrectBody: 'Serve una riga provvigionale positiva prima di poter aggiungere una correzione.',
    stmtCorrectionRecorded: 'Correzione registrata.',
    stmtCorrectionFailed: 'Correzione non riuscita.',
    stmtOriginalLine: 'Riga originale',
    stmtSignedAmount: 'Importo con segno',
    stmtReference: 'Riferimento',
    stmtAppend: 'Aggiungi la correzione',
    // Sales Staff
    staffTitle: 'Personale di vendita',
    staffSubtitle: 'Gestisci persone, piani provvigionali datati e portafogli di partner. Le modifiche vengono sottoposte ad audit.',
    staffNote: 'Solo un super amministratore può modificare personale, piani o assegnazioni dei partner.',
    staffPeople: 'Persone',
    staffActive: 'Attivi',
    staffContractors: 'Collaboratori esterni',
    staffNoStaff: 'Ancora nessun personale di vendita',
    staffNoStaffBody: 'Aggiungi qui sotto una persona con un account attivo esistente.',
    staffKind: 'Tipo',
    staffRole: 'Ruolo',
    staffConnect: 'Connect',
    staffEligible: 'Idoneo',
    staffNotEligible: 'Non ancora idoneo',
    staffEligibility: '{status}: {active}/{target} attivi, {months}/{required} mesi',
    staffAdd: 'Aggiungi una persona',
    staffCreated: 'Scheda del personale creata.',
    staffAddFailed: 'Impossibile aggiungere la persona.',
    staffInviteSent: 'Invito inviato. Il record del personale viene creato con questo ruolo, responsabile e data di inizio quando lo accettano.',
    staffInviteNeedsName: 'Questa email non ha ancora un account, quindi riceverà un invito. Aggiungi prima il nome.',
    staffInvitesTitle: 'Invitati, non ancora accettato',
    staffInvitedOn: 'Invitato il {date}',
    staffInviteFailed: 'Non creato all’accettazione: {reason}',
    staffResendInvite: 'Invia di nuovo l’invito',
    staffInviteResent: 'Invito inviato di nuovo.',
    staffCancelInvite: 'Annulla invito',
    staffInviteCancelled: 'Invito annullato.',
    staffInviteActionFailed: 'Impossibile modificare l’invito.',
    inviteLinkAction: "Copia link d’invito",
    inviteLinkTitle: "Copia link d’invito",
    inviteLinkField: "Link d’invito",
    inviteLinkLoading: "Creazione di un nuovo link...",
    inviteLinkCopy: "Copia",
    inviteLinkCopied: "Copiato",
    inviteLinkSelected: "Il link è selezionato. Premi Ctrl+C, o Cmd+C su Mac, per copiarlo.",
    inviteLinkEmail: "Invia dal mio account",
    inviteLinkReplaces: "Questo link sostituisce quello delle email d’invito precedenti. Funziona una sola volta e scade il {date}.",
    inviteLinkDiscard: "Chiudendo questa finestra il link viene eliminato. Non verrà più mostrato.",
    inviteLinkExpired: "Questo invito è scaduto. Invialo di nuovo per crearne uno nuovo, poi copia quel link.",
    inviteLinkFailed: "Non è stato possibile creare il link d’invito.",
    inviteLinkClose: "Chiudi",
    inviteLinkMailSubject: "Il tuo invito a EV Initiative",
    inviteLinkMailGreeting: "Ciao {name},",
    inviteLinkMailGreetingNoName: "Ciao,",
    inviteLinkMailBody: "Ti invio il tuo invito a EV Initiative dalla mia email nel caso il precedente non ti sia arrivato. Il link qui sotto funziona una sola volta e scade il {date}.",
    inviteLinkMailSignoff: "Grazie,",
    inviteLinkSignInPage: "Ha già un account EV Initiative con questa email, quindi questo link apre la pagina di accesso. Accedere con quell’account accetta l’invito.",
    inviteLinkMailSignInPage: "Ti invio il tuo invito a EV Initiative dalla mia email nel caso il precedente non ti sia arrivato. Hai già un account EV Initiative con questa email, quindi accedi con quello dal link qui sotto per accettarlo.",
    staffAccountEmail: 'Email dell’account',
    staffStaffRole: 'Ruolo di vendita',
    staffContractor: 'Collaboratore esterno',
    staffEmployee: 'Dipendente',
    staffPayThrough: 'Paga tramite',
    staffStatesComma: 'Stati (separati da virgole)',
    staffCreate: 'Crea la scheda del personale',
    staffPersonDetails: 'Dettagli della persona',
    staffChooseBody: 'Seleziona una riga o usa Dettagli della persona per controllarne la scheda, i piani e il portafoglio.',
    staffRecord: 'Scheda del personale',
    staffAccount: 'Account',
    staffPartnerBook: 'Portafoglio partner',
    staffNoPartners: 'Nessun partner assegnato',
    staffNoPartnersBody: 'Assegna un partner qui sotto per aggiungerlo al portafoglio di questa persona.',
    staffMoveAll: "Sposta tutti i partner",
    staffMoveAllIntro: "Sposta da ora tutti i partner che {name} segue oggi a un altro SP Manager o al portafoglio di EVI. I caricatori già avviati mantengono il timbro di avvio.",
    staffMoveAllTo: "Sposta a",
    staffHouseBook: "Portafoglio di EVI",
    staffMoveAllButton: "Sposta tutti i partner",
    staffMoveAllDone: "{count} partner spostati.",
    staffDepartureMoves: "Alla data di uscita, i partner di questa persona passano al suo Sales Manager, o al portafoglio di EVI se non ce n’è uno.",
    staffDeparted: 'Uscita il {date}: {reason}',
    staffUpdated: 'Scheda del personale aggiornata.',
    staffUpdateFailed: 'Aggiornamento non riuscito.',
    staffStates: 'Stati',
    staffEmploymentNote: 'Le modifiche al rapporto di lavoro usano il passaggio datato in Nuova revisione del piano. I timbri dei caricatori, le assegnazioni del portafoglio e la finestra di attività restano legati a questa scheda del personale.',
    staffEndDate: 'Data di fine (facoltativa)',
    staffDepartureReason: 'Motivo dell’uscita',
    staffGoodStanding: 'Buoni rapporti',
    staffCause: 'Giusta causa',
    staffConductIds: 'ID delle righe contabili legate alla condotta (solo giusta causa)',
    staffSave: 'Salva le modifiche al personale',
    staffManagerAssignment: 'Assegnazione datata del responsabile',
    staffManagerRecorded: 'Assegnazione datata del responsabile registrata.',
    staffPlanRevisions: 'Revisioni del piano',
    staffNoPlans: 'Nessuna revisione del piano',
    staffNoPlansBody: 'Registra un’offerta datata che questa persona dovrà accettare.',
    staffRevision: 'Revisione',
    staffEffectiveFrom: 'In vigore dal',
    staffEffectiveTo: 'In vigore fino al',
    staffOngoing: 'In corso',
    staffAssignPartner: 'Assegna un partner',
    staffAssignIntro: 'Registra una nuova assegnazione con il relativo motivo. La cronologia delle assegnazioni precedenti resta intatta.',
    staffAssignmentRecorded: 'Assegnazione registrata.',
    staffUnassigned: 'Non assegnato',
    staffStandardPlan: 'Piano standard per i futuri rappresentanti',
    staffSiteClaims: 'Rivendicazioni di siti',
    staffClaimsIntro: 'I siti esistenti non di vendita richiedono approvazione. Nessun accesso al portafoglio né provvigione viene concesso mentre una rivendicazione è in attesa di verifica.',
    staffNoClaims: 'Nessuna rivendicazione di siti',
    staffNoClaimsBody: 'Qui compariranno i siti di vendita registrati per la verifica.',
    staffRegistrant: 'Richiedente',
    staffExisting: 'Esistente ({kind}): {id}',
    staffReviewRecorded: 'Verifica registrata.',
    staffReviewFailed: 'Verifica non riuscita.',
    staffDecision: 'Decisione',
    staffReject: 'Rifiuta',
    staffApprove: 'Approva',
    staffRecordDecision: 'Registra la decisione',
    staffReassignRecorded: 'Riassegnazione registrata.',
    staffReassign: 'Riassegna la rivendicazione',
    staffReassignFailed: 'Riassegnazione non riuscita.',
    staffRegistrantKind: 'Tipo del nuovo richiedente',
    // Plan revision form
    planNoMonthLimit: 'Nessun limite di mesi',
    planStoredStandard: 'Revisione standard salvata',
    planNew: 'Nuova revisione del piano',
    planPrefilled: 'Precompilato dalla revisione standard salvata {revision}. I piani accettati e i timbri dei caricatori esistenti restano invariati.',
    planPrefilledCode: 'Precompilato dallo standard in vigore, revisione {revision}, non ancora registrata. I piani accettati e i timbri dei caricatori esistenti restano invariati.',
    planLoading: 'caricamento',
    planLoadFailed: 'Impossibile caricare lo standard salvato. Non è possibile registrare una revisione.',
    planStage: 'Fase del piano',
    planStage1: 'Provvigioni della Fase 1',
    planStage2: 'Tariffe di busta paga della Fase 2',
    planNoRates: 'Nessuna tariffa salvata disponibile per questa fase.',
    planCentsReadback: 'Pari a {amount}',
    planRecorded: 'Revisione registrata. Una revisione personale richiede l’accettazione.',
    planFailed: 'Impossibile registrare la revisione.',
    planDatedRevision: 'Revisione datata del piano o del ruolo',
    planEmployeeMove: 'Passaggio datato a dipendente e busta paga',
    planRoleOnRevision: 'Ruolo in questa revisione',
    planNotice: 'Riferimento della comunicazione scritta',
    planRecord: 'Registra la nuova revisione',
  },
  pt: {
    // Shared labels and the status values the screens write themselves
    notRecorded: 'Não registrado',
    notSet: 'Não definido',
    noResults: 'Nenhum resultado',
    person: 'Pessoa',
    status: 'Situação',
    reason: 'Motivo',
    partner: 'Parceiro',
    partners: 'Parceiros',
    chooseAPerson: 'Escolha uma pessoa',
    chooseAPartner: 'Escolha um parceiro',
    nextStep: 'Próximo passo',
    ledgerMonth: 'Mês contábil',
    allMonths: 'Todos os meses',
    manager: 'Gerente',
    noManager: 'Sem gerente',
    rep: 'Representante',
    roleManager: 'Gerente, com carteira própria de representante',
    territory: 'Território',
    startDate: 'Data de início',
    effectiveDate: 'Data de vigência',
    action: 'Ação',
    period: 'Período',
    currency: 'Moeda',
    location: 'Local',
    name: 'Nome',
    email: 'E-mail',
    paymentMethod: 'Forma de pagamento',
    stripeConnect: 'Stripe Connect',
    payroll: 'Folha de pagamento',
    payrollEligibility: 'Elegibilidade para a folha de pagamento',
    activePartners: 'Parceiros ativos',
    marginShare: 'Participação na margem',
    heldUnderCap: 'Retido pelo teto',
    earned: 'Ganho',
    paymentsAndPayroll: 'Pagamentos e folha de pagamento',
    salesStaff: 'Profissional de vendas',
    salesPartner: 'Sales Partner',
    recording: 'Registrando...',
    recordAssignment: 'Registrar atribuição',
    assignmentFailed: 'Falha na atribuição.',
    pointOne: '{n} ponto',
    pointOther: '{n} pontos',
    statusActive: 'ativo',
    statusInactive: 'inativo',
    statusQualified: 'qualificado',
    statusNotQualified: 'não qualificado',
    statusAccepted: 'aceito',
    statusAwaitingAcceptance: 'aguardando aceitação',
    statusAssigned: 'atribuído',
    statusOffline: 'desconectado',
    statusNotStarted: 'não iniciado',
    statusPending: 'pendente',
    statusNoSubmission60: 'Sem envios em 60 dias',
    statusBelowPace: 'Abaixo do ritmo de 6 meses',
    statusOrderFollowUp: 'Pedido precisa de acompanhamento',
    // Sales Team view: map, team, reports and scoreboard tabs
    teamTitle: 'Equipe de vendas',
    teamSubtitle: 'Sua rede, a atividade da equipe e os ganhos registrados.',
    teamOpenPerson: 'Abrir a equipe de uma pessoa',
    teamLoading: 'Carregando sua equipe',
    teamChoose: 'Escolha uma equipe',
    teamEmptyBody: 'Os dados de equipe e de carteira que você pode acessar aparecem aqui.',
    tabMap: 'Mapa',
    tabTeam: 'Equipe',
    tabReports: 'Relatórios',
    tabScoreboard: 'Placar',
    teamMyBook: 'Minha carteira',
    teamWholeTeam: 'Equipe inteira',
    teamNetworkMap: 'Mapa da rede',
    teamEveryone: 'Todas as pessoas no seu escopo',
    teamStage: 'Etapa',
    teamAllStages: 'Todas as etapas',
    teamChargerType: 'Tipo de carregador',
    teamAllTypes: 'Todos os tipos',
    teamState: 'Estado',
    teamAllStates: 'Todos os estados',
    teamLaunchedFrom: 'Lançado a partir de',
    teamLaunchedThrough: 'Lançado até',
    teamPinLegend: 'A cor do marcador identifica a pessoa. Um círculo preenchido indica conectado, um círculo vazio indica desconectado e um ponto de exclamação indica falha. Locais sem coordenadas no mapa: {n}.',
    teamCloseDetails: 'Fechar detalhes do local',
    teamCity: 'Cidade',
    teamChargers: 'Carregadores',
    teamDirectDeal: 'Venda direta',
    teamLaunchDate: 'Data de lançamento',
    teamNotLaunched: 'Não lançado',
    teamSessions30: 'Sessões, últimos 30 dias',
    teamKwh30: 'kWh, últimos 30 dias',
    teamNoSites: 'Nenhum local corresponde',
    teamNoSitesBody: 'Altere os filtros para ver outros locais da sua carteira.',
    teamSite: 'Local',
    teamDirectReports: 'Subordinados diretos',
    teamNoDirectReports: 'Nenhum subordinado direto',
    teamNoDirectReportsBody: 'As atribuições com data aparecem aqui depois de registradas.',
    teamActivity: 'Atividade',
    teamEarnedMonth: 'Ganho neste mês',
    teamActivatedMonth: 'Ativados neste mês',
    teamLaunchedMonth: 'Lançados neste mês',
    teamPace: 'Ritmo',
    teamOnPace: 'No ritmo',
    teamEligibility: '{active}/{target} ativos por {months}/{required} meses.',
    teamUsdAverage: 'Média de seis meses em USD: {amount}',
    teamNotAvailable: 'Não disponível',
    teamScoreboard: 'Placar da equipe',
    teamThisWeek: 'Esta semana',
    teamThisMonth: 'Este mês',
    teamScoreNote: 'Apenas contagens. Classificação por lançamentos qualificados e depois por parceiros ativos e ativações. Leads, demonstrações e solicitações usam seus registros de atividade auditados. Ligações e conversas usam as notas de parceiros. Nenhuma estimativa é adicionada.',
    teamOutreach: 'Prospecção',
    teamLaunches: 'Lançamentos',
    scoreLeadsStarted: 'leads iniciados',
    scoreCalls: 'ligações',
    scoreConversations: 'conversas',
    scoreDemos: 'demonstrações',
    scoreApplications: 'solicitações',
    scoreApproved: 'aprovados',
    scoreFirstPaidOrders: 'primeiros pedidos pagos',
    scoreActivated: 'ativados',
    scoreActivePartners: 'parceiros ativos',
    scoreQualifyingLaunches: 'lançamentos qualificados',
    reportMonthly: 'Comissão por mês e categoria',
    reportSessions: 'Pontos de sessão por carregador',
    reportYearEnd: 'Totais de fim de ano',
    reportsTitle: 'Meus relatórios',
    reportYear: 'Ano',
    reportKind: 'Relatório',
    reportDownloadFailed: 'Não foi possível baixar o relatório.',
    reportExport: 'Exportar CSV',
    reportEmpty: 'Nenhuma atividade registrada',
    reportEmptyBody: 'Os ganhos registrados e os comprovantes de pagamento aparecerão aqui.',
    reportNote: 'As moedas contábeis ficam separadas. Os totais de fim de ano ajudam a preparar declarações e não são um formulário fiscal.',
    activityRecorded: 'Atividade registrada.',
    activityFailed: 'Não foi possível registrar a atividade.',
    activityLabel: 'Registrar minha atividade',
    activityLead: 'Novo lead iniciado',
    activityDemo: 'Demonstração',
    activityApplication: 'Solicitação',
    activityEvidence: 'Referência do comprovante',
    activityPlaceholder: 'Referência da sua atividade',
    activityRecord: 'Registrar atividade',
    // Sales payout run
    payTitle: 'Execução de repasses',
    paySubtitle: 'Revise os extratos fechados e depois confirme os pagamentos. Os repasses automáticos continuam desativados até serem aprovados.',
    payCycle: 'Ciclo mensal',
    payCycleBody: 'Os extratos fecham no dia 1. A revisão ocorre do dia 1 ao 4. O pagamento é no dia 5 ou no próximo dia útil. Cada moeda é paga separadamente. Contas que não estão prontas e transferências com falha continuam pendentes de pagamento.',
    payStatementMonth: 'Mês do extrato',
    payClose: 'Fechar extratos',
    payPay: 'Pagar e exportar a folha de pagamento',
    paySync: 'Sincronizar ganhos',
    paySynced: 'Ganhos e estornos sincronizados.',
    payPreview: 'Apenas prévia',
    payRequestFailed: 'Falha na solicitação.',
    payNewLines: 'Novas linhas contábeis: {n}. É necessário revisar o extrato.',
    payDate: 'Data de pagamento: {date}.',
    payMayProceed: 'Os pagamentos podem prosseguir.',
    payWait: 'Os pagamentos aguardam até essa data.',
    payNothing: 'Nada a processar',
    payNothingBody: 'Nenhum saldo de extrato ou de pagamento está disponível para este mês.',
    payLedgerHeld: 'Contábil e retido',
    payPayment: 'Pagamento',
    payReview: 'Revisão',
    payHeld: 'Retido: {amount}',
    payConversion: 'Custo de conversão: {amount}',
    payReady: 'Pronto',
    payPrepareQuote: 'Preparar cotação de conversão',
    payQuotePrepared: 'Cotação preparada. Gere uma nova prévia.',
    payConfirmCheck: 'Revisei estes valores, moedas e destinatários bloqueados.',
    payConfirmClose: 'Confirmar o fechamento dos extratos',
    payConfirmPay: 'Confirmar a execução de repasses',
    payRunRecorded: 'Execução registrada: {result}',
    payPayroll: 'Exportação e conciliação da folha de pagamento',
    payDownload: 'Baixar o CSV da folha de pagamento registrada',
    payPaymentId: 'ID do pagamento ou da exportação',
    payProviderRef: 'Referência do provedor',
    payRecordPayroll: 'Registrar folha de pagamento concluída',
    payPayrollRecorded: 'Pagamento da folha registrado.',
    payActualCost: 'Custo real de conversão',
    payActualBody: 'Registre o custo final de conversão do provedor na moeda contábil. Uma diferença em relação à cotação vira um ajuste no extrato aberto atual.',
    payLedgerCurrency: 'Moeda contábil',
    payActualCents: 'Custo real em centavos',
    payRecordActual: 'Registrar custo real',
    payActualReconciled: 'Custo real de conversão conciliado.',
    // Partner book: partners, orders and onboarding help
    bookPartnersSubtitle: 'Atividade, locais e próximos passos dos parceiros da sua carteira.',
    bookLoadingPartners: 'Carregando seus parceiros...',
    bookQualifyingChargers: 'Carregadores qualificados',
    bookLaunches12: 'Lançamentos em 12 meses',
    bookActivityTarget: 'Meta de atividade: 8',
    bookLaunches6: 'Lançamentos em 6 meses',
    bookPaceTarget: 'Meta de ritmo: 4',
    bookLocationsSubmissions: 'Locais / envios',
    bookLastContact: 'Último contato',
    bookLocationsHeading: 'Locais e envios',
    bookNoLocations: 'Nenhum local ainda',
    bookNoLocationsBody: 'Os locais enviados por este parceiro aparecerão aqui.',
    bookNotes: 'Notas e próximos passos',
    bookNoPartners: 'Nenhum parceiro na sua carteira',
    bookNoPartnersBody: 'Os parceiros atribuídos e seus locais aparecerão aqui.',
    bookLoadingSubmission: 'Carregando envio...',
    bookLocationType: 'Tipo de local',
    bookAddress: 'Endereço',
    bookContact: 'Contato',
    bookConnectivity: 'Conectividade',
    bookCommissioning: 'Comissionamento autorizado',
    bookAwaitingReview: 'Aguardando revisão da EVI',
    bookInstallerExpires: 'Expiração do acesso do instalador',
    bookNotIssued: 'Não emitido',
    bookLaunch: 'Lançamento',
    bookAwaitingApproval: 'Aguardando aprovação da EVI',
    bookOrdersTitle: 'Pedidos',
    bookOrdersSubtitle: 'Pedidos de clientes atribuídos a você e aos parceiros da sua carteira.',
    bookLoadingOrders: 'Carregando pedidos...',
    bookOrderDate: 'Data do pedido',
    bookDirectStaffOrder: 'Pedido direto da equipe',
    bookOrderTotal: 'Total do pedido',
    bookQty: 'Qtd. {n}',
    bookRegisterDeal: 'Registrar esta venda direta',
    bookClaimRecorded: 'Reivindicação do local registrada. Locais existentes fora de vendas exigem revisão de um superadministrador.',
    bookClaimFailed: 'Não foi possível registrar a reivindicação do local.',
    bookDealEvidence: 'Comprovante da venda direta',
    bookRegisterButton: 'Registrar venda direta',
    bookNoOrders: 'Nenhum pedido ainda',
    bookNoOrdersBody: 'Os pedidos das suas vendas diretas e da sua carteira de parceiros aparecerão aqui.',
    bookNewestOrders: 'Pedidos mais recentes',
    bookOlderOrders: 'Pedidos mais antigos',
    bookOnboardingTitle: 'Ajuda de integração',
    bookOnboardingSubtitle: 'Ajude as pessoas dos locais da sua carteira a obter o acesso de que precisam.',
    bookInvite: 'Convidar uma pessoa',
    bookInviteBody: 'Convide ou reenvie um convite. O envio segue a política de e-mail atual.',
    bookNoInviteLocations: 'Nenhum local para convidar ainda',
    bookNoInviteLocationsBody: 'Um local precisa estar na sua carteira antes que você possa convidar os usuários dele.',
    bookInviteRecorded: 'Convite registrado. O envio segue a política de e-mail atual.',
    bookInviteFailed: 'Não foi possível enviar o convite.',
    bookChooseLocation: 'Escolha um local',
    bookAccess: 'Acesso',
    roleBuildingAdmin: 'Administrador do prédio',
    rolePropertyManager: 'Gestor do imóvel',
    roleDriver: 'Motorista',
    bookInviting: 'Registrando o convite...',
    bookSendInvite: 'Enviar ou reenviar o convite',
    // Partner and direct site notes
    notesHeading: 'Ligações, visitas e e-mails',
    notesIntro: 'Registre cada contato no mesmo dia. As notas pertencem à EVI. Uma correção adiciona uma versão e preserva a original.',
    notesEmpty: 'Nenhuma nota ainda',
    notesEmptyBody: 'Registre abaixo o primeiro contato e um próximo passo claro.',
    notesVersion: 'Versão {n}',
    notesDue: 'Prazo',
    notesAddCorrection: 'Adicionar versão corrigida',
    notesLinksFailed: 'Não foi possível carregar os locais e envios vinculados.',
    notesRecorded: 'Nota registrada.',
    notesFailed: 'Não foi possível registrar a nota.',
    notesChannel: 'Canal',
    notesCall: 'Ligação',
    notesVisit: 'Visita',
    notesEmailChannel: 'E-mail',
    notesProjectStage: 'Etapa do projeto',
    notesNote: 'Nota',
    notesNextDue: 'Prazo do próximo passo',
    notesLocationOptional: 'Local (opcional)',
    notesNoLocation: 'Nenhum local vinculado',
    notesSubmissionOptional: 'Envio (opcional)',
    notesNoSubmission: 'Nenhum envio vinculado',
    notesRecordVersion: 'Registrar nova versão',
    notesRecordNote: 'Registrar nota',
    notesCancel: 'Cancelar correção',
    notesSiteTitle: 'Notas de locais diretos',
    notesNoClaims: 'Nenhuma reivindicação de local direto',
    notesNoClaimsBody: 'Suas reivindicações de locais diretos e o histórico de contato delas aparecerão aqui.',
    notesStaffTitle: 'Notas de parceiros e próximos passos',
    notesStaffIntro: 'Registre notas de qualquer parceiro, incluindo os parceiros próprios da EVI. As notas ficam com o parceiro quando ele é atribuído a um representante.',
    notesFindPartner: 'Encontrar um parceiro',
    notesHouseBook: ' (carteira própria da EVI)',
    notesFirstPartners: 'Primeiros parceiros',
    notesMorePartners: 'Mais parceiros',
    notesChooseBody: 'Selecione um parceiro para ler ou registrar o histórico de contato.',
    // My commission and Sales statements
    commPlanRevision: 'Plano de comissão, revisão {n}',
    commOfferRevision: 'Sua oferta, revisão {n}',
    commEffective: 'Vigente a partir de {date}',
    commThrough: 'até {date}',
    commAccepted: 'Aceito em {date}. Seu plano aceito é exibido somente para leitura.',
    commDirectMargin: 'Margem direta de hardware',
    commAboveThreshold: 'Acima do limite anual',
    commPartnerPoints: 'Pontos de sessão de parceiros',
    commFleetShare: 'Participação em assinaturas de frota',
    commFleetMeta: '{n} meses; somente pedidos de frota atribuídos',
    commFleetEvery: 'Cada renovação enquanto estiver na equipe; somente pedidos de frota',
    commAnnualThreshold: 'Limite anual de margem de hardware ({currency})',
    commFirstOrder: 'Primeiro pedido pago qualificado',
    commFourthCharger: 'Quarto carregador qualificado',
    commBookDuration: 'Duração da margem da carteira de parceiros',
    commNoMonthLimit: 'Sem limite de meses enquanto atribuído',
    commMonthsPerPartner: '{n} meses por parceiro',
    commContinuation: 'Continuidade após saída em boa situação',
    commMonths: '{n} meses',
    commOwnLadder: 'Escala de carregadores diretos próprios',
    commFromCharger: 'A partir do carregador carimbado qualificado',
    commSessionPoints: 'Pontos de sessão',
    commLadderNote: 'O acúmulo começa no quarto carregador direto qualificado. Os primeiros 12 meses são um ano de formação. Depois disso, um novo lançamento precisa de 8 lançamentos qualificados nos últimos 12 meses, incluindo ele mesmo. Lançamentos sem carimbo contam para a atividade, mas nunca recebem um carimbo depois. Os carimbos existentes continuam pagando enquanto você estiver na equipe.',
    commBookTiers: 'Faixas de margem da carteira de parceiros',
    commActiveAtLeast: 'Parceiros ativos, no mínimo',
    commPointsNote: 'Os pontos de sessão de parceiros se aplicam após a comissão do parceiro. Os valores de sessão da equipe têm como teto o saldo disponível da EVI. A participação em assinaturas de frota começa quando o faturamento de assinaturas está ativo; outras assinaturas não geram participação.',
    commGolden: 'Bônus Golden pago: {amount}.',
    commOverrideTiers: 'Faixas de comissão do gerente sobre a equipe',
    commTeamActiveAtLeast: 'Parceiros ativos da equipe, no mínimo',
    commGrowth: 'Bônus de crescimento do gerente: {six} com seis parceiros ativos e {payroll} no marco de folha de pagamento do subordinado. Pontos de sessão da equipe: {points}.',
    commAccepting: 'Aceitando...',
    commAccept: 'Aceitar este plano de comissão',
    commOfferAccepted: 'Oferta aceita.',
    commOfferFailed: 'Falha ao aceitar a oferta.',
    commStage2: 'Informações da Etapa 2',
    commStage2Body: 'Seis meses completos de comissão registrada, após estornos. Isso não move você para a Etapa 2. Um superadministrador precisa registrar uma revisão de plano aprovada.',
    commConsecutive: '{n} de {required} meses consecutivos',
    commCompletedMonth: 'Mês concluído',
    commRecordedCommission: 'Comissão registrada ({currency})',
    commNoMonths: 'Nenhum mês do plano concluído ainda',
    commNoMonthsBody: 'Aqui aparecem apenas os meses concluídos desde a data de vigência do seu plano.',
    commMonthsAvailable: '{n} de 6 meses concluídos disponíveis.',
    commAverage: 'Média: {amount}',
    commAgainst: 'em relação a {amount}',
    commNoCrossCurrency: '; sem comparação entre moedas',
    commStatements: 'Extratos mensais',
    commNoStatements: 'Nenhum extrato ainda',
    commNoStatementsBody: 'Seu primeiro extrato aparece depois que um mês de comissão é fechado.',
    commOpening: 'Abertura',
    commReversals: 'Estornos',
    commPaid: 'Pago',
    commClosing: 'Fechamento',
    commNoPayments: 'Nenhum pagamento ainda',
    commNoPaymentsBody: 'Os pagamentos via Connect ou os registros de folha de pagamento aparecerão depois que os ganhos forem pagos.',
    commLedgerAmount: 'Valor no registro',
    commMonth: 'Mês',
    commStripeTransfer: 'Transferência Stripe',
    commNotSent: 'Não enviado',
    commConversionCost: 'Custo de conversão',
    commCashAmount: 'Valor pago',
    commSentAs: 'Enviado como {amount}',
    commReservedAs: 'Reservado como {amount}',
    commLines: 'Linhas de comissão',
    commNoLines: 'Nenhuma linha de comissão ainda',
    commNoLinesBody: 'As atividades qualificadas criam linhas de comissão aqui, com eventuais correções posteriores exibidas separadamente.',
    commCategory: 'Categoria',
    commBasis: 'Base',
    commPlainTerms: "Como ler: a base é o valor ao qual sua taxa se aplica (num pedido de hardware, a margem da EVI). Retido é o que foi ganho mas ainda não pago: a EVI paga seus ganhos com um parceiro a partir da margem de hardware recebida desse parceiro, até a metade, e o restante é pago à medida que os próximos pedidos dele trazem mais. A etapa 2 é o plano de folha de pagamento da EVI com salário base: seis meses completos de comissão são o critério para ser considerado, e a mudança é um novo plano acordado com você.",
    commRate: 'Taxa',
    commCommission: 'Comissão',
    commPlanRevisionHeader: 'Revisão do plano',
    commTitle: 'Minha comissão',
    commSubtitle: 'Sua oferta, sua atividade de comissão e seus registros de pagamento. CAD e USD ficam separados.',
    commNoOffer: 'Nenhuma oferta registrada ainda',
    commNoOfferBody: 'A EVI registrará um plano de comissão para você revisar e aceitar.',
    commClosedNote: 'Extratos fechados não mudam. As correções aparecem como novas linhas.',
    commLoading: 'Carregando comissão...',
    commNewestLines: 'Linhas mais recentes',
    commOlderLines: 'Linhas mais antigas',
    stmtTitle: 'Extratos de vendas',
    stmtSubtitle: 'Revise as comissões, os extratos, os pagamentos e a folha de pagamento registrados da sua equipe de vendas.',
    stmtNote: 'Use os scripts revisados para acumular e fechar um mês, pagar prestadores via Connect ou exportar a folha de pagamento. Esta página nunca inicia uma cobrança nem uma transferência.',
    stmtChooseBody: 'Selecione um membro da equipe para revisar o registro de comissões e os extratos dele.',
    stmtReviewQueue: 'Fila de revisão',
    stmtNothingReview: 'Nada aguardando revisão',
    stmtNothingReviewBody: 'Os itens de revisão de liquidação aparecerão aqui quando um comprovante precisar de atenção.',
    stmtSource: 'Origem',
    stmtCostMissing: 'Custo não registrado',
    stmtCostIntro: 'Os comprovantes manuais exclusivos de administradores são armazenados separadamente. O instantâneo original do pedido não é alterado.',
    stmtNoMissing: 'Nenhum comprovante de custo pendente',
    stmtNoMissingBody: 'Os pedidos pagos atribuídos sem um instantâneo de custo aparecerão aqui.',
    stmtCostRecorded: 'Comprovante de custo registrado.',
    stmtCostFailed: 'Não foi possível salvar o comprovante de custo.',
    stmtChooseCurrency: 'Escolha a moeda',
    stmtUnits: '{name}: {n} unidades',
    stmtUnitCost: 'Custo unitário',
    stmtAutelFee: 'Taxa da Autel absorvida por unidade',
    stmtEvidenceReason: 'Comprovante e motivo',
    stmtRecordEvidence: 'Registrar comprovante manual',
    stmtCorrection: 'Adicionar uma correção',
    stmtCorrectionIntro: 'Estornos são negativos. Restituições não podem exceder o direito original. Use uma referência estável para uma nova tentativa.',
    stmtNoLinesToCorrect: 'Nenhuma linha para corrigir',
    stmtNoLinesToCorrectBody: 'É necessária uma linha de comissão positiva antes de adicionar uma correção.',
    stmtCorrectionRecorded: 'Correção registrada.',
    stmtCorrectionFailed: 'Falha na correção.',
    stmtOriginalLine: 'Linha original',
    stmtSignedAmount: 'Valor com sinal',
    stmtReference: 'Referência',
    stmtAppend: 'Adicionar correção',
    // Sales Staff
    staffTitle: 'Profissionais de vendas',
    staffSubtitle: 'Gerencie pessoas, planos de comissão com data e carteiras de parceiros. As alterações são auditadas.',
    staffNote: 'Somente um superadministrador pode alterar a equipe, os planos ou as atribuições de parceiros.',
    staffPeople: 'Pessoas',
    staffActive: 'Ativos',
    staffContractors: 'Prestadores',
    staffNoStaff: 'Nenhum profissional de vendas ainda',
    staffNoStaffBody: 'Adicione abaixo uma pessoa com uma conta ativa existente.',
    staffKind: 'Tipo',
    staffRole: 'Função',
    staffConnect: 'Connect',
    staffEligible: 'Elegível',
    staffNotEligible: 'Ainda não elegível',
    staffEligibility: '{status}: {active}/{target} ativos, {months}/{required} meses',
    staffAdd: 'Adicionar uma pessoa',
    staffCreated: 'Registro de profissional criado.',
    staffAddFailed: 'Não foi possível adicionar a pessoa.',
    staffInviteSent: 'Convite enviado. O registro de equipe é criado com esta função, gerente e data de início quando o convite for aceito.',
    staffInviteNeedsName: 'Este e-mail ainda não tem conta, então a pessoa receberá um convite. Adicione o nome primeiro.',
    staffInvitesTitle: 'Convidados, ainda não aceitaram',
    staffInvitedOn: 'Convidado em {date}',
    staffInviteFailed: 'Não criado ao aceitar: {reason}',
    staffResendInvite: 'Reenviar convite',
    staffInviteResent: 'Convite enviado novamente.',
    staffCancelInvite: 'Cancelar convite',
    staffInviteCancelled: 'Convite cancelado.',
    staffInviteActionFailed: 'Não foi possível alterar o convite.',
    inviteLinkAction: "Copiar link do convite",
    inviteLinkTitle: "Copiar link do convite",
    inviteLinkField: "Link do convite",
    inviteLinkLoading: "Criando um novo link...",
    inviteLinkCopy: "Copiar",
    inviteLinkCopied: "Copiado",
    inviteLinkSelected: "O link está selecionado. Pressione Ctrl+C, ou Cmd+C no Mac, para copiá-lo.",
    inviteLinkEmail: "Enviar da minha conta",
    inviteLinkReplaces: "Este link substitui o dos e-mails de convite anteriores. Ele funciona uma vez e expira em {date}.",
    inviteLinkDiscard: "Fechar esta janela descarta o link. Ele não será mostrado novamente.",
    inviteLinkExpired: "Este convite expirou. Reenvie-o para criar um novo e depois copie esse link.",
    inviteLinkFailed: "Não foi possível criar o link do convite.",
    inviteLinkClose: "Fechar",
    inviteLinkMailSubject: "Seu convite para a EV Initiative",
    inviteLinkMailGreeting: "Olá, {name},",
    inviteLinkMailGreetingNoName: "Olá,",
    inviteLinkMailBody: "Estou enviando seu convite para a EV Initiative do meu próprio e-mail caso o anterior não tenha chegado. O link abaixo funciona uma vez e expira em {date}.",
    inviteLinkMailSignoff: "Obrigado,",
    inviteLinkSignInPage: "Essa pessoa já tem uma conta EV Initiative com este e-mail, então este link abre a página de login. Entrar com essa conta aceita o convite.",
    inviteLinkMailSignInPage: "Estou enviando seu convite para a EV Initiative do meu próprio e-mail caso o anterior não tenha chegado. Você já tem uma conta EV Initiative com este e-mail, então entre com ela pelo link abaixo para aceitar.",
    staffAccountEmail: 'E-mail da conta',
    staffStaffRole: 'Função em vendas',
    staffContractor: 'Prestador',
    staffEmployee: 'Funcionário',
    staffPayThrough: 'Pagar via',
    staffStatesComma: 'Estados (separados por vírgula)',
    staffCreate: 'Criar registro de profissional',
    staffPersonDetails: 'Detalhes da pessoa',
    staffChooseBody: 'Selecione uma linha ou use Detalhes da pessoa para revisar o registro, os planos e a carteira dela.',
    staffRecord: 'Registro de profissional',
    staffAccount: 'Conta',
    staffPartnerBook: 'Carteira de parceiros',
    staffNoPartners: 'Nenhum parceiro atribuído',
    staffNoPartnersBody: 'Atribua um parceiro abaixo para adicioná-lo à carteira desta pessoa.',
    staffMoveAll: "Mover todos os parceiros",
    staffMoveAllIntro: "Move a partir de agora todos os parceiros que {name} atende hoje para outro SP Manager ou para a carteira da EVI. Os carregadores já lançados mantêm o carimbo de lançamento.",
    staffMoveAllTo: "Mover para",
    staffHouseBook: "Carteira da EVI",
    staffMoveAllButton: "Mover todos os parceiros",
    staffMoveAllDone: "{count} parceiros movidos.",
    staffDepartureMoves: "Na data de saída, os parceiros desta pessoa passam para o seu Sales Manager, ou para a carteira da EVI se não houver.",
    staffDeparted: 'Saída em {date}: {reason}',
    staffUpdated: 'Registro de profissional atualizado.',
    staffUpdateFailed: 'Falha na atualização.',
    staffStates: 'Estados',
    staffEmploymentNote: 'As mudanças de vínculo usam a mudança com data em Nova revisão do plano. Os carimbos de carregadores, as atribuições de carteira e a janela de atividade ficam com este registro de profissional.',
    staffEndDate: 'Data de término (opcional)',
    staffDepartureReason: 'Motivo da saída',
    staffGoodStanding: 'Boa situação',
    staffCause: 'Justa causa',
    staffConductIds: 'IDs das linhas contábeis relacionadas à conduta (somente justa causa)',
    staffSave: 'Salvar alterações do profissional',
    staffManagerAssignment: 'Atribuição de gerente com data',
    staffManagerRecorded: 'Atribuição de gerente com data registrada.',
    staffPlanRevisions: 'Revisões do plano',
    staffNoPlans: 'Nenhuma revisão do plano',
    staffNoPlansBody: 'Registre uma oferta com data para esta pessoa aceitar.',
    staffRevision: 'Revisão',
    staffEffectiveFrom: 'Vigente a partir de',
    staffEffectiveTo: 'Vigente até',
    staffOngoing: 'Em andamento',
    staffAssignPartner: 'Atribuir um parceiro',
    staffAssignIntro: 'Registre uma nova atribuição com o motivo. O histórico de atribuições anteriores permanece intacto.',
    staffAssignmentRecorded: 'Atribuição registrada.',
    staffUnassigned: 'Não atribuído',
    staffStandardPlan: 'Plano padrão para futuros representantes',
    staffSiteClaims: 'Reivindicações de locais',
    staffClaimsIntro: 'Locais existentes fora de vendas precisam de aprovação. Nenhum acesso à carteira nem comissão é concedido enquanto uma reivindicação aguarda revisão.',
    staffNoClaims: 'Nenhuma reivindicação de local',
    staffNoClaimsBody: 'Os locais de vendas registrados aparecerão aqui para revisão.',
    staffRegistrant: 'Solicitante',
    staffExisting: 'Existente ({kind}): {id}',
    staffReviewRecorded: 'Revisão registrada.',
    staffReviewFailed: 'Falha na revisão.',
    staffDecision: 'Decisão',
    staffReject: 'Rejeitar',
    staffApprove: 'Aprovar',
    staffRecordDecision: 'Registrar decisão',
    staffReassignRecorded: 'Reatribuição registrada.',
    staffReassign: 'Reatribuir reivindicação',
    staffReassignFailed: 'Falha na reatribuição.',
    staffRegistrantKind: 'Tipo do novo solicitante',
    // Plan revision form
    planNoMonthLimit: 'Sem limite de meses',
    planStoredStandard: 'Revisão padrão salva',
    planNew: 'Nova revisão do plano',
    planPrefilled: 'Preenchido com base na revisão padrão salva {revision}. Os planos aceitos e os carimbos de carregadores existentes permanecem inalterados.',
    planPrefilledCode: 'Preenchido com base no padrão vigente, revisão {revision}, ainda não registrada. Os planos aceitos e os carimbos de carregadores existentes permanecem inalterados.',
    planLoading: 'carregando',
    planLoadFailed: 'Não foi possível carregar o padrão salvo. Não é possível registrar uma revisão.',
    planStage: 'Etapa do plano',
    planStage1: 'Comissão da Etapa 1',
    planStage2: 'Taxas de folha de pagamento da Etapa 2',
    planNoRates: 'Nenhuma taxa salva está disponível para esta etapa.',
    planCentsReadback: 'Equivale a {amount}',
    planRecorded: 'Revisão registrada. Uma revisão pessoal exige aceitação.',
    planFailed: 'Não foi possível registrar a revisão.',
    planDatedRevision: 'Revisão com data do plano ou da função',
    planEmployeeMove: 'Mudança com data para funcionário e folha de pagamento',
    planRoleOnRevision: 'Função nesta revisão',
    planNotice: 'Referência do aviso por escrito',
    planRecord: 'Registrar nova revisão',
  },
};

export type SalesTeamCopy = Readonly<Record<SalesTeamCopyKey, string>>;
export const SALES_TEAM_COPY_KEYS = Object.keys(en) as SalesTeamCopyKey[];

export function salesTeamCopy(locale: unknown): SalesTeamCopy {
  return copy[normalizeLocale(typeof locale === 'string' ? locale : null)];
}

/** Copy invite link's dialog words in this locale (components/ui/InviteLinkDialog.tsx), for the Sales Staff invites. */
export function salesInviteLinkCopy(t: SalesTeamCopy): InviteLinkCopy {
  return {
    action: t.inviteLinkAction, title: t.inviteLinkTitle, field: t.inviteLinkField, loading: t.inviteLinkLoading, copy: t.inviteLinkCopy,
    copied: t.inviteLinkCopied, selected: t.inviteLinkSelected, email: t.inviteLinkEmail, replaces: t.inviteLinkReplaces, discard: t.inviteLinkDiscard,
    expired: t.inviteLinkExpired, failed: t.inviteLinkFailed, close: t.inviteLinkClose, mailSubject: t.inviteLinkMailSubject,
    mailGreeting: t.inviteLinkMailGreeting, mailGreetingNoName: t.inviteLinkMailGreetingNoName, mailBody: t.inviteLinkMailBody, mailSignoff: t.inviteLinkMailSignoff,
    signInPage: t.inviteLinkSignInPage, mailSignInPage: t.inviteLinkMailSignInPage,
  };
}

/** Fills {name} placeholders in one pass. Values print as a template literal prints them; an unknown name stays as written. */
export function fillSalesCopy(template: string, values: Readonly<Record<string, string | number | null | undefined>>): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (Object.hasOwn(values, name) ? String(values[name]) : match));
}

export type SalesCopyPart = { text: string } | { name: string };
/** Splits a template into literal text and placeholder names, for templates whose values are elements. */
export function splitSalesCopy(template: string): SalesCopyPart[] {
  const parts: SalesCopyPart[] = [], placeholder = /\{(\w+)\}/g;
  let last = 0, match: RegExpExecArray | null;
  while ((match = placeholder.exec(template))) {
    if (match.index > last) parts.push({ text: template.slice(last, match.index) });
    parts.push({ name: match[1] ?? '' });
    last = match.index + match[0].length;
  }
  if (last < template.length) parts.push({ text: template.slice(last) });
  return parts;
}
